import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import {
  Info,
  PhoneCall,
  PhoneOff,
  Heart,
  HeartOff,
  Activity,
  TrendingUp,
  Zap,
  BarChart3,
  Layers,
  Camera,
  X,
  RefreshCw,
  ShieldCheck,
  MapPin,
  Briefcase,
  Users,
  Compass,
  UserCheck,
  UserX,
  FileText,
  Edit2,
  User,
  ArrowRight,
  Star,
  GraduationCap,
  Banknote,
  ChevronDown,
  Calendar,
  PlusCircle,
  ArrowLeftCircle,
  Check,
  Trash2,
  History,
  Search,
  GitCompare,
  PauseCircle,
  MessageSquare,
} from "lucide-react";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import TableCardView from "./ui/tableCardView";
import JsomPromptMatchMakingModal from "./JsomPromptMatchMakingModal";
import toast from "react-hot-toast";
import LoaderService from "../services/LoaderService";
import AstroMatchModal from "./AstroMatchModal";
import {
  GetPassword,
  GetPotentialMatchesAdmin,
  GetProfileDetails,
  GetUserMatches,
  GetUserTransactions,
  GetMatchedProfile,
  GetPreferenceCategories,
  SearchSkippedMatches,
  GetUserLikedMatches,
  GetProfileLikedBy,
  GetSammelanEvents,
  GetDropdownData,
  GetPhoneCallEvents,
  GetCustomerSupportAdmins,
  getMatchmakingResultsFeed,
  // special preference related APIs
  GetSpecialPreferenceNotes,
  GetUserSpecialPreferenceActivity,
  GetUserHistory,
  SearchStates,
  SearchCities,
  GetMatchNotificationLog,
  GetMatchNotificationLogBatch,
  getMatchPublishSummary,
  GetPublishPreview,
} from "./apis/get";
import {
  PostGeneratePrompt,
  PostPublishProfile,
  PostResetMatch,
  PostStorePrompt,
  UpdateDecision,
  PostAddCreditsToUser,
  PostWhatsappPdfMatches,
  PostAdminLikeDislike,
  PostForceMatchmakingAdmin,
  PostPublishMultiple,
  PostPublishWithOverride,
  PostUpdateUserInstructions,
  PostResetMatchingData,
  PostTriggerServices,
  PostSendMatchNotification,
  PostSendRequestedMatchTemplate,
  PostMoveToQueue,
  PostMoveToSkipped,
  PostMatchMakingAstrologyDetails,
  PostCommonInstructionDislike,
  PostTriggerPotentialMatches,
  PostServiceJob,
  PostAssignUserSammelan,
  PostRemoveUserSammelan,
  PostPhoneCallFeedback,
  PostUpdateProfilePhoto,
  // special preference related APIs
  PostSpecialPreferenceNotes,
  PatchSpecialPreferenceNotes,
  DeleteSpecialPreferenceNotes,
  PostRemoveSpecialPreferenceKey,
  PostDeactivatePair,
} from "./apis/post";
import { PatchAssignUserInstruction, PatchHoldUser } from "./apis/patch";
import { ApiRequest } from "../services/api";
import ConfirmationModal from "./ConfirmationModal";
import SimpleMessageModal from "./SimpleNotification";
import { IsNull, setLocalItem, getLocalItem } from "../utilities/util";
import { HardDeleteUser } from "./apis/delete";
import { EnableUser, DisableUser } from "./apis/put";
import LogViewer from "./LogViewer";
import ComparisonMatrimonialProfileModal from "./ComparisonMatrimonialProfileModal";
import IndividualProfileView from "./IndividualProfileView";
import DeactivatedMatchesModal from "./DeactivatedMatchesModal";
import ManualMatchProcessMainModel from "./ManualMatchMaking/ManualMatchProcessMainModel";
import EditableModal from "./EditableModal";
import Tooltip from "@mui/material/Tooltip";
import PaymentModal from "./PaymentModal";
import DislikeReasonModal from "./DislikeReasonModal";
import { FaFilePdf } from "react-icons/fa";
import AstrologyDetailsView from "./AstrologyDetailsView";
// console.log("userprofileData", GetProfileDetails());
interface SortConfig {
  key: string;
  direction: "asc" | "desc";

}

const describePublishNotification = (response: any): {
  kind: "success" | "warning" | "error";
  message: string;
} => {
  const notification = response?.notification ?? response?.data?.notification;
  if (!notification) {
    return {
      kind: "warning",
      message: "Match published, but WhatsApp delivery status was not returned.",
    };
  }
  if (notification.skipped) {
    const reason = String(notification.reason || "notification policy").replace(/_/g, " ");
    return { kind: "warning", message: `Match published, but WhatsApp was skipped: ${reason}.` };
  }
  if (notification.delivered === false || notification.error) {
    return {
      kind: "error",
      message: `Match published, but WhatsApp failed${notification.error ? `: ${notification.error}` : "."}`,
    };
  }

  const deliveryStatuses = [
    notification.user_whatsapp?.status,
    notification.candidate_whatsapp?.status,
  ].filter(Boolean);
  const failed = deliveryStatuses.some((status) => String(status).toUpperCase() === "FAILED");
  const sent = deliveryStatuses.some((status) =>
    ["SENT", "DELIVERED", "READ"].includes(String(status).toUpperCase()),
  );

  if (failed) return { kind: "error", message: "Match published, but at least one WhatsApp card failed." };
  if (sent) return { kind: "success", message: "Match published and WhatsApp card sent." };
  if (notification.attempted === false) {
    return { kind: "warning", message: "Match published; no WhatsApp notification was attempted." };
  }
  return { kind: "warning", message: "Match published; WhatsApp delivery is still unconfirmed." };
};

const showPublishNotificationToast = (response: any) => {
  const result = describePublishNotification(response);
  if (result.kind === "success") toast.success(result.message);
  else if (result.kind === "error") toast.error(result.message);
  else toast(result.message, { icon: "⚠️" });
};

const stringifySearchValue = (value: any): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map((entry) => stringifySearchValue(entry)).join(" ");
  }
  if (typeof value === "object") {
    return Object.values(value)
      .map((entry) => stringifySearchValue(entry))
      .join(" ");
  }
  return String(value);
};

const buildRowSearchText = (row: Record<string, any>): string =>
  Object.values(row)
    .map((value) => stringifySearchValue(value))
    .join(" ")
    .toLowerCase();

const normalizeText = (value: any): string =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const normalizeCityOption = (value: any): string => {
  const rawValue = Array.isArray(value)
    ? value[0]
    : value && typeof value === "object"
      ? value.name || value.city || Object.values(value)[0] || ""
      : value;
  const text = normalizeText(rawValue);
  const tupleMatch = text.match(/^\(\s*['"](.+?)['"]\s*,\s*\)$/);
  return normalizeText(tupleMatch ? tupleMatch[1] : text);
};

const dedupeOptions = (options: string[]) =>
  Array.from(
    new Map(options.map((option) => [option.toLowerCase(), option])).values(),
  );

const MultiSelectDropdown = ({
  label,
  options,
  selectedValues,
  onToggle,
  onReset,
  icon,
  searchValue,
  onSearchChange,
}: {
  label: string;
  options: string[];
  selectedValues: string[];
  onToggle: (val: string) => void;
  onReset: () => void;
  icon?: React.ReactNode;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [localSearchTerm, setLocalSearchTerm] = React.useState("");
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const searchTerm = searchValue ?? localSearchTerm;
  const setSearchTerm = onSearchChange ?? setLocalSearchTerm;

  const filteredOptions = React.useMemo(() => {
    if (!searchTerm.trim()) return options;
    const q = searchTerm.toLowerCase().trim();
    return options.filter((opt) => opt.toLowerCase().includes(q));
  }, [options, searchTerm]);

  const normalizedSearch = searchTerm.trim();

  const hasExactOption = React.useMemo(() => {
    if (!normalizedSearch) return true;
    const q = normalizedSearch.toLowerCase();
    return options.some((opt) => opt.toLowerCase() === q);
  }, [options, normalizedSearch]);

  const displayOptions = React.useMemo(() => {
    const selectedCustom = selectedValues.filter(
      (val) => !options.some((opt) => opt.toLowerCase() === val.toLowerCase()),
    );
    const merged = [...selectedCustom, ...filteredOptions];
    return Array.from(new Set(merged));
  }, [selectedValues, options, filteredOptions]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      className={`relative ${isOpen ? "z-[9999]" : "z-[10]"}`}
      ref={dropdownRef}
    >
      <div className="space-y-1.5 p-1 bg-slate-950/30 rounded-2xl border border-transparent hover:border-slate-800 transition-colors">
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center gap-1.5">
            <span className="text-indigo-500/50">{icon}</span>
            <label className="text-[9px] font-black text-slate-500 uppercase tracking-tighter">
              {label}
            </label>
          </div>
          {selectedValues.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onReset();
              }}
              className="text-[8px] font-black text-rose-500 hover:text-rose-400 uppercase tracking-tighter"
            >
              Clear ({selectedValues.length})
            </button>
          )}
        </div>

        <div
          onClick={() => setIsOpen(!isOpen)}
          className="w-full h-9 bg-slate-900 border border-slate-700/30 text-white text-[10px] font-bold px-3 rounded-xl flex items-center justify-between cursor-pointer hover:bg-slate-800/50 transition-all overflow-hidden"
        >
          <span className="truncate pr-4">
            {selectedValues.length === 0
              ? `All ${label}s`
              : selectedValues.join(", ")}
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-3 w-3 transition-transform ${isOpen ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={3}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 z-[99999] bg-[#0f172a] border border-slate-700/50 rounded-xl shadow-[0_20px_60px_rgba(0,0,0,0.8)] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          <div className="p-2 border-b border-slate-700/40 bg-slate-900/60">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={`Search ${label.toLowerCase()}...`}
              className="w-full h-8 bg-slate-950 border border-slate-700/50 text-white text-[10px] font-bold px-3 rounded-lg focus:outline-none"
            />
          </div>
          <div className="max-h-[200px] overflow-y-auto custom-scrollbar p-1.5 space-y-0.5">
            {displayOptions.map((opt) => {
              const isSelected = selectedValues.includes(opt);
              return (
                <button
                  key={opt}
                  onClick={() => onToggle(opt)}
                  className={`w-full px-3 py-2 text-left text-[10px] font-bold transition-all rounded-lg flex items-center justify-between group ${isSelected
                    ? "bg-indigo-500/10 text-indigo-400"
                    : "text-slate-400 hover:bg-white/5 hover:text-slate-200"
                    }`}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-3.5 h-3.5 rounded border transition-colors flex items-center justify-center ${isSelected
                        ? "bg-indigo-500 border-indigo-400"
                        : "border-slate-700 group-hover:border-slate-600"
                        }`}
                    >
                      {isSelected && (
                        <svg
                          className="w-2.5 h-2.5 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={4}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </div>
                    <span>{opt}</span>
                  </div>
                </button>
              );
            })}
            {normalizedSearch && !hasExactOption && (
              <button
                onClick={() => {
                  onToggle(normalizedSearch);
                  setSearchTerm("");
                }}
                className="w-full px-3 py-2 text-left text-[10px] font-bold rounded-lg text-emerald-400 hover:bg-emerald-500/10 transition-all"
              >
                Add "{normalizedSearch}"
              </button>
            )}
            {displayOptions.length === 0 && (
              <div className="px-3 py-2 text-[10px] text-slate-500 font-bold">
                No options found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export type Models = {
  popUpCancel: boolean;
  popUpSubmit: boolean;
  popUpCandidate: boolean;
  reasonAndGapModal: boolean;
  manaulMatchProcessMainModal: boolean;
  row: any;
  // Add other properties of models as needed
};

enum classType {
  machingProfile = "maching_profile",
  astroProfile = "astro_profile",
  rejectProfile = "reject_profile",
}

interface IUserDetails {
  userId: string;
  name: string;
  email: string;
  phone: string;
  matrimonial_phone?: string;
  user_phone?: string;
  gender: string;
  Gender?: string;
  age: any;
  status: string;
  totalCredits: number;
  creditsUsed: number;
  location?: string;
  city?: string;
  state?: string;
  occupation?: string;
  Occupation?: string;
  manglik?: string;
  lastLogin?: string;
  photo?: string;
  version?: string;
  platform?: string;
  adminNote?: string;
  adminInstructions?: { instruction: string; date: string }[];
  has_child?: boolean;
  children?: any[];
  verified?: boolean;
  createdAt?: string;
  progress?: number;
  dislikeReasons?: {
    user?: string;
    reason?: string;
    name?: string;
    matrimonial_id?: string;
  }[];
  qualification?: string;
  working_with?: string;
  min_income?: string;
  max_income?: string;
  sammelanRegistrations?: any[];
  isPremium?: boolean;
}

// const getProductionApiBaseUrl = () => {
//   const configuredUrl = String(process.env.REACT_APP_API_URL || "").trim();
//   const fallbackUrl = "https://api.v2.production.smartmatrimony.ai";
//   const baseUrl = /^https?:\/\//i.test(configuredUrl) ? configuredUrl : fallbackUrl;
//   return baseUrl.replace(/\/+$/, "");
// };

const mappingClassType: Record<string, string> = {
  "Queued Profiles": "maching_profile",
  users: "users",
  "Matched Profiles": "astro_profile",
  "Rejected Profiles": "reject_profile",
  "Special Pref Skipped": "maching_profile",
  "Special Pref Requeued": "maching_profile",
};

export enum PromptGeneraterName {
  MatchMaking = 2,
  Astrology = 3,
  Summary = 4,
}

const tabs: string[] = [
  "Full Profile",
  "Potential Matches",
  "Queued Profiles",
  "Matched Profiles",
  // "Rejected Profiles",
  "Skipped Matchess",
  "Special Pref Skipped",
  "Special Pref Requeued",
  "Transactions",
];

const DEFAULT_ASTRO_DATA = {
  profile: {
    userId: "",
    name: "",
    gender: "",
    age: "",
    city: "",
    score: "",
    gptScore: "",
    status: "",
    proximity: "",
    date: "",
    decision: "",
    photo: "",
  },
  summary: {
    positiveHighlights: [],
    enhancementOpportunities: [],
    practicalTakeaways: [],
  },
  matchedTags: [],
  astrology: {
    maxScore: "",
    score: "",
    compatibility: "",
    overallMessage: "",
    strengths: "",
    challenges: "",
    status: "",
  },
  astrologyCategories: [],
  clusterAnalysis: [],
};

const isRequested = (status: string) =>
  status !== "not_requested" && status !== "No" && !!status;

const getRequestedLabel = (status: string) => {
  switch (status) {
    case "user_requested":
      return "User Requested";
    case "candidate_requested":
      return "Candidate Requested";
    case "mutual_requested":
      return "Mutual Requested";
    default:
      return "";
  }
};

const PreferenceDetail = ({
  selectedPreference,
  userProfileData,
  specialPreferenceNotes,
}: {
  selectedPreference: string;
  userProfileData: any;
  specialPreferenceNotes?: Record<string, any>;
}) => {
  const pref: any = userProfileData.preference || {};
  const apiNotes: Record<string, any> = specialPreferenceNotes?.includes || {};

  // Debug log to check what data we're receiving
  console.log("PreferenceDetail - selectedPreference:", selectedPreference);
  console.log("PreferenceDetail - apiNotes:", apiNotes);
  console.log("PreferenceDetail - pref:", pref);

  // Merge: API data overrides profile data for location/qualification/profession
  const apiAdditional: Record<string, any> = {};

  // Handle location from API
  if (
    apiNotes.location &&
    Array.isArray(apiNotes.location) &&
    apiNotes.location.length > 0
  ) {
    apiAdditional.location = {
      preferred_location: apiNotes.location.join(", "),
      negotiable: false,
    };
  }

  // Handle qualification from API
  if (
    apiNotes.qualification &&
    Array.isArray(apiNotes.qualification) &&
    apiNotes.qualification.length > 0
  ) {
    apiAdditional.qualification = {
      value: apiNotes.qualification.join(", "),
      negotiable: false,
    };
  }

  // Handle profession from API
  if (
    apiNotes.profession &&
    Array.isArray(apiNotes.profession) &&
    apiNotes.profession.length > 0
  ) {
    apiAdditional.profession = {
      value: apiNotes.profession.join(", "),
      negotiable: false,
    };
  }

  // Merge with profile preferences (API data takes priority)
  const additional = { ...(pref.additional || {}), ...apiAdditional };
  const personal = pref.personal || {};
  const lifestyle = pref.lifestyle || {};
  const preferences = pref.preferences || {};

  const fieldMap: Record<string, { label: string; data: any }> = {
    location: {
      label: "Preferred Location",
      data: additional.location || null,
    },
    qualification: {
      label: "Qualification",
      data: additional.qualification || null,
    },
    profession: {
      label: "Profession",
      data: additional.profession || null,
    },
  };

  const entry = fieldMap[selectedPreference];
  if (!entry) return null;

  const d = entry.data;
  const isEmpty =
    !d || (typeof d === "object" && !d.value && !d.preferred_location);

  return (
    <div className="mt-2 p-3 bg-indigo-500/5 border border-indigo-500/20 rounded-xl space-y-1.5 animate-in fade-in slide-in-from-top-1 duration-200">
      <span className="text-[9px] font-black text-indigo-400 uppercase tracking-widest">
        {entry.label}
      </span>
      {isEmpty ? (
        <p className="text-[10px] text-slate-500 italic">Not set</p>
      ) : (
        <>
          {/* location has preferred_location + location_type */}
          {d.preferred_location !== undefined && (
            <div className="flex justify-between items-center">
              <span className="text-[9px] text-slate-500 uppercase">
                Preferred
              </span>
              <span className="text-[11px] font-bold text-white">
                {d.preferred_location || "Any"}
              </span>
            </div>
          )}
          {d.location_type !== undefined && (
            <div className="flex justify-between items-center">
              <span className="text-[9px] text-slate-500 uppercase">Type</span>
              <span className="text-[11px] font-bold text-slate-300">
                {d.location_type || "Any"}
              </span>
            </div>
          )}
          {/* qualification / profession have .value */}
          {d.value !== undefined && (
            <div className="flex justify-between items-center">
              <span className="text-[9px] text-slate-500 uppercase">Value</span>
              <span className="text-[11px] font-bold text-white">
                {d.value || "No Preference"}
              </span>
            </div>
          )}
          {/* negotiable flag */}
          {d.negotiable !== undefined && (
            <div className="flex justify-between items-center">
              <span className="text-[9px] text-slate-500 uppercase">
                Negotiable
              </span>
              <span
                className={`text-[10px] font-black uppercase px-1.5 py-0.5 rounded ${d.negotiable
                  ? "bg-emerald-500/10 text-emerald-400"
                  : "bg-rose-500/10 text-rose-400"
                  }`}
              >
                {d.negotiable ? "Yes" : "No"}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const normalizeSpecialPreferenceNotes = (rawNotes?: Record<string, any>) => {
  if (!rawNotes || typeof rawNotes !== "object") {
    return {};
  }

  const normalized: Record<string, any> = {};
  const includes: Record<string, string[]> = {};
  const excludes: Record<string, string[]> = {};

  Object.entries(rawNotes).forEach(([key, value]) => {
    if (key === "includes" && value && typeof value === "object") {
      Object.entries(value).forEach(([includeKey, includeValue]) => {
        if (Array.isArray(includeValue) && includeValue.length > 0) {
          includes[includeKey] = includeValue.map((item) => String(item));
        }
      });
      return;
    }

    if (key === "excludes" && value && typeof value === "object") {
      Object.entries(value).forEach(([excludeKey, excludeValue]) => {
        if (Array.isArray(excludeValue) && excludeValue.length > 0) {
          excludes[excludeKey] = excludeValue.map((item) => String(item));
        }
      });
      return;
    }

    if (Array.isArray(value) && value.length > 0) {
      includes[key] = value.map((item) => String(item));
    }
  });

  if (Object.keys(includes).length > 0) {
    normalized.includes = includes;
  }
  if (Object.keys(excludes).length > 0) {
    normalized.excludes = excludes;
  }

  return normalized;
};

const SelectionChip = ({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3 py-2 rounded-xl border text-[10px] font-black uppercase tracking-widest transition-all ${active
      ? "bg-indigo-500/20 text-indigo-200 border-indigo-400/60 shadow-[0_0_0_1px_rgba(129,140,248,0.25)]"
      : "bg-slate-900/70 text-slate-300 border-slate-700/70 hover:bg-slate-800 hover:border-slate-600"
      }`}
  >
    {label}
  </button>
);

const formatLakhsLabel = (value: number) => {
  if (value >= 10000000) {
    return `₹${value / 10000000} Cr`;
  }
  return `₹${value / 100000}L`;
};

const prettifyDropdownOption = (value: string) =>
  value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

const formatMatchPublishedAt = (value?: string | null) => {
  if (!value) return "Not published";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Not published";

  return {
    date: parsed.toLocaleDateString([], {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }),
    time: parsed.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
};

const fetchMatchmakingFeedRowsForUser = async (userId: string) => {
  const rows: any[] = [];
  const limit = 200;
  let offset = 0;

  for (let page = 0; page < 10; page += 1) {
    const response: any = await getMatchmakingResultsFeed({
      uid: userId,
      limit,
      offset,
    });
    const payload = response?.data || {};
    const pageRows = Array.isArray(payload.rows) ? payload.rows : [];
    rows.push(...pageRows);

    if (!payload.pagination?.has_more || pageRows.length === 0) {
      break;
    }
    offset += limit;
  }

  return rows;
};

const SPECIAL_PREFERENCE_DROPDOWN_KEY_MAP: Record<string, string[]> = {
  qualification: ["qualification"],
  profession: ["profession", "occupation"],
  dietary: ["dietaryoptions"],
  family_type: ["familytypeoptions"],
  family_background: ["familybackgroundoptions"],
  marital_status: ["profile_marital_option"],
  working_with: ["workingwithoptions"],
  hobbies: ["hobbies"],
  caste: ["caste"],
  sub_caste: ["subcaste"],
  gotra: ["gotra"],
  languages_known: ["language"],
  manglik: ["manglikoptions"],
  relocation_willingness: ["relocationoptions", "relocation_willingness"],
  height: ["heightoptions", "height"],
};

const UserDetails = () => {
  const [downloadingBiodata, setDownloadingBiodata] = useState(false);
  const [biodataDownloadMenuOpen, setBiodataDownloadMenuOpen] = useState(false);
  const columnMap: Record<string, any[]> = {
    "Matched Profiles": [
      {
        label: "Member Profile",
        accessor: "member_info",
        cellRenderer: (row: any) => (
          <div className="flex items-center gap-3 min-w-[210px]">
            <div className="relative group/img shrink-0">
              <div className="w-12 h-12 rounded-xl bg-slate-800 border border-slate-700/50 overflow-hidden flex items-center justify-center shadow-inner transition-all hover:scale-105 cursor-zoom-in">
                {row.icon && row.icon.startsWith("http") ? (
                  <img
                    src={row.icon}
                    alt={row.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-slate-500 font-bold text-lg">
                    {row.name ? row.name[0] : "U"}
                  </span>
                )}
              </div>
              {row.icon && row.icon.startsWith("http") && (
                <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[10000] w-[450px] h-[450px] rounded-2xl border-4 border-slate-900 shadow-[0_0_80px_rgba(0,0,0,1)] bg-black/95 p-2 opacity-0 group-hover/img:opacity-100 pointer-events-none transition-all duration-200 hidden group-hover/img:block border-slate-800">
                  <img
                    src={row.icon}
                    alt={row.name}
                    className="w-full h-full object-contain rounded-xl"
                  />
                </div>
              )}
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="font-bold text-slate-200 text-sm whitespace-nowrap">
                {row.name}
              </span>
              <div className="flex flex-col gap-0.5 mt-0.5">
                <div className="flex gap-1 text-[9px] font-black uppercase tracking-tighter">
                  <span className="text-slate-600">Age:</span>{" "}
                  <span className="text-slate-400">{row.age}y</span>
                  <span className="text-slate-800 ml-1">â€¢</span>
                  <span className="text-slate-600 ml-1">Ht:</span>{" "}
                  <span className="text-slate-400">{row.height}</span>
                  <span className="text-slate-800 ml-1">â€¢</span>
                  <span className="text-slate-600 ml-1">Sex:</span>{" "}
                  <span className="text-slate-400">{row.gender}</span>
                </div>
                <div className="text-[9px] text-indigo-400 font-black uppercase tracking-tighter flex items-center gap-1">
                  <span className="text-slate-600 mr-1">Edu:</span>
                  <GraduationCap
                    size={10}
                    className="text-indigo-400/30 shrink-0"
                  />
                  <span className="truncate">{row.qualification || "N/A"}</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-500/5 border border-indigo-500/20">
                  <span className="text-[7px] text-slate-600 font-black uppercase">
                    Status:
                  </span>
                  <span className="text-indigo-400 text-[8px] font-black uppercase tracking-tighter">
                    {row.marital_status || "N/A"}
                  </span>
                </div>
                <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/5 border border-amber-500/20">
                  <span className="text-[7px] text-slate-600 font-black uppercase">
                    Manglik:
                  </span>
                  <span className="text-amber-500 text-[8px] font-black uppercase tracking-tighter">
                    {row.manglik?.replace(/_/g, " ")}
                  </span>
                </div>
              </div>
              {/* Added Intercaste and MaternalGotra */}
              <div className="flex flex-wrap gap-1 mt-1">
                <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-500/5 border border-emerald-500/20">
                  <span className="text-[7px] text-slate-600 font-black uppercase">
                    Intercaste:
                  </span>
                  <span className="text-emerald-500 text-[8px] font-black uppercase tracking-tighter">
                    {row.intercaste === true
                      ? "Yes"
                      : row.intercaste === false
                        ? "No"
                        : "N/A"}
                  </span>
                </div>
                <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-500/5 border border-purple-500/20">
                  <span className="text-[7px] text-slate-600 font-black uppercase">
                    Maternal Gotra:
                  </span>
                  <span className="text-purple-500 text-[8px] font-black uppercase tracking-tighter">
                    {row.maternal_gotra || "None"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ),
      },
      {
        label: "Metrics & Location",
        accessor: "metrics_location",
        cellRenderer: (row: any) => (
          <div className="flex flex-col gap-1.5 min-w-[150px]">
            <div className="flex items-center gap-2">
              <div className="flex flex-col">
                <span className="text-[7px] text-slate-600 uppercase font-black tracking-widest leading-none">
                  Match Score
                </span>
                <span className="text-emerald-400 font-mono font-black text-xs leading-tight mt-0.5">
                  {row.score || 0}
                </span>
              </div>
              <div className="flex flex-col border-l border-slate-800 pl-2">
                <span className="text-[7px] text-slate-600 uppercase font-black tracking-widest leading-none">
                  Gunn Score
                </span>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-amber-400 font-mono font-black text-xs leading-tight">
                    {row.gptScore || 0}
                  </span>
                  <Tooltip title="Astrology Details" placement="top">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenAstrologyDetails(row);
                      }}
                      className="p-0.5 rounded bg-fuchsia-500/5 text-fuchsia-300 border border-fuchsia-500/20 hover:bg-fuchsia-500/10 transition-all"
                    >
                      <Star size={8} />
                    </button>
                  </Tooltip>
                </div>
              </div>
            </div>
            <div className="flex flex-col border-t border-slate-800/50 pt-1 mt-0.5">
              <div className="flex items-start gap-1">
                <MapPin size={10} className="text-slate-700 shrink-0 mt-0.5" />
                <div className="flex flex-col">
                  <span className="text-[7px] text-slate-600 uppercase font-black leading-none">
                    Location
                  </span>
                  <span className="text-[10px] text-slate-400 font-bold leading-tight">
                    {row.city || "N/A"}, {row.state || "N/A"}
                  </span>
                </div>
              </div>
              <div className="flex flex-col">
                <div className="flex items-center gap-1.5 mt-2 ml-3.5 bg-indigo-500/5 px-2 py-1 rounded-md border border-indigo-500/10 w-fit">
                  <span className="text-[7px] text-indigo-400 font-black uppercase tracking-tighter">
                    Proximity:
                  </span>
                  <span className="text-[11px] text-indigo-300 font-black font-mono tracking-tight">
                    {row.proximity || 0} km
                  </span>
                </div>
                <div className="flex items-center gap-1.5 mt-1 ml-3.5 bg-emerald-500/5 px-2 py-0.5 rounded-md border border-emerald-500/10 w-fit">
                  <span className="text-[7px] text-emerald-400 font-black uppercase tracking-tighter whitespace-nowrap">
                    Source:
                  </span>
                  <span className="text-[9px] text-emerald-300 font-bold tracking-tight">
                    {!row.source_service || row.source_service === "unknown"
                      ? "default_service"
                      : row.source_service}
                  </span>
                </div>
                {row.created_by && (
                  <div className="flex items-center gap-1.5 mt-1 ml-3.5 bg-blue-500/5 px-2 py-0.5 rounded-md border border-blue-500/10 w-fit">
                    <span className="text-[7px] text-blue-400 font-black uppercase tracking-tighter whitespace-nowrap">
                      Created By:
                    </span>
                    <span className="text-[9px] text-blue-300 font-bold tracking-tight">
                      {row.created_by}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ),
      },
      {
        label: "Matched / Published",
        accessor: "match_at",
        cellRenderer: (row: any) => {
          const matchedAt = formatMatchPublishedAt(row.match_at);
          const publishedAt = formatMatchPublishedAt(row.publish_at);

          const renderDateBlock = (value: any, tag: string) => (
            <div className="flex items-start gap-2">
              <Calendar size={13} className="text-slate-700 shrink-0 mt-0.5" />
              {typeof value === "string" ? (
                <div className="flex flex-col">
                  <span className="text-[10px] text-slate-300 font-black whitespace-nowrap">
                    {value}
                  </span>
                  <span className="text-[8px] uppercase tracking-[0.18em] text-slate-500 font-bold">
                    {tag}
                  </span>
                </div>
              ) : (
                <div className="flex flex-col">
                  <span className="text-[11px] text-slate-300 font-black whitespace-nowrap">
                    {value.date}
                  </span>
                  <span className="text-[9px] text-slate-600 font-mono">
                    {value.time}
                  </span>
                  <span className="text-[8px] uppercase tracking-[0.18em] text-slate-500 font-bold mt-0.5">
                    {tag}
                  </span>
                </div>
              )}
            </div>
          );

          return (
            <div className="flex flex-col gap-3 min-w-[160px]">
              {renderDateBlock(matchedAt, "Matched")}
              {renderDateBlock(publishedAt, "Published")}
            </div>
          );
        },
      },
      {
        label: "Interactions",
        accessor: "interactions",
        cellRenderer: (row: any) => (
          <div className="flex flex-col gap-2 min-w-[160px]">
            <div className="flex items-start justify-between gap-3">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <span className="px-1.5 py-0.5 rounded bg-sky-500/10 border border-sky-500/20 text-[7px] text-sky-300 font-black uppercase tracking-widest">
                    User
                  </span>
                  <div className="flex gap-1 bg-slate-950 p-0.5 rounded-lg border border-slate-800 shadow-inner">
                    <Tooltip
                      title={row.like === true ? "User Liked" : "User Like"}
                      placement="top"
                    >
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLikeDislikeAction(
                            row.userId,
                            row.like === true ? false : true,
                            false,
                          );
                        }}
                        className={`p-1.5 rounded cursor-pointer transition-all ${row.like === true ? "bg-red-500/20 text-red-500" : "text-slate-600 hover:text-slate-400"}`}
                      >
                        <Heart
                          size={14}
                          fill={row.like === true ? "currentColor" : "none"}
                        />
                      </div>
                    </Tooltip>
                    <Tooltip
                      title={
                        row.like === false ? "User Disliked" : "User Dislike"
                      }
                      placement="top"
                    >
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          if (row.like === false)
                            handleLikeDislikeAction(
                              row.userId,
                              false,
                              false,
                              "",
                            );
                          else
                            setDislikeModal({
                              isOpen: true,
                              candidateId: row.userId,
                              candidateName: row.name,
                            });
                        }}
                        className={`p-1.5 rounded cursor-pointer transition-all ${row.like === false ? "bg-yellow-500/20 text-yellow-500" : "text-slate-600 hover:text-slate-400"}`}
                      >
                        <HeartOff size={14} />
                      </div>
                    </Tooltip>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="px-1.5 py-0.5 rounded bg-fuchsia-500/10 border border-fuchsia-500/20 text-[7px] text-fuchsia-300 font-black uppercase tracking-widest">
                    Candidate
                  </span>
                  <div className="flex gap-1 bg-slate-950 p-0.5 rounded-lg border border-slate-800 shadow-inner">
                    <Tooltip
                      title={
                        row.candidateLike === true
                          ? "Candidate Liked"
                          : "Candidate Like"
                      }
                      placement="top"
                    >
                      <div
                        className={`p-1.5 rounded transition-all ${row.candidateLike === true ? "bg-red-500/20 text-red-500" : "text-slate-600"}`}
                      >
                        <Heart
                          size={14}
                          fill={
                            row.candidateLike === true ? "currentColor" : "none"
                          }
                        />
                      </div>
                    </Tooltip>
                    <Tooltip
                      title={
                        row.candidateLike === false
                          ? "Candidate Disliked"
                          : "Candidate Dislike"
                      }
                      placement="top"
                    >
                      <div
                        className={`p-1.5 rounded transition-all ${row.candidateLike === false ? "bg-yellow-500/20 text-yellow-500" : "text-slate-600"}`}
                      >
                        <HeartOff size={14} />
                      </div>
                    </Tooltip>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Tooltip
                  title={row.call?.called ? "Already Called" : "Click to Call"}
                  placement="top"
                >
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePhoneCall(row.userId);
                    }}
                    className={`p-1.5 rounded-lg border flex items-center justify-center transition-all cursor-pointer hover:scale-105 active:scale-95 ${row.call?.called ? "bg-green-500/10 border-green-500/20 text-green-500" : "bg-slate-800/50 border-slate-700/30 text-slate-500"}`}
                  >
                    {row.call?.called ? (
                      <PhoneCall size={14} />
                    ) : (
                      <PhoneOff size={14} />
                    )}
                  </div>
                </Tooltip>
                <Tooltip
                  title={row.publish === "yes" ? renderMatchNotificationLogTooltip(row.userId, row.name) : "Publish match to enable PDF actions"}
                  arrow
                  placement="top"
                  enterDelay={180}
                  leaveDelay={80}
                  onOpen={() => {
                    if (row.publish === "yes") {
                      void handleMatchNotificationLogHover(row.userId);
                    }
                  }}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: "rgb(15, 23, 42)",
                        border: "1px solid rgba(100, 116, 139, 0.28)",
                        borderRadius: "16px",
                        p: "12px",
                        maxWidth: "360px",
                        boxShadow: "0 18px 45px rgba(0,0,0,0.75)",
                        "& .MuiTooltip-arrow": {
                          color: "rgb(15, 23, 42)",
                        },
                      },
                    },
                  }}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSendPdfModal({
                        isOpen: true,
                        candidateId: row.userId,
                        candidateName: row.name,
                        isUserRequested: row.requested === "user_requested",
                      });
                    }}
                    onMouseEnter={() => {
                      if (row.publish === "yes") {
                        void handleMatchNotificationLogHover(row.userId);
                      }
                    }}
                    disabled={row.publish !== "yes"}
                    className={`p-1.5 rounded-lg border transition-all ${row.publish === "yes"
                      ? "bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 border-indigo-500/30"
                      : "bg-slate-800/40 text-slate-600 border-slate-700/30 cursor-not-allowed opacity-60"
                      }`}
                    title={row.publish === "yes" ? "Hover to preview match log" : "Publish match to send PDF"}
                  >
                    <FaFilePdf size={14} />
                  </button>
                </Tooltip>
              </div>
            </div>

            {row.like === false && row.likeReason && (
              <div className="flex flex-col gap-0.5 mt-1 px-1 bg-yellow-500/5 border border-yellow-500/10 rounded-md">
                <div className="flex items-center gap-1">
                  <HeartOff size={8} className="text-yellow-500" />
                  <span className="text-[7px] text-yellow-500/60 font-black uppercase tracking-tighter">
                    User Dislike Reason
                  </span>
                </div>
                <span className="text-[9px] text-yellow-500/80 italic line-clamp-2 leading-tight">
                  "{row.likeReason}"
                </span>
              </div>
            )}
            {row.candidateLike === false && row.candidateLikeReason && (
              <div className="flex flex-col gap-0.5 mt-1 px-1 bg-fuchsia-500/5 border border-fuchsia-500/10 rounded-md">
                <div className="flex items-center gap-1">
                  <HeartOff size={8} className="text-fuchsia-400" />
                  <span className="text-[7px] text-fuchsia-300/70 font-black uppercase tracking-tighter">
                    Candidate Dislike Reason
                  </span>
                </div>
                <span className="text-[9px] text-fuchsia-200/80 italic line-clamp-2 leading-tight">
                  "{row.candidateLikeReason}"
                </span>
              </div>
            )}
            {row.like === true && row.likedNotCalled48h && (
              <div className="flex items-center gap-1.5 mt-1 px-2 py-1 bg-amber-500/10 border border-amber-500/20 rounded-md">
                <PhoneOff size={9} className="text-amber-300 shrink-0" />
                <span className="text-[8px] text-amber-200 font-black uppercase tracking-[0.12em]">
                  Liked, No call within 48 hours
                </span>
              </div>
            )}
            <div className="flex flex-col gap-1 mt-1">
              <div className="flex items-center gap-1.5">
                <Briefcase size={10} className="text-slate-700 shrink-0" />
                <div className="flex flex-col">
                  <span className="text-[7px] text-slate-700 font-black uppercase leading-none">
                    Profession
                  </span>
                  <span className="text-[10px] text-slate-400 font-medium leading-tight truncate max-w-[150px]">
                    {row.profession || "N/A"}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <MapPin size={10} className="text-slate-700 shrink-0" />
                <div className="flex flex-col">
                  <span className="text-[7px] text-slate-700 font-black uppercase leading-none">
                    Work Loc
                  </span>
                  <span className="text-[10px] text-slate-300 font-medium leading-tight truncate max-w-[150px]">
                    {row.occupation_location || "N/A"}
                  </span>
                </div>
              </div>
              <div className="flex gap-3 mt-1 pl-4">
                <div className="flex flex-col">
                  <span className="text-[7px] text-slate-700 font-black uppercase leading-none">
                    Income
                  </span>
                  <span className="text-[9px] text-emerald-400 font-mono font-black">
                    {row.income || "N/A"}
                  </span>
                </div>
                <div className="flex flex-col border-l border-slate-800 pl-3">
                  <span className="text-[7px] text-slate-700 font-black uppercase leading-none">
                    Diet
                  </span>
                  <span className="text-[9px] text-slate-500 font-black">
                    {row.dietary || "N/A"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ),
      },
      {
        label: "Ops & Status",
        accessor: "operations_status",
        cellRenderer: (row: any) => (
          <div className="flex flex-col min-w-[170px] h-full py-1.5 gap-4">
            <div className="flex items-center gap-1.5">
              {/* <button onClick={(e) => { e.stopPropagation(); handleMoveToQueue(row.userId); }} className="flex-1 px-3 py-1.5 bg-amber-500/10 hover:bg-amber-500/20 text-amber-500 border border-amber-500/30 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all shadow-sm active:scale-95">Queue</button> */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleMoveToSkipped(row.userId);
                }}
                className="flex-1 px-3 py-1.5 bg-slate-500/10 hover:bg-slate-500/20 text-slate-400 border border-slate-500/30 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all shadow-sm active:scale-95"
              >
                Skip
              </button>
              {renderDeactivatePairButton(row.userId)}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const baseUserId = userIdParams || userDetails.userId;
                  const candidateId = row.userId || row.candidate_id;
                  if (!baseUserId || !candidateId) {
                    toast.error("Missing user or candidate id");
                    return;
                  }
                  navigate(
                    `/match-pair?user_id=${encodeURIComponent(baseUserId)}&candidate_id=${encodeURIComponent(candidateId)}`,
                  );
                }}
                className="flex-1 px-3 py-1.5 bg-indigo-500/10 hover:bg-indigo-600 text-indigo-400 hover:text-white border border-indigo-500/30 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all shadow-sm active:scale-95 flex items-center justify-center gap-1.5"
              >
                <GitCompare size={12} /> Compare
              </button>
            </div>

            <div className="flex flex-col items-center gap-2 mt-1 pt-3 border-t border-slate-800/40">
              <div className="flex items-center justify-center w-full px-2">
                <span
                  className={`w-full text-center px-2 py-1 rounded-md text-[8px] font-black uppercase tracking-[0.15em] border transition-all ${row.publish === "yes" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20 shadow-[0_0_12px_rgba(16,185,129,0.05)]" : "bg-slate-800/40 text-slate-600 border-slate-700/30"}`}
                >
                  {row.publish === "yes" ? "Published" : "Match Status: Draft"}
                </span>
              </div>
              {row.publish === "yes" &&
                (() => {
                  const cid = row.userId || row.candidate_id;
                  const d = matchDeliveryStatuses[cid];
                  const st = (
                    d?.read_at
                      ? "read"
                      : d?.delivered_at
                        ? "delivered"
                        : d?.status || ""
                  )
                    .toString()
                    .toLowerCase();
                  const map: Record<string, { label: string; cls: string }> = {
                    read: { label: "✓✓ Read", cls: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
                    delivered: { label: "✓✓ Delivered", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
                    sent: { label: "✓ Sent", cls: "bg-slate-700/30 text-slate-400 border-slate-600/30" },
                    failed: { label: "✕ Failed", cls: "bg-rose-500/10 text-rose-400 border-rose-500/20" },
                    pending: { label: "◯ Pending", cls: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
                  };
                  const meta =
                    map[st] || { label: "◯ No receipt", cls: "bg-slate-800/40 text-slate-600 border-slate-700/30" };
                  const title =
                    d?.error_message ||
                    (d?.read_at
                      ? `Read ${d.read_at}`
                      : d?.delivered_at
                        ? `Delivered ${d.delivered_at}`
                        : d?.sent_at
                          ? `Sent ${d.sent_at}`
                          : "No WhatsApp receipt yet");
                  return (
                    <div className="flex items-center justify-center w-full px-2" title={title}>
                      <span
                        className={`w-full text-center px-2 py-1 rounded-md text-[8px] font-black uppercase tracking-[0.15em] border ${meta.cls}`}
                      >
                        WA: {meta.label}
                      </span>
                    </div>
                  );
                })()}
              {row.publish === "no" && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePublishMatch(row);
                  }}
                  className="w-full py-2 bg-rose-500/10 hover:bg-rose-600 text-rose-400 hover:text-white border border-rose-500/20 hover:border-rose-500 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-[0_4px_12px_rgba(244,63,94,0.15)] hover:shadow-[0_8px_20px_rgba(244,63,94,0.25)] active:scale-[0.98] flex items-center justify-center gap-2"
                >
                  Publish Match
                </button>
              )}
              <div className="flex items-center justify-center w-full px-2">
                <span
                  className={`w-full text-center px-2 py-1 rounded-md text-[8px] font-black uppercase tracking-[0.15em] border transition-all ${isRequested(row.requested)
                    ? row.requested === "mutual_requested"
                      ? "bg-purple-500/10 text-purple-400 border-purple-500/20"
                      : row.requested === "user_requested"
                        ? "bg-blue-500/10 text-blue-400 border-blue-500/20"
                        : "bg-amber-500/10 text-amber-400 border-amber-500/20"
                    : "bg-slate-800/40 text-slate-600 border-slate-700/30"
                    }`}
                >
                  {isRequested(row.requested)
                    ? getRequestedLabel(row.requested)
                    : " Not Requested"}
                </span>
              </div>
            </div>
          </div>
        ),
      },
      {
        label: "Insights",
        accessor: "matching_insights",
        cellRenderer: (row: any) => {
          return (
            <div className="flex flex-col gap-2 min-w-[240px]">
              <div className="flex flex-col gap-1 bg-slate-950/40 p-2 rounded-xl border border-white/5 shadow-inner">
                <div className="flex items-center justify-between border-b border-white/[0.03] pb-1 mb-1">
                  <span className="text-[8px] font-black text-indigo-400/60 uppercase tracking-widest flex items-center gap-1">
                    <User size={8} /> Member Bio
                  </span>
                  <div className="text-[8px] text-slate-600 font-mono">
                    {row.date
                      ? new Date(row.date).toLocaleDateString([], {
                        day: "2-digit",
                        month: "short",
                      })
                      : ""}
                  </div>
                </div>
                <div className="text-[10px] text-slate-300 italic leading-relaxed whitespace-pre-wrap break-words">
                  "{row.about_me || "No bio provided"}"
                </div>
              </div>

              <div className="flex items-center justify-between px-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewReasonButton(row);
                  }}
                  className="flex items-center gap-1.5 text-[9px] font-black text-blue-400 uppercase tracking-widest hover:text-blue-300 transition-all group"
                >
                  View Analysis Details{" "}
                  <ArrowRight
                    size={10}
                    className="group-hover:translate-x-0.5 transition-transform"
                  />
                </button>
              </div>
            </div>
          );
        },
      },
    ],
    Transactions: [
      { label: "ID", accessor: "id" },
      {
        label: "Status",
        accessor: "status",
        cellRenderer: (row: any) => (
          <span
            className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase border ${row.status === "SUCCESS"
              ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
              : row.status === "FAILED"
                ? "bg-rose-500/10 text-rose-400 border-rose-500/20"
                : "bg-amber-500/10 text-amber-400 border-amber-500/20"
              }`}
          >
            {row.status}
          </span>
        ),
      },
      {
        label: "Type",
        accessor: "transaction_type",
        cellRenderer: (row: any) => (
          <span className="text-slate-300 font-medium text-xs">
            {row.transaction_type?.replace(/_/g, " ")}
          </span>
        ),
      },
      {
        label: "Credits",
        accessor: "credits",
        cellRenderer: (row: any) => (
          <div
            className={`font-mono font-bold text-sm ${row.credits >= 0 ? "text-emerald-400" : "text-rose-400"}`}
          >
            {row.credits > 0 ? `+${row.credits}` : row.credits}
          </div>
        ),
      },
      {
        label: "Amount",
        accessor: "amount_paid",
        cellRenderer: (row: any) => (
          <div className="text-slate-300 text-xs font-mono">
            {row.amount_paid ? `${row.amount_paid}` : "-"}
            {row.payment_type && (
              <span className="ml-2 text-[10px] text-slate-500 opacity-70 uppercase">
                ({row.payment_type})
              </span>
            )}
          </div>
        ),
      },
      {
        label: "Description",
        accessor: "description",
        cellRenderer: (row: any) => (
          <div
            className="max-w-[200px] truncate text-slate-400 text-[11px] italic"
            title={row.description}
          >
            {row.description}
          </div>
        ),
      },
      {
        label: "TXN Details",
        accessor: "transaction_id",
        cellRenderer: (row: any) => (
          <div className="flex flex-col gap-0.5 font-mono">
            <span className="text-[9px] text-slate-500 uppercase tracking-tighter">
              ID: {row.merchant_transaction_id || row.transaction_id || "N/A"}
            </span>
            {row.payment_utr && (
              <span className="text-[9px] text-blue-400/70">
                UTR: {row.payment_utr}
              </span>
            )}
          </div>
        ),
      },
      {
        label: "Date",
        accessor: "created_at",
        cellRenderer: (row: any) => (
          <div className="text-slate-500 text-[10px] whitespace-nowrap">
            {new Date(row.created_at).toLocaleDateString()}
            <br />
            <span className="opacity-60">
              {new Date(row.created_at).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          </div>
        ),
      },
    ],
    // "Rejected Profiles": [
    //   {
    //     label: "Icon",
    //     accessor: "icon",
    //     cellRenderer: (row: any) => (
    //       <div className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-700/50 overflow-hidden flex items-center justify-center">
    //         {row.icon ? <img src={row.icon} alt="" className="w-full h-full object-cover" /> : <span className="text-slate-500 font-bold">{row.name[0]}</span>}
    //       </div>
    //     )
    //   },
    //   { label: "UserId", accessor: "userId" },
    //   { label: "Name", accessor: "name" },
    //   { label: "Gender", accessor: "gender" },
    //   { label: "Age", accessor: "age" },
    //   { label: "City", accessor: "city" },
    //   { label: "State", accessor: "state" },

    //   { label: "Gunn", accessor: "gptScore", cellRenderer: (row: any) => <span className="text-amber-400 font-mono font-bold">{row.gptScore || "0"}</span> },
    //   { label: "Match Score", accessor: "score", cellRenderer: (row: any) => <span className="text-emerald-400 font-mono font-bold">{row.score || "0"}</span> },
    //   { label: "Publish", accessor: "publish" },
    //   { label: "Decision", accessor: "decision" },
    //   { label: "Proximity", accessor: "proximity", cellRenderer: (row: any) => <span>{row.proximity} km</span> },
    //   { label: "Date", accessor: "date", cellRenderer: (row: any) => row.date ? new Date(row.date).toLocaleDateString() : 'N/A' },
    //   {
    //     label: "Analysis",
    //     accessor: "reason",
    //     cellRenderer: (row: any) => (
    //       <div className="flex flex-col gap-1">
    //         <Tooltip title={row.reason?.gap || row.reason?.overview || row.reason || "No insights"}>
    //           <div className="max-w-[150px] truncate text-[10px] text-slate-500 italic">
    //             {row.reason?.gap || row.reason?.overview || (typeof row.reason === 'string' ? row.reason : "View detailed analysis")}
    //           </div>
    //         </Tooltip>
    //         {(row.reason?.gap || row.reason?.overview || row.reason) && (
    //           <button
    //             onClick={(e) => { e.stopPropagation(); handleViewReasonButton(row); }}
    //             className="w-fit px-3 py-1 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all"
    //           >
    //             View Analysis
    //           </button>
    //         )}
    //       </div>
    //     )
    //   }
    // ],

    "Queued Profiles": [
      {
        label: "Candidate Profile",
        accessor: "candidate_info",
        cellRenderer: (row: any) => (
          <div className="flex items-center gap-3 min-w-[200px]">
            <div className="relative group/img shrink-0">
              <div className="w-12 h-12 rounded-xl bg-slate-800 border border-slate-700/50 overflow-hidden flex items-center justify-center transition-all hover:scale-105 cursor-zoom-in shadow-inner">
                {row.icon ? (
                  <img
                    src={row.icon}
                    alt={row.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-slate-500 font-bold text-lg">
                    {row.name ? row.name[0] : "U"}
                  </span>
                )}
              </div>
              {row.icon && (
                <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[10000] w-[450px] h-[450px] rounded-2xl border-4 border-slate-900 shadow-[0_0_80px_rgba(0,0,0,1)] bg-black/95 p-2 opacity-0 group-hover/img:opacity-100 pointer-events-none transition-all duration-200 hidden group-hover/img:block border-slate-800">
                  <img
                    src={row.icon}
                    alt={row.name}
                    className="w-full h-full object-contain rounded-xl"
                  />
                </div>
              )}
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="font-bold text-slate-200 text-sm whitespace-nowrap">
                {row.name}
              </span>
              <div className="flex gap-1 text-[9px] font-black uppercase tracking-tighter mt-0.5">
                <span className="text-slate-600">Age:</span>{" "}
                <span className="text-slate-400">{row.age}y</span>
                <span className="text-slate-800 ml-1">â€¢</span>
                <span className="text-slate-600 ml-1">Sex:</span>{" "}
                <span className="text-slate-400">{row.gender}</span>
              </div>
              <div className="flex items-center gap-1 mt-0.5">
                <span className="text-[7px] text-slate-700 font-black uppercase">
                  User ID:
                </span>
                <span className="text-[9px] font-mono text-slate-500 bg-slate-800/10 px-1 rounded border border-slate-800 shadow-sm">
                  {row.userId}
                </span>
              </div>
            </div>
          </div>
        ),
      },
      {
        label: "Priority & Service",
        accessor: "status_score",
        cellRenderer: (row: any) => {
          const deactivateButton = renderDeactivatePairButton(row.userId);

          return (
            <div className="flex flex-col gap-2 min-w-[130px]">
              <div className="flex flex-col bg-amber-500/5 border border-amber-500/10 rounded-lg p-1.5 shadow-inner">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[7px] font-black text-amber-500/60 uppercase tracking-widest">
                    LLM Score
                  </span>
                  <span className="text-[7px] font-black text-slate-600 uppercase tracking-widest">
                    Dist
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-amber-400 font-mono font-black text-sm leading-none">
                    {row.llmScore || "0"}
                  </span>
                  <span className="text-slate-500 font-mono text-[10px] leading-none">
                    {row.proximity || 0} km
                  </span>
                </div>
              </div>
              <div className="flex flex-col bg-indigo-500/5 border border-indigo-500/10 rounded-lg p-1.5 shadow-inner">
                <span className="text-[7px] font-black text-indigo-400/60 uppercase tracking-widest mb-0.5">
                  Source Service
                </span>
                <span className="text-indigo-300 font-bold text-[10px] leading-none truncate">
                  {!row.source_service || row.source_service === "unknown"
                    ? "default_service"
                    : row.source_service}
                </span>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (
                    row.HasError !== 1 &&
                    row.forceMatch !== "Requested" &&
                    !isRequested(row.requested)
                  )
                    handleForceMatchmaking(row.userId);
                }}
                disabled={
                  row.forceMatch === "Requested" ||
                  isRequested(row.requested) ||
                  row.HasError === 1
                }
                className={`px-3 py-2 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all border ${row.HasError === 1
                  ? "bg-rose-500/10 text-rose-500 border-rose-500/20 opacity-60"
                  : row.forceMatch === "Requested" || isRequested(row.requested)
                    ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                    : "bg-orange-500/10 text-orange-400 border-orange-500/30 hover:bg-orange-500/20 hover:border-orange-500/50"
                  }`}
              >
                {row.forceMatch === "Requested" || isRequested(row.requested)
                  ? (getRequestedLabel(row.requested) || "Requested") + " âœ“"
                  : row.HasError === 1
                    ? "Error"
                    : "Force Match"}
              </button>
              {deactivateButton && (
                <div className="flex">{deactivateButton}</div>
              )}
            </div>
          );
        },
      },
      {
        label: "Background",
        accessor: "context",
        cellRenderer: (row: any) => (
          <div className="flex flex-col gap-2 min-w-[170px]">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1.5">
                <Briefcase size={10} className="text-slate-700 shrink-0" />
                <div className="flex flex-col">
                  <span className="text-[7px] text-slate-700 font-black uppercase leading-none">
                    Job
                  </span>
                  <span className="text-[10px] text-slate-300 font-medium leading-tight truncate max-w-[130px]">
                    {row.profession || "N/A"}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <MapPin size={10} className="text-slate-700 shrink-0" />
                <div className="flex flex-col">
                  <span className="text-[7px] text-slate-700 font-black uppercase leading-none">
                    Loc
                  </span>
                  <span className="text-[10px] text-slate-400 font-bold leading-tight truncate max-w-[130px]">
                    {row.city}, {row.state}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <MapPin size={10} className="text-slate-700 shrink-0" />
                <div className="flex flex-col">
                  <span className="text-[7px] text-slate-700 font-black uppercase leading-none">
                    Work Loc
                  </span>
                  <span className="text-[10px] text-slate-300 font-bold leading-tight truncate max-w-[130px]">
                    {row.occupation_location || "N/A"}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex gap-3 border-t border-slate-800/80 pt-1.5 mt-0.5">
              <div className="flex flex-col">
                <span className="text-[7px] text-slate-700 font-black uppercase leading-none">
                  Manglik
                </span>
                <span className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">
                  {row.manglik || "Unknown"}
                </span>
              </div>
              {/* Added Intercaste and MaternalGotra */}
              <div className="flex flex-col border-l border-slate-800 pl-3">
                <span className="text-[7px] text-slate-700 font-black uppercase leading-none">
                  Intercaste
                </span>
                <span className="text-[9px] text-emerald-500 font-bold uppercase tracking-widest mt-0.5">
                  {row.intercaste === true
                    ? "Yes"
                    : row.intercaste === false
                      ? "No"
                      : "N/A"}
                </span>
              </div>
              <div className="flex flex-col border-l border-slate-800 pl-3">
                <span className="text-[7px] text-slate-700 font-black uppercase leading-none">
                  Mat. Gotra
                </span>
                <span className="text-[9px] text-purple-500 font-bold uppercase tracking-widest mt-0.5">
                  {row.maternal_gotra || "None"}
                </span>
              </div>
              <div className="flex flex-col border-l border-slate-800 pl-3 bg-indigo-500/5 px-2 py-1 rounded shadow-sm">
                <span className="text-[7px] text-indigo-400 font-black uppercase leading-none">
                  Proximity
                </span>
                <span className="text-[11px] text-indigo-300 font-black font-mono mt-0.5 tracking-tight">
                  {row.proximity || 0} km
                </span>
              </div>
            </div>
          </div>
        ),
      },
      {
        label: "Family & Insights",
        accessor: "details",
        cellRenderer: (row: any) => (
          <div className="flex flex-col gap-2 min-w-[210px]">
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <span className="text-[8px] font-black text-indigo-400 uppercase tracking-tighter bg-indigo-500/5 px-1 rounded">
                  Family Background
                </span>
                <span className="text-[8px] font-mono text-slate-600">
                  {row.date
                    ? new Date(row.date).toLocaleDateString([], {
                      day: "2-digit",
                      month: "short",
                    })
                    : ""}
                </span>
              </div>
              <Tooltip
                title={row.family_background || "No info"}
                placement="top"
              >
                <div className="text-[10px] text-slate-400 bg-slate-950/50 p-1.5 rounded border border-slate-800/80 leading-relaxed line-clamp-2 italic cursor-help">
                  {row.family_background || "Not specified"}
                </div>
              </Tooltip>
            </div>
            <div className="flex items-center justify-between border-t border-slate-800/50 pt-1.5 mt-1">
              <div className="flex flex-col">
                <span className="text-[7px] text-slate-700 font-black uppercase leading-none">
                  Family Type
                </span>
                <span className="text-[9px] text-slate-500 font-bold uppercase tracking-widest mt-0.5">
                  {row.family_type || "N/A"}
                </span>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewReasonButton(row);
                }}
                className="px-2 py-1.5 bg-blue-500/5 hover:bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded-lg text-[8px] font-black uppercase tracking-widest transition-all whitespace-nowrap"
              >
                Analysis â†’
              </button>
            </div>
          </div>
        ),
      },
      {
        label: "Compare",
        accessor: "compare",
        cellRenderer: (row: any) => {
          const baseUserId = userIdParams || userDetails.userId;
          const candidateId = row.userId || row.candidate_id;
          return (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (!baseUserId || !candidateId) {
                  toast.error("Missing user or candidate id");
                  return;
                }
                navigate(
                  `/match-pair?user_id=${encodeURIComponent(baseUserId)}&candidate_id=${encodeURIComponent(candidateId)}`,
                );
              }}
              className="px-3 py-1.5 bg-indigo-600/10 hover:bg-indigo-600 text-indigo-400 hover:text-white border border-indigo-500/20 rounded-lg transition-all flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-widest shadow-sm whitespace-nowrap"
            >
              <GitCompare size={14} /> Compare
            </button>
          );
        },
      },
    ],
    "Skipped Matchess": [
      {
        label: "Force Match",
        accessor: "forceMatch",
        cellRenderer: (row: any) => {
          const deactivateButton = renderDeactivatePairButton(row.userId);

          return (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (
                    row.forceMatch !== "Requested" &&
                    !isRequested(row.requested) &&
                    row.HasError !== 1
                  ) {
                    handleForceMatchmaking(row.userId);
                  }
                }}
                disabled={
                  row.forceMatch === "Requested" ||
                  isRequested(row.requested) ||
                  row.HasError === 1
                }
                className={`
                  group relative overflow-hidden px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-[0.12em] transition-all duration-300
                  flex items-center gap-2.5 backdrop-blur-md border border-white/5
                  ${row.HasError === 1
                    ? "bg-rose-500/5 text-rose-400 border-rose-500/20 cursor-not-allowed opacity-60"
                    : row.forceMatch === "Requested" || isRequested(row.requested)
                      ? "bg-indigo-500/10 text-indigo-400 border-indigo-500/20 cursor-default shadow-[inset_0_0_12px_rgba(99,102,241,0.05)]"
                      : "bg-indigo-500/10 text-indigo-400 border-indigo-500/30 hover:bg-indigo-500/20 hover:text-indigo-300 hover:border-indigo-500/50 hover:shadow-[0_8px_20px_-6px_rgba(99,102,241,0.3)] active:scale-95 cursor-pointer"
                  }
                `}
              >
                {row.forceMatch === "Requested" || isRequested(row.requested) ? (
                  <>
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse shadow-[0_0_8px_rgba(99,102,241,0.5)]"></div>
                    {getRequestedLabel(row.requested) || "Requested"}
                  </>
                ) : row.HasError === 1 ? (
                  "Error State"
                ) : (
                  "Force Match"
                )}
                {!(row.forceMatch === "Requested" || isRequested(row.requested)) &&
                  row.HasError !== 1 && (
                    <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/[0.05] to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                  )}
              </button>
              {deactivateButton && (
                <div className="mt-2 flex">{deactivateButton}</div>
              )}
            </>
          );
        },
      },
      { label: "UserId", accessor: "userId" },
      { label: "Name", accessor: "name" },
      { label: "Gender", accessor: "gender" },
      { label: "Age", accessor: "age" },
      { label: "City", accessor: "city" },
      { label: "State", accessor: "state" },
      { label: "Occupation", accessor: "occupation" },
      {
        label: "Score Status",
        accessor: "score_status",
        cellRenderer: (row: any) => {
          const reason = row.reason;
          let score: number | null = null;
          let isFailed = false;

          // 1. Try direct reason.score
          if (
            reason?.score !== undefined &&
            reason?.score !== null &&
            reason?.score !== ""
          ) {
            score = parseFloat(reason.score);
          }

          // 2. Fallback: parse score from reason.reason text via regex (e.g. "Low score (20)" or "Score 0:")
          if (score === null && typeof reason?.reason === "string") {
            const match = reason.reason.match(
              /(?:score|Score)\s*[:(]?\s*(\d+(?:\.\d+)?)/i,
            );
            if (match) {
              score = parseFloat(match[1]);
            }
          }

          // 3. Check if text indicates failure (Astrology Failed, Hard block, etc.)
          const reasonText =
            typeof reason === "string"
              ? reason
              : reason?.reason || reason?.reason_gap || "";
          if (
            typeof reasonText === "string" &&
            /failed|hard\s*block/i.test(reasonText)
          ) {
            isFailed = true;
          }
          if (
            reason?.nonneg?.failed &&
            Object.keys(reason.nonneg.failed).length > 0
          ) {
            isFailed = true;
          }

          // Determine display
          if (score === 0 || (score === null && isFailed)) {
            return (
              <div className="flex flex-col items-start gap-1">
                <span className="px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest bg-rose-500/15 text-rose-400 border border-rose-500/30">
                  âœ• Failed
                </span>
                <span className="text-[9px] text-rose-500/60 font-mono">
                  Score: 0
                </span>
              </div>
            );
          } else if (score !== null && score > 0) {
            const scoreColor =
              score >= 50
                ? "text-emerald-400"
                : score >= 20
                  ? "text-amber-400"
                  : "text-orange-400";
            const scoreBg =
              score >= 50
                ? "bg-emerald-500/15 border-emerald-500/30"
                : score >= 20
                  ? "bg-amber-500/15 border-amber-500/30"
                  : "bg-orange-500/15 border-orange-500/30";
            return (
              <div className="flex flex-col items-start gap-1">
                <span
                  className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest ${scoreBg} ${scoreColor} border`}
                >
                  Score: {score}
                </span>
                {reason?.label && (
                  <span className="text-[9px] text-slate-500 font-medium italic">
                    {reason.label}
                  </span>
                )}
              </div>
            );
          } else {
            return (
              <span className="text-[10px] text-slate-600 italic">â€”</span>
            );
          }
        },
      },

      {
        label: "Skipped At",
        accessor: "skipped_at",
        cellRenderer: (row: any) => (
          <div className="flex flex-col text-[10px] leading-tight font-medium text-slate-500">
            <span>
              {row.skipped_at
                ? new Date(row.skipped_at).toLocaleDateString([], {
                  day: "2-digit",
                  month: "short",
                  year: "2-digit",
                })
                : "N/A"}
            </span>
            <span className="opacity-70">
              {row.skipped_at
                ? new Date(row.skipped_at).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })
                : ""}
            </span>
          </div>
        ),
      },
      {
        label: "Reason",
        accessor: "reason",
        cellRenderer: (row: any) => {
          const reason = row.reason;
          if (
            !reason ||
            (typeof reason === "object" && Object.keys(reason).length === 0)
          ) {
            return (
              <span className="text-[10px] text-slate-600 italic">
                No reason
              </span>
            );
          }

          // Build formatted reason lines
          const lines: { text: string; color: string }[] = [];
          const plainReason =
            typeof reason === "string"
              ? reason
              : typeof reason?.reason === "string"
                ? reason.reason
                : "";

          if (reason?.label) {
            const labelColor =
              reason.label === "Low Fit"
                ? "text-rose-400"
                : reason.label === "Explore"
                  ? "text-amber-400"
                  : reason.label === "Strong"
                    ? "text-emerald-400"
                    : "text-purple-400";
            lines.push({
              text: `âš¡ ${reason.label}${reason.score ? ` (Score: ${reason.score})` : ""}`,
              color: labelColor,
            });
          }

          if (
            reason?.nonneg?.failed &&
            Object.keys(reason.nonneg.failed).length > 0
          ) {
            lines.push({
              text: "âŒ NON-NEGOTIABLE FAILED:",
              color: "text-rose-500 font-black",
            });
            Object.entries(reason.nonneg.failed).forEach(
              ([key, vals]: [string, any]) => {
                const reasons = Array.isArray(vals)
                  ? vals.join(", ")
                  : String(vals);
                lines.push({
                  text: `   âœ– [${key}] ${reasons}`,
                  color: "text-rose-400",
                });
              },
            );
          }

          if (
            reason?.nonneg?.satisfied &&
            Object.keys(reason.nonneg.satisfied).length > 0
          ) {
            lines.push({
              text: "âœ… Non-negotiables satisfied:",
              color: "text-emerald-500",
            });
            Object.entries(reason.nonneg.satisfied).forEach(
              ([key, vals]: [string, any]) => {
                const reasons = Array.isArray(vals)
                  ? vals.join(", ")
                  : String(vals);
                lines.push({
                  text: `   âœ” [${key}] ${reasons}`,
                  color: "text-emerald-400",
                });
              },
            );
          }

          if (reason?.positives?.length > 0) {
            lines.push({ text: "ðŸŸ¢ Positives:", color: "text-emerald-500" });
            reason.positives.forEach((p: any) => {
              lines.push({
                text: `   +${p.delta} ${p.rule}`,
                color: "text-emerald-400",
              });
            });
          }

          if (reason?.negatives?.length > 0) {
            lines.push({ text: "ðŸ”´ Negatives:", color: "text-rose-500" });
            reason.negatives.forEach((n: any) => {
              lines.push({
                text: `   ${n.delta} ${n.rule}`,
                color: "text-rose-400",
              });
            });
          }

          if (reason?.commentary_bullets?.length > 0) {
            lines.push({ text: "ðŸ“‹ Overview:", color: "text-blue-400" });
            reason.commentary_bullets.forEach((b: string) => {
              lines.push({ text: `   â€¢ ${b}`, color: "text-slate-400" });
            });
          }

          if (reason?.reason_gap) {
            lines.push({
              text: `âš ï¸ ${reason.reason_gap}`,
              color: "text-amber-400",
            });
          }

          if (lines.length === 0 && plainReason) {
            lines.push({ text: plainReason, color: "text-slate-300" });
          }

          if (lines.length === 0) {
            return (
              <span className="text-[10px] text-slate-600 italic">
                No reason
              </span>
            );
          }

          const popupContent = (
            <div className="space-y-[2px]">
              {lines.map((line, i) => (
                <div
                  key={i}
                  className={`text-[10px] leading-[1.5] whitespace-pre-wrap break-words ${line.color}`}
                >
                  {line.text}
                </div>
              ))}
            </div>
          );

          return (
            <Tooltip
              title={popupContent}
              arrow
              placement="left-start"
              enterDelay={200}
              leaveDelay={100}
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "rgb(15, 23, 42)",
                    border: "1px solid rgba(100, 116, 139, 0.3)",
                    borderRadius: "12px",
                    p: "12px",
                    maxWidth: "480px",
                    maxHeight: "400px",
                    overflowY: "auto",
                    boxShadow: "0 12px 40px rgba(0,0,0,0.8)",
                    "& .MuiTooltip-arrow": {
                      color: "rgb(15, 23, 42)",
                    },
                  },
                },
              }}
            >
              <div
                className="max-w-[320px] min-w-[220px] cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              >
                {lines.slice(0, 3).map((line, i) => (
                  <div
                    key={i}
                    className={`text-[10px] leading-[1.45] whitespace-pre-wrap break-words overflow-hidden ${line.color}`}
                  >
                    {line.text}
                  </div>
                ))}
                {lines.length > 3 && (
                  <span className="text-[9px] text-slate-600 italic">
                    ...hover for more
                  </span>
                )}
              </div>
            </Tooltip>
          );
        },
      },
    ],
    "Potential Matches": [
      {
        label: "Candidate Profile",
        accessor: "candidate_info",
        cellRenderer: (row: any) => (
          <div className="flex items-center gap-3 min-w-[210px]">
            <div className="relative group/img shrink-0">
              {row.photo_url || row.photo_compressed_url ? (
                <Tooltip
                  title={
                    <div className="bg-slate-950/90 backdrop-blur-xl border-4 border-slate-900 shadow-[0_0_80px_rgba(0,0,0,0.8)] rounded-2xl overflow-hidden p-1 w-[450px] h-[450px]">
                      <img
                        src={row.photo_url || row.photo_compressed_url}
                        alt={row.name}
                        className="w-full h-full object-contain rounded-xl"
                      />
                    </div>
                  }
                  placement="right"
                  arrow
                >
                  <div className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-700/50 overflow-hidden flex items-center justify-center shadow-inner transition-all hover:scale-105 cursor-zoom-in relative">
                    <img
                      src={row.photo_compressed_url || row.photo_url}
                      alt={row.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </Tooltip>
              ) : (
                <div className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-700/50 overflow-hidden flex items-center justify-center shadow-inner">
                  <span className="text-slate-500 font-bold text-lg">
                    {row.name ? row.name[0] : "U"}
                  </span>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="font-bold text-slate-200 text-sm whitespace-nowrap">
                {row.name || "N/A"}
              </span>
              <div className="flex gap-1 text-[9px] font-black uppercase tracking-tighter mt-0.5">
                <span className="text-slate-600">Age:</span>{" "}
                <span className="text-slate-400">{row.age}y</span>
                <span className="text-slate-800 ml-1">â€¢</span>
                <span className="text-slate-600 ml-1">Sex:</span>{" "}
                <span className="text-slate-400">{row.gender}</span>
              </div>
              <div className="flex items-center gap-1 mt-0.5">
                <span className="text-[7px] text-slate-700 font-black uppercase">
                  Matri ID:
                </span>
                <span className="text-[9px] font-mono text-indigo-400 font-bold bg-indigo-500/5 px-1 rounded border border-indigo-500/10 shadow-sm">
                  {row.matrimonial_id}
                </span>
              </div>
            </div>
          </div>
        ),
      },
      {
        label: "Background & Location",
        accessor: "bg_location",
        cellRenderer: (row: any) => (
          <div className="flex flex-col gap-1.5 min-w-[170px]">
            <div className="flex items-start gap-1">
              <MapPin size={10} className="text-slate-700 shrink-0 mt-0.5" />
              <div className="flex flex-col">
                <span className="text-[7px] text-slate-700 font-black uppercase leading-none">
                  Location
                </span>
                <span className="text-[10px] text-slate-400 font-bold leading-tight">
                  {row.city}, {row.state}
                </span>
              </div>
            </div>
            <div className="flex flex-col border-t border-slate-800/50 pt-1.5 mt-1">
              <div className="flex flex-col">
                <span className="text-[7px] text-slate-700 font-black uppercase leading-none">
                  Caste / Gotra
                </span>
                <span className="text-[9px] text-slate-300 font-medium mt-0.5">
                  {row.caste || "N/A"}{" "}
                  <span className="text-slate-600 ml-1">/</span>{" "}
                  <span className="text-slate-500 italic ml-1">
                    {row.gotra || "N/A"}
                  </span>
                </span>
              </div>
            </div>
            <div
              className={`px-1.5 py-0.5 rounded text-[8px] font-black uppercase border w-fit mt-1 border-white/5 ${row.intercaste ? "bg-emerald-500/10 text-emerald-400" : "bg-slate-800/50 text-slate-600"}`}
            >
              {row.intercaste
                ? "Caste Preference: OPEN"
                : "Caste Preference: NO"}
            </div>
          </div>
        ),
      },
      {
        label: "Professional & About",
        accessor: "pro_about",
        cellRenderer: (row: any) => (
          <div className="flex flex-col gap-2 min-w-[220px]">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1.5">
                <Briefcase size={10} className="text-slate-700 shrink-0" />
                <div className="flex flex-col">
                  <span className="text-[7px] text-slate-700 font-black uppercase leading-none">
                    Profession
                  </span>
                  <span className="text-[10px] text-slate-300 font-medium leading-tight truncate max-w-[150px]">
                    {row.occupation || "N/A"}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <GraduationCap size={10} className="text-slate-700 shrink-0" />
                <div className="flex flex-col">
                  <span className="text-[7px] text-slate-700 font-black uppercase leading-none">
                    Education
                  </span>
                  <span className="text-[10px] text-slate-400 font-medium leading-tight truncate max-w-[150px]">
                    {row.qualification || "N/A"}
                  </span>
                </div>
              </div>
            </div>
            <div className="bg-slate-950/40 p-1.5 rounded-xl border border-white/5 shadow-inner">
              <div className="text-[10px] text-slate-400 italic leading-relaxed whitespace-pre-wrap break-words line-clamp-2">
                "{row.about_me || "No bio provided"}"
              </div>
            </div>
          </div>
        ),
      },
      {
        label: "Workflow",
        accessor: "workflow",
        cellRenderer: (row: any) => (
          <div className="flex flex-col gap-2 items-center min-w-[120px]">
            <button
              onClick={async (e) => {
                e.stopPropagation();
                if (
                  window.confirm(
                    `Trigger Single Queue Match for ${row.name || row.candidate_id}?`,
                  )
                ) {
                  try {
                    LoaderService.show();
                    const response = await PostServiceJob({
                      service_name: "matchmaking_queue_single",
                      user_id: userIdParams || userDetails.userId,
                      candidate_id: row.candidate_id,
                    });
                    const isSuccess =
                      response?.statusCode === 200 ||
                      response?.body?.success === true ||
                      response?.status === "success" ||
                      response?.success === true;
                    if (isSuccess)
                      toast.success(
                        response?.body?.message ||
                        response?.message ||
                        "Queue Match Triggered",
                      );
                    else
                      toast.error(
                        response?.body?.message ||
                        response?.message ||
                        "Failed to trigger Queue Match",
                      );
                  } catch (error: any) {
                    toast.error(error.message || "Error occurred");
                  } finally {
                    LoaderService.hide();
                  }
                }
              }}
              className="w-full px-3 py-1.5 bg-emerald-600/10 hover:bg-emerald-600 text-emerald-400 hover:text-white border border-emerald-500/20 rounded-lg transition-all flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-widest shadow-sm"
            >
              <Layers size={14} /> Queue
            </button>
            <span
              className={`px-2 py-0.5 mt-1 rounded text-[8px] font-black uppercase border shadow-sm ${row.is_active ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-rose-500/10 text-rose-400 border-rose-500/20"}`}
            >
              {row.is_active ? "Active" : "Inactive"}
            </span>
          </div>
        ),
      },
    ],
    "Like To": [
      {
        label: "Interactions",
        accessor: "likes",
        cellRenderer: (row: any) => (
          <div className="flex flex-col gap-1.5 min-w-[140px]">
            {row.likedBy && (
              <div className="flex flex-col bg-blue-500/10 border border-blue-500/20 rounded-lg p-1.5">
                <span className="text-[8px] font-black text-blue-400 uppercase tracking-tighter mb-0.5">
                  Liked By
                </span>
                <span className="text-[10px] font-bold text-slate-200 truncate">
                  {row.likedBy.name}
                </span>
                <span className="text-[8px] font-mono text-blue-400/70">
                  {row.likedBy.matrimonial_id}
                </span>
              </div>
            )}
            {row.likedTo && (
              <div className="flex flex-col bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-1.5">
                <span className="text-[8px] font-black text-emerald-400 uppercase tracking-tighter mb-0.5">
                  Liked To
                </span>
                <span className="text-[10px] font-bold text-slate-200 truncate">
                  {row.likedTo.name}
                </span>
                <span className="text-[8px] font-mono text-emerald-400/70">
                  {row.likedTo.matrimonial_id}
                </span>
              </div>
            )}
            {row.like === false && row.likeReason && (
              <div className="flex flex-col gap-0.5 mt-1 px-2 py-1 bg-yellow-500/5 border border-yellow-500/10 rounded-lg shadow-inner">
                <div className="flex items-center gap-1.5">
                  <HeartOff size={10} className="text-yellow-500/70" />
                  <span className="text-[7px] text-yellow-500/50 font-black uppercase tracking-widest">
                    Dislike Reason
                  </span>
                </div>
                <span className="text-[9.5px] text-yellow-500/80 italic line-clamp-3 leading-snug">
                  "{row.likeReason}"
                </span>
              </div>
            )}
          </div>
        ),
      },
      {
        label: "Icon",
        accessor: "icon",
        cellRenderer: (row: any) => (
          <div className="relative group/img">
            <div className="w-12 h-12 rounded-xl bg-slate-800 border border-slate-700/50 overflow-hidden flex items-center justify-center shadow-inner transition-all hover:scale-105 cursor-zoom-in">
              {row.icon && row.icon.startsWith("http") ? (
                <img
                  src={row.icon}
                  alt={row.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-slate-500 font-bold text-lg">
                  {row.name ? row.name[0] : "U"}
                </span>
              )}
            </div>
          </div>
        ),
      },
      {
        label: "User info",
        accessor: "userInfo",
        cellRenderer: (row: any) => (
          <div className="flex flex-col">
            <span className="font-bold text-slate-200">{row.name}</span>
            <span className="text-[10px] font-mono text-slate-500">
              {row.matrimonial_id}
            </span>
          </div>
        ),
      },
      {
        label: "Mobile",
        accessor: "mobile",
        cellRenderer: (row: any) => (
          <span className="text-emerald-400 font-mono font-bold text-[11px] tracking-tighter">
            {row.mobile}
          </span>
        ),
      },
      { label: "City", accessor: "city" },
      {
        label: "Profession",
        accessor: "profession",
        cellRenderer: (row: any) => (
          <span
            className="text-[11px] text-slate-400 italic truncate max-w-[150px]"
            title={row.profession}
          >
            {row.profession}
          </span>
        ),
      },
      {
        label: "Liked Date",
        accessor: "date",
        cellRenderer: (row: any) => (
          <div className="flex flex-col text-[10px] leading-tight font-medium text-slate-500">
            <span>
              {row.date
                ? new Date(row.date).toLocaleDateString([], {
                  day: "2-digit",
                  month: "short",
                  year: "2-digit",
                })
                : "N/A"}
            </span>
            <span className="opacity-70">
              {row.date
                ? new Date(row.date).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })
                : ""}
            </span>
          </div>
        ),
      },
    ],
    "Liked By": [
      {
        label: "Icon",
        accessor: "icon",
        cellRenderer: (row: any) => (
          <div className="relative group/img">
            <div className="w-12 h-12 rounded-xl bg-slate-800 border border-slate-700/50 overflow-hidden flex items-center justify-center shadow-inner transition-all hover:scale-105 cursor-zoom-in">
              {row.icon && row.icon.startsWith("http") ? (
                <img
                  src={row.icon}
                  alt={row.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-slate-500 font-bold text-lg">
                  {row.name ? row.name[0] : "U"}
                </span>
              )}
            </div>
          </div>
        ),
      },
      {
        label: "User info",
        accessor: "userInfo",
        cellRenderer: (row: any) => (
          <div className="flex flex-col">
            <span className="font-bold text-slate-200">{row.name}</span>
            <span className="text-[10px] font-mono text-slate-500">
              {row.matrimonial_id}
            </span>
          </div>
        ),
      },
      {
        label: "Mobile",
        accessor: "mobile",
        cellRenderer: (row: any) => (
          <span className="text-emerald-400 font-mono font-bold text-[11px] tracking-tighter">
            {row.mobile}
          </span>
        ),
      },
      {
        label: "City",
        accessor: "city",
        cellRenderer: (row: any) => (
          <div className="flex items-center gap-1.5 text-slate-300">
            <MapPin size={12} className="text-indigo-400/50" />
            <span className="text-[10px] font-bold">{row.city}</span>
          </div>
        ),
      },
      {
        label: "Profession",
        accessor: "profession",
        cellRenderer: (row: any) => (
          <div className="flex items-center gap-1.5 text-slate-400">
            <Briefcase size={12} className="text-amber-400/50" />
            <span
              className="text-[10px] font-medium italic truncate max-w-[120px]"
              title={row.profession}
            >
              {row.profession}
            </span>
          </div>
        ),
      },
      {
        label: "Liked At",
        accessor: "date",
        cellRenderer: (row: any) => (
          <div className="flex flex-col text-[10px] leading-tight font-medium text-slate-500">
            <span>
              {row.date
                ? new Date(row.date).toLocaleDateString([], {
                  day: "2-digit",
                  month: "short",
                  year: "2-digit",
                })
                : "N/A"}
            </span>
            <span className="opacity-70">
              {row.date
                ? new Date(row.date).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })
                : ""}
            </span>
          </div>
        ),
      },
    ],
  };

  columnMap["Special Pref Skipped"] = columnMap["Skipped Matchess"];
  columnMap["Special Pref Requeued"] = [
    {
      label: "UserId",
      accessor: "userId",
    },
    {
      label: "Name",
      accessor: "name",
    },
    {
      label: "Location",
      accessor: "location",
    },
    {
      label: "Occupation",
      accessor: "occupation",
    },
    {
      label: "Queue Mode",
      accessor: "reactivation_mode",
      cellRenderer: (row: any) => (
        <span
          className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest border ${row.reactivation_mode === "inserted"
            ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
            : "bg-indigo-500/15 text-indigo-400 border-indigo-500/30"
            }`}
        >
          {row.reactivation_mode === "inserted"
            ? "Inserted"
            : "Requeued"}
        </span>
      ),
    },
    {
      label: "Reactivated At",
      accessor: "reactivated_at",
      cellRenderer: (row: any) => (
        <div className="flex flex-col text-[10px] leading-tight font-medium text-slate-500">
          <span>
            {row.reactivated_at
              ? new Date(row.reactivated_at).toLocaleDateString([], {
                day: "2-digit",
                month: "short",
                year: "2-digit",
              })
              : "N/A"}
          </span>
          <span className="opacity-70">
            {row.reactivated_at
              ? new Date(row.reactivated_at).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })
              : ""}
          </span>
        </div>
      ),
    },
    {
      label: "Queue Source",
      accessor: "queue_source_service",
      cellRenderer: (row: any) => (
        <span className="text-[10px] text-slate-400 font-medium">
          {row.queue_source_service || "existing_queue"}
        </span>
      ),
    },
    {
      label: "Reason",
      accessor: "reason",
      cellRenderer: (row: any) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleViewReasonButton(row);
          }}
          className="px-3 py-1.5 bg-blue-500/5 hover:bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded-lg text-[8px] font-black uppercase tracking-widest transition-all whitespace-nowrap"
        >
          View Reason
        </button>
      ),
    },
  ];

  const { userIdParams } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const _userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
  const {
    userId,
    name,
    email,
    phone,
    gender,
    Gender,
    age,
    status,
    progress,
    verified,
  } = _userDetails;
  const [userDetails, setUserDetails] = useState<IUserDetails>({
    userId: userId || "",
    name: name || "",
    email: email || "",
    phone: phone || "",
    matrimonial_phone:
      _userDetails.matrimonial_phone || _userDetails.matrimonialPhone || "",
    user_phone: _userDetails.user_phone || _userDetails.userPhone || "",
    gender: gender || Gender || "",
    Gender: Gender || gender || "",
    age: age || "",
    status: status || "",
    totalCredits: 0,
    creditsUsed: 0,
    location: undefined,
    version: undefined,
    platform: undefined,
    city: undefined,
    state: undefined,
    occupation: undefined,
    Occupation: undefined,
    manglik: undefined,
    lastLogin: undefined,
    photo: undefined,
    adminNote: undefined,
    createdAt: undefined,
    verified: verified || false,
    progress: progress ? parseFloat(String(progress).replace("%", "")) : 0,
    dislikeReasons: undefined,
  });
  const [activeTab, setActiveTab] = useState<string>(tabs[0]);
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [tempNote, setTempNote] = useState("");
  const [dropdownNotes, setDropdownNotes] = useState<string[]>([]);
  const [dropdownData, setDropdownData] = useState<Record<string, any>>({});
  const [isAddingOthers, setIsAddingOthers] = useState(false);
  const [candidateUserId, setCandidateUserId] = useState<string>("");
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null);
  const [currentUsername, setCurrentUsername] = useState<string | null>(null);
  const [isPhotoUploading, setIsPhotoUploading] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const [comparisonModal, setComparisonModal] = useState({
    status: false,
    type: "",
  });
  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
  const [confirmEnableModal, setConfirmEnableModal] = useState(false);
  const [confirmDisableModal, setConfirmDisableModal] = useState(false);
  const [holdUserModal, setHoldUserModal] = useState(false);
  const [holdDurationDays, setHoldDurationDays] = useState("7");
  const [holdReason, setHoldReason] = useState("");
  const [holdStartedAt, setHoldStartedAt] = useState(() => new Date().toISOString());
  const [confirmAddPaymentModal, setConfirmAddPaymentModal] = useState(false);
  const [confirmAddWhatsappPdfModal, setConfirmAddWhatsappPdfModal] =
    useState(false);
  const [confirmVerifyModal, setConfirmVerifyModal] = useState(false);
  const [confirmDeactivatePairModal, setConfirmDeactivatePairModal] =
    useState(false);
  const [pendingDeactivateCandidateId, setPendingDeactivateCandidateId] =
    useState<string>("");
  const [errorModal, setErrorModal] = useState({
    show: false,
    message: "",
    variant: "error",
  });
  const [resetPasswordModal, setResetPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState("Welcome@123");
  const [confirmPublishModal, setConfirmPublishModal] = useState(false);
  const [confirmResetMatchingModal, setConfirmResetMatchingModal] =
    useState(false);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const [publishPending, setPublishPending] = useState<{
    type: "individual" | "match_row";
    candidateId: string;
    row?: any;
  } | null>(null);
  const [publishInstructionData, setPublishInstructionData] =
    useState<any>(null);
  const [publishInstructionLoading, setPublishInstructionLoading] =
    useState(false);
  // Gate violations preview for the publish popup: {overridable, blocking, can_override, needs_override}
  const [publishViolations, setPublishViolations] = useState<any>(null);
  const [publishOverrideReason, setPublishOverrideReason] = useState("");
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const buildErrorMessage = (response: any) => {
    let message = response?.message || "Operation failed";

    if (
      Array.isArray(response?.missing_fields) &&
      response?.missing_fields.length > 0
    ) {
      message += `\n\nMissing fields: ${response.missing_fields.join(", ")}`;
    }

    return message;
  };

  const [showLogs, setShowLogs] = useState<{
    common: boolean;
    userLogs: boolean;
  }>({
    common: false,
    userLogs: false,
  });

  const [transactionsData, setTransactionsData] = useState<any>({
    transactions: [],
    summary: {
      total_purchased: 0,
      total_spent: 0,
      total_granted: 0,
      transaction_count: 0,
    },
    usage_analytics: {
      total_credits_used: 0,
      total_credits_added: 0,
      usage_by_type: {},
      most_used_feature: "N/A",
      last_usage_date: null,
    },
    detailed_usages: [],
  });

  const [deactivatedMatchesModalOpen, setDeactivatedMatchesModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [userHistory, setUserHistory] = useState<any[]>([]);
  const [activeHistoryTab, setActiveHistoryTab] = useState<"Profile" | "Preference">("Profile");
  const [historyDateFilter, setHistoryDateFilter] = useState<string>("all");
  const [historySearchQuery, setHistorySearchQuery] = useState<string>("");
  const actionMenuRef = useRef<HTMLDivElement>(null);

  const fetchUserHistory = async (targetUserId: string) => {
    setHistoryLoading(true);
    try {
      const response = await GetUserHistory(targetUserId);
      if (response.status === "success") {
        setUserHistory(response.data);
      } else {
        toast.error("Failed to fetch history");
      }
    } catch (error) {
      toast.error("Error fetching history");
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        actionMenuRef.current &&
        !actionMenuRef.current.contains(event.target as Node)
      ) {
        setIsActionMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsActionMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);


  const quickActionToneClasses = {
    indigo: "border-indigo-500/20 bg-indigo-500/10 text-indigo-400",
    emerald: "border-emerald-500/20 bg-emerald-500/10 text-emerald-400",
    amber: "border-amber-500/20 bg-amber-500/10 text-amber-400",
    blue: "border-blue-500/20 bg-blue-500/10 text-blue-400",
    orange: "border-orange-500/20 bg-orange-500/10 text-orange-400",
    rose: "border-rose-500/20 bg-rose-500/10 text-rose-400",
    pink: "border-pink-500/20 bg-pink-500/10 text-pink-400",
    slate: "border-slate-500/20 bg-slate-500/10 text-slate-300",
  } as const;

  const displayHoldDate = useMemo(
    () =>
      new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(holdStartedAt)),
    [holdStartedAt],
  );

  const [transactionFilters, setTransactionFilters] = useState({
    status: "",
    transaction_type: "",
  });

  const [showAstroModal, setShowAstroModal] = useState(false);
  const [sammelanEvents, setSammelanEvents] = useState<any[]>([]);
  const [selectedSammelanEvent, setSelectedSammelanEvent] =
    useState<string>("");
  const [isAssigningSammelan, setIsAssigningSammelan] = useState(false);
  const [showRemoveSammelanModal, setShowRemoveSammelanModal] = useState(false);
  const [removeSammelanTarget, setRemoveSammelanTarget] = useState<{
    eventId: number;
    eventName: string;
  } | null>(null);
  const [astrologyDetailsModal, setAstrologyDetailsModal] = useState<{
    open: boolean;
    loading: boolean;
    error: string;
    data: any | null;
    candidateName: string;
  }>({
    open: false,
    loading: false,
    error: "",
    data: null,
    candidateName: "",
  });
  const [matchMakingStep, setMatchMakingStep] = useState(1);
  const [matchMakingStepText, setMatchMakingStepText] = useState(
    `Match Making For UserId: ${userId} or CandidateUserId: ${candidateUserId}`,
  );
  const [matchMakingPrompt, setMatchMakingPrompt] = useState({});
  const [showMatchMakingModal, setShowMatchMakingModal] = useState(false);
  const [search, setSearch] = useState("");
  const [supportAdmins, setSupportAdmins] = useState<any[]>([]);
  const [phoneCallModal, setPhoneCallModal] = useState<{
    isOpen: boolean;
    loading: boolean;
    data: any | null;
    adminName: string;
    feedback: string;
    candidateId: string;
  }>({
    isOpen: false,
    loading: false,
    data: null,
    adminName: "",
    feedback: "",
    candidateId: "",
  });
  const [userProfileData, setUserProfileData] = useState({
    profile: {},
    preference: {},
    photos: [],
    bioData: "",
    sammelanRegistrations: [],
  });
  const [preferenceCategories, setPreferenceCategories] = useState<any[]>([]);
  const [candidateProfileData, setCandidateProfileData] = useState({
    profile: {},
    preference: {},
    photos: [],
    bioData: "",
    sammelanRegistrations: [],
  });
  const [password, setPassword] = useState("");
  // â”€â”€ Special Preference Notes (API-driven) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const [specialPreferenceNotes, setSpecialPreferenceNotes] = useState<
    Record<string, any>
  >({});
  const [selectedSpecialPreference, setSelectedSpecialPreference] =
    useState("");
  const [specialPreferenceMode, setSpecialPreferenceMode] = useState<
    "includes" | "excludes"
  >("excludes");
  const [specialPreferenceSearch, setSpecialPreferenceSearch] = useState("");

  // New state for detailed selections
  const [selectedSpecialPreferenceValues, setSelectedSpecialPreferenceValues] =
    useState<string[]>([]);
  const [customSpecialPreferenceValue, setCustomSpecialPreferenceValue] =
    useState("");
  const [countrySearch, setCountrySearch] = useState("");
  const [locationSearch, setLocationSearch] = useState("");
  const [stateSearch, setStateSearch] = useState("");
  const [stateApiOptions, setStateApiOptions] = useState<string[]>([]);
  const [cityApiOptions, setCityApiOptions] = useState<string[]>([]);
  const [qualificationSearch, setQualificationSearch] = useState("");
  const [professionSearch, setProfessionSearch] = useState("");
  const [heightSearch, setHeightSearch] = useState("");
  const [heightMin, setHeightMin] = useState("");
  const [heightMax, setHeightMax] = useState("");
  const [mappedPreferenceSearch, setMappedPreferenceSearch] = useState("");
  const [selectedAge, setSelectedAge] = useState("");
  const [ageMin, setAgeMin] = useState("");
  const [ageMax, setAgeMax] = useState("");
  const [selectedSalary, setSelectedSalary] = useState("");
  const [selectedProximity, setSelectedProximity] = useState("");
  const [salaryMin, setSalaryMin] = useState("");
  const [salaryMax, setSalaryMax] = useState("");
  const [proximityMin, setProximityMin] = useState("");
  const [proximityMax, setProximityMax] = useState("");

  // Special preference options
  const specialPreferenceOptions = [
    { value: "country", label: "Country" },
    { value: "location", label: "City" },
    { value: "working_location", label: "Working Location" },
    { value: "state", label: "State" },
    { value: "age", label: "Age" },
    { value: "qualification", label: "Qualification" },
    { value: "profession", label: "Profession" },
    { value: "height", label: "Height" },
    { value: "dietary", label: "Dietary" },
    { value: "family_type", label: "Family Type" },
    { value: "family_background", label: "Family Background" },
    { value: "marital_status", label: "Marital Status" },
    { value: "working_with", label: "Working With" },
    { value: "relocation_willingness", label: "Relocation Willingness" },
    { value: "hobbies", label: "Hobbies" },
    { value: "manglik", label: "Manglik" },
    { value: "caste", label: "Caste" },
    { value: "sub_caste", label: "Sub Caste" },
    { value: "gotra", label: "Gotra" },
    { value: "languages_known", label: "Languages Known" },
    { value: "salary", label: "Salary" },
    { value: "proximity_range", label: "Proximity Range" },
  ];

  const heightOptions = [
    "4ft 5in - 134cm",
    "4ft 6in - 137cm",
    "4ft 7in - 139cm",
    "4ft 8in - 142cm",
    "4ft 9in - 144cm",
    "4ft 10in - 147cm",
    "4ft 11in - 149cm",
    "5ft 0in - 152cm",
    "5ft 1in - 154cm",
    "5ft 2in - 157cm",
    "5ft 3in - 160cm",
    "5ft 4in - 162cm",
    "5ft 5in - 165cm",
    "5ft 6in - 167cm",
    "5ft 7in - 170cm",
    "5ft 8in - 172cm",
    "5ft 9in - 175cm",
    "5ft 10in - 177cm",
    "5ft 11in - 180cm",
    "6ft 0in - 182cm",
    "6ft 1in - 185cm",
    "6ft 2in - 187cm",
    "6ft 3in - 190cm",
    "6ft 4in - 193cm",
    "6ft 5in - 195cm",
    "6ft 6in - 198cm",
    "6ft 7in - 200cm",
    "6ft 8in - 203cm",
    "6ft 9in - 205cm",
    "6ft 10in - 208cm",
    "6ft 11in - 210cm",
    "7ft 0in - 213cm",
  ];

  const qualificationOptions = [
    "B.Tech/B.E.",
    "M.Tech/M.E.",
    "BCA",
    "MCA",
    "B.Sc.",
    "M.Sc.",
    "B.Com",
    "M.Com",
    "BBA",
    "MBA",
    "B.A.",
    "M.A.",
    "B.Ed.",
    "M.Ed.",
    "MBBS",
    "MD",
    "MS",
    "BDS",
    "MDS",
    "B.Pharm",
    "M.Pharm",
    "LLB",
    "LLM",
    "CA",
    "CS",
    "ICWA",
    "CFA",
    "PhD",
    "M.Phil",
    "Diploma",
    "ITI",
    "12th Pass",
    "10th Pass",
    "BE",
    "ME",
    "B.Arch",
    "M.Arch",
    "B.Des",
    "M.Des",
    "BFA",
    "MFA",
    "B.J.M.C.",
    "M.J.M.C.",
    "B.P.Ed.",
    "M.P.Ed.",
  ];

  const professionOptions = [
    "Software Engineer",
    "Doctor",
    "Teacher",
    "Lawyer",
    "Accountant",
    "Business Analyst",
    "Project Manager",
    "Data Scientist",
    "Marketing Manager",
    "Sales Executive",
    "HR Manager",
    "Consultant",
    "Architect",
    "Engineer",
    "Nurse",
    "Pharmacist",
    "Dentist",
    "Veterinarian",
    "Journalist",
    "Graphic Designer",
    "Web Developer",
    "Mobile App Developer",
    "DevOps Engineer",
    "System Administrator",
    "Database Administrator",
    "Network Engineer",
    "Security Analyst",
    "Product Manager",
    "UX/UI Designer",
    "Content Writer",
    "Digital Marketing Specialist",
    "Financial Analyst",
    "Investment Banker",
    "Chartered Accountant",
    "Company Secretary",
    "Professor",
    "Research Scientist",
    "Pilot",
    "Flight Attendant",
    "Hotel Manager",
    "Event Planner",
    "Interior Designer",
    "Fashion Designer",
    "Chef",
    "Restaurant Manager",
    "Real Estate Agent",
    "Insurance Agent",
    "Bank Manager",
    "Credit Analyst",
  ];
  const ageOptions = ["above", "under", "between"];
  const agePresetValues = [21, 24, 27, 30, 35, 40];
  const ageRangePresets = [
    [18, 24],
    [24, 28],
    [28, 32],
    [32, 36],
    [36, 40],
    [40, 50],
  ];
  const salaryOptions = ["above", "under", "between"];
  const proximityOptions = ["above", "under", "between"];
  const salaryPresetValues = [
    500000, 1000000, 1500000, 2000000, 2500000, 3000000,
  ];
  const salaryRangePresets = [
    [0, 500000],
    [500000, 1000000],
    [1000000, 1500000],
    [1500000, 2000000],
    [2000000, 2500000],
    [2500000, 3000000],
  ];
  const proximityPresetValues = [10, 25, 50, 100, 250, 500];
  const proximityRangePresets = [
    [0, 10],
    [10, 25],
    [25, 50],
    [50, 100],
    [100, 250],
    [250, 500],
  ];

  // Filtered options based on search
  const filteredSpecialPreferenceOptions = specialPreferenceOptions.filter(
    (option) =>
      option.label
        .toLowerCase()
        .includes(specialPreferenceSearch.toLowerCase()),
  );

  // Filtered detailed options
  const filteredLocationOptions = cityApiOptions;
  const filteredStateOptions = stateApiOptions;
  const countryQuickOptions = [
    "Out of India",
    "Abroad",
    "USA",
    "Canada",
    "United Kingdom",
    "Australia",
    "UAE / Dubai",
    "Germany",
    "Singapore",
    "New Zealand",
    "Ireland",
  ];
  const filteredCountryOptions = countryQuickOptions.filter((option) =>
    option.toLowerCase().includes(countrySearch.toLowerCase()),
  );

  const qualificationSourceOptions =
    Array.isArray(dropdownData?.qualification) &&
      dropdownData.qualification.length > 0
      ? dropdownData.qualification
      : qualificationOptions;
  const professionSourceOptions =
    Array.isArray(dropdownData?.profession) &&
      dropdownData.profession.length > 0
      ? dropdownData.profession
      : Array.isArray(dropdownData?.occupation) &&
        dropdownData.occupation.length > 0
        ? dropdownData.occupation
        : professionOptions;

  const filteredQualificationOptions = qualificationSourceOptions.filter(
    (option: string) =>
      option.toLowerCase().includes(qualificationSearch.toLowerCase()),
  );

  const filteredProfessionOptions = professionSourceOptions.filter(
    (option: string) =>
      option.toLowerCase().includes(professionSearch.toLowerCase()),
  );

  const filteredHeightOptions = heightOptions.filter((option) =>
    option.toLowerCase().includes(heightSearch.toLowerCase()),
  );

  const heightRangeOptions = useMemo(
    () => (filteredHeightOptions.length > 0 ? filteredHeightOptions : heightOptions),
    [filteredHeightOptions],
  );

  const mappedSpecialPreferenceOptions = useMemo(() => {
    const keys =
      SPECIAL_PREFERENCE_DROPDOWN_KEY_MAP[selectedSpecialPreference] || [];
    const values = keys.flatMap((key) =>
      Array.isArray(dropdownData?.[key]) ? dropdownData[key] : [],
    );

    return Array.from(
      new Set(values.map((value) => String(value).trim()).filter(Boolean)),
    );
  }, [dropdownData, selectedSpecialPreference]);
  const filteredMappedSpecialPreferenceOptions =
    mappedSpecialPreferenceOptions.filter((option) =>
      option.toLowerCase().includes(mappedPreferenceSearch.toLowerCase()),
    );

  const resetSpecialPreferenceDraft = useCallback(() => {
    setSelectedSpecialPreferenceValues([]);
    setSelectedAge("");
    setAgeMin("");
    setAgeMax("");
    setSelectedSalary("");
    setSalaryMin("");
    setSalaryMax("");
    setSelectedProximity("");
    setProximityMin("");
    setProximityMax("");
    setCustomSpecialPreferenceValue("");
    setCountrySearch("");
    setLocationSearch("");
    setStateSearch("");
    setQualificationSearch("");
    setProfessionSearch("");
    setHeightSearch("");
    setHeightMin("");
    setHeightMax("");
    setMappedPreferenceSearch("");
  }, []);

  // Fetch states from API with 3 second debounce; load 10 defaults on mount
  useEffect(() => {
    let isActive = true;
    const timer = setTimeout(async () => {
      try {
        const res: any = await SearchStates(stateSearch, 10);
        if (
          isActive &&
          (res?.status === true || res?.status === "success") &&
          Array.isArray(res.data)
        ) {
          const states: string[] = res.data
            .map((s: any) => normalizeCityOption(s))
            .filter(Boolean);
          setStateApiOptions(dedupeOptions(states));
        }
      } catch (err) {
        console.error("Failed to fetch states", err);
      }
    }, 1000);
    return () => {
      isActive = false;
      clearTimeout(timer);
    };
  }, [stateSearch]);

  // Fetch cities from API with 3 second debounce; load 10 defaults on mount
  useEffect(() => {
    let isActive = true;
    const timer = setTimeout(async () => {
      try {
        const res: any = await SearchCities(locationSearch, 10);
        if (
          isActive &&
          (res?.status === true || res?.status === "success") &&
          Array.isArray(res.data)
        ) {
          const cities: string[] = res.data
            .map((c: any) => normalizeCityOption(c))
            .filter(Boolean);
          setCityApiOptions(dedupeOptions(cities));
        }
      } catch (err) {
        console.error("Failed to fetch cities", err);
      }
    }, 1000);
    return () => {
      isActive = false;
      clearTimeout(timer);
    };
  }, [locationSearch]);

  useEffect(() => {
    if (selectedSpecialPreference !== "height" || !heightMin || !heightMax) {
      return;
    }

    const minIndex = heightOptions.indexOf(heightMin);
    const maxIndex = heightOptions.indexOf(heightMax);
    if (minIndex < 0 || maxIndex < 0) {
      return;
    }

    const start = Math.min(minIndex, maxIndex);
    const end = Math.max(minIndex, maxIndex);
    const extractCm = (value: string) => {
      const match = value.match(/(\d+)\s*cm\b/i);
      return match ? Number(match[1]) : null;
    };
    const minCm = extractCm(heightOptions[start]);
    const maxCm = extractCm(heightOptions[end]);
    if (minCm === null || maxCm === null) return;

    // A min/max selection is one continuous centimetre range. Saving every dropdown
    // preset separately omitted real values between presets (155, 156, 161, 163...).
    setSelectedSpecialPreferenceValues([
      `between ${minCm}cm and ${maxCm}cm`,
    ]);
  }, [heightMin, heightMax, selectedSpecialPreference]);

  const toggleSpecialPreferenceDraftValue = useCallback((value: string) => {
    const normalizedValue = value.trim();
    if (!normalizedValue) return;

    setSelectedSpecialPreferenceValues((current) =>
      current.includes(normalizedValue)
        ? current.filter((item) => item !== normalizedValue)
        : [...current, normalizedValue],
    );
  }, []);

  const [showOppositeMatch, setShowOppositeMatch] = useState(false);
  const [dislikeModal, setDislikeModal] = useState({
    isOpen: false,
    candidateId: "",
    candidateName: "",
  });
  const [sendPdfModal, setSendPdfModal] = useState({
    isOpen: false,
    candidateId: "",
    candidateName: "",
    isUserRequested: false,
  });
  const [matchNotificationLogs, setMatchNotificationLogs] = useState<
    Record<string, any>
  >({});
  const [matchDeliveryStatuses, setMatchDeliveryStatuses] = useState<
    Record<string, any>
  >({});
  const [sendingRequestedTemplate, setSendingRequestedTemplate] = useState<
    Record<string, boolean>
  >({});
  const [matchNotificationLogLoading, setMatchNotificationLogLoading] =
    useState<Record<string, boolean>>({});
  const [matchNotificationLogErrors, setMatchNotificationLogErrors] =
    useState<Record<string, string>>({});
  const [astroModalData, setAstroModalData] = useState(DEFAULT_ASTRO_DATA);
  const [oppositeAstroModalData, setOppositeAstroModalData] =
    useState(DEFAULT_ASTRO_DATA);
  const [showPhotoComparison, setShowPhotoComparison] = useState(false);
  const [userPhotoZoom, setUserPhotoZoom] = useState(1);
  const [matchedPhotoZoom, setMatchedPhotoZoom] = useState(1);
  const [userPhotoPos, setUserPhotoPos] = useState({ x: 0, y: 0 });
  const [matchedPhotoPos, setMatchedPhotoPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState({ user: false, matched: false });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleMatchNotificationLogHover = async (candidateId: any) => {
    const currentUserId = userIdParams || userDetails.userId;
    if (!currentUserId || !candidateId) return;

    const cacheKey = `${currentUserId}-${candidateId}`;

    if (
      matchNotificationLogs[cacheKey] ||
      matchNotificationLogLoading[cacheKey]
    ) {
      return;
    }

    setMatchNotificationLogLoading((prev) => ({
      ...prev,
      [cacheKey]: true,
    }));
    setMatchNotificationLogErrors((prev) => ({
      ...prev,
      [cacheKey]: "",
    }));

    try {
      const response: any = await GetMatchNotificationLog(
        currentUserId,
        candidateId,
      );

      const payload =
        response?.data?.data ??
        response?.data ??
        response?.body?.data ??
        response?.result?.data ??
        response;

      if (payload?.user || payload?.candidate) {
        setMatchNotificationLogs((prev) => ({
          ...prev,
          [cacheKey]: payload,
        }));
      } else {
        setMatchNotificationLogs((prev) => ({
          ...prev,
          [cacheKey]: { user: null, candidate: null },
        }));
        setMatchNotificationLogErrors((prev) => ({
          ...prev,
          [cacheKey]:
            response?.message ||
            response?.msg ||
            "No match notification log found",
        }));
      }
    } catch (error: any) {
      console.error("Failed to fetch match notification log", error);
      setMatchNotificationLogs((prev) => ({
        ...prev,
        [cacheKey]: { user: null, candidate: null },
      }));
      setMatchNotificationLogErrors((prev) => ({
        ...prev,
        [cacheKey]:
          error?.message || "Unable to load match notification log",
      }));
    } finally {
      setMatchNotificationLogLoading((prev) => ({
        ...prev,
        [cacheKey]: false,
      }));
    }
  };

  const renderMatchNotificationLogTooltip = (
    candidateId: any,
    candidateName: string,
  ) => {
    const currentUserId = userIdParams || userDetails.userId;
    const cacheKey = `${currentUserId || "unknown"}-${candidateId || "unknown"}`;
    const log = matchNotificationLogs[cacheKey];
    const isLoading = Boolean(matchNotificationLogLoading[cacheKey]);
    const error = matchNotificationLogErrors[cacheKey];

    const formatDateTime = (value: any) => {
      if (!value) return "N/A";
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime())
        ? String(value)
        : parsed.toLocaleString();
    };

    const renderLogCard = (title: string, entry: any, tone: string) => (
      <div className={`rounded-xl border ${tone} bg-slate-950/60 p-3`}>
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-200">
            {title}
          </span>
          <span className="text-[9px] font-mono text-slate-400">
            ID: {entry?.log_id ?? "N/A"}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2 text-[9px]">
          <div>
            <div className="text-slate-500 font-black uppercase tracking-widest">
              Status
            </div>
            <div className="mt-0.5 font-mono font-bold text-slate-100">
              {entry?.status ?? "N/A"}
            </div>
          </div>
          <div>
            <div className="text-slate-500 font-black uppercase tracking-widest">
              Retry
            </div>
            <div className="mt-0.5 font-mono font-bold text-slate-300">
              {formatDateTime(entry?.retry_date)}
            </div>
          </div>
          <div className="col-span-2">
            <div className="text-slate-500 font-black uppercase tracking-widest">
              Updated
            </div>
            <div className="mt-0.5 font-mono font-bold text-slate-300">
              {formatDateTime(entry?.updated_date)}
            </div>
          </div>
        </div>
      </div>
    );

    return (
      <div className="w-[340px] max-w-[340px]">
        <div className="mb-3 flex items-center gap-2 border-b border-slate-800 pb-2">
          <FaFilePdf size={13} className="text-rose-400 shrink-0" />
          <div className="min-w-0">
            <div className="text-[10px] font-black uppercase tracking-[0.2em] text-white">
              Match Notification Log
            </div>
            <div className="text-[9px] font-mono text-slate-400 truncate">
              {candidateName || "Candidate"} | User {currentUserId || "N/A"} | Candidate {candidateId || "N/A"}
            </div>
          </div>
        </div>

        {isLoading && (
          <div className="rounded-xl border border-slate-800 bg-slate-950/70 px-3 py-4 text-[11px] text-slate-300">
            Loading match notification log...
          </div>
        )}

        {!isLoading && error && (
          <div className="rounded-xl border border-amber-500/20 bg-amber-500/10 px-3 py-3 text-[11px] text-amber-200">
            {error}
          </div>
        )}

        {!isLoading && !error && log && (log.user || log.candidate) && (
          <div className="space-y-2">
            {renderLogCard("User", log.user, "border-sky-500/20")}
            {renderLogCard("Candidate", log.candidate, "border-emerald-500/20")}
          </div>
        )}

        {!isLoading && !error && (!log || (!log.user && !log.candidate)) && (
          <div className="rounded-xl border border-slate-800 bg-slate-950/70 px-3 py-4 text-[11px] text-slate-400">
            No match notification log available yet.
          </div>
        )}
      </div>
    );
  };

  const [tableData, setTableData] = useState<Record<string, any[]>>({
    "Matched Profiles": [],
    "Rejected Profiles": [],
    "Queued Profiles": [],
    "Potential Matches": [],
    "Skipped Matchess": [],
    "Special Pref Skipped": [],
    "Special Pref Requeued": [],
    "Like To": [],
    "Liked By": [],
  });
  const [potentialMatchesTotal, setPotentialMatchesTotal] = useState(0);
  const [matchedSubTab, setMatchedSubTab] = useState<
    "AI Matches" | "Outgoing" | "Incoming"
  >("AI Matches");
  const [page, setPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

  const [timer, setTimer] = useState<number>(0);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  useEffect(() => {
    const fetchSupportAdmins = async () => {
      try {
        const response = await GetCustomerSupportAdmins();
        if (Array.isArray(response)) {
          setSupportAdmins(response);
        } else if (response && response.data) {
          setSupportAdmins(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch support admins", error);
      }
    };
    fetchSupportAdmins();
  }, []);

  const fetchPreferenceCategories = useCallback(async () => {
    try {
      const resp: any = await GetPreferenceCategories();
      if (resp.status === true || resp.success === true) {
        setPreferenceCategories(resp.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch preference categories", err);
    }
  }, []);

  useEffect(() => {
    fetchPreferenceCategories();
  }, [fetchPreferenceCategories]);

  useEffect(() => {
    const data = localStorage.getItem("data");
    if (data) {
      try {
        const parsed = JSON.parse(data);
        setCurrentUserRole(parsed.Role || parsed.role || null);
        setCurrentUsername(parsed.username || parsed.Username || null);
      } catch (error) {
        console.error("Failed to parse user role", error);
      }
    }
  }, []);

  // const formatTime = (seconds: number) => {
  //   const mins = Math.floor(seconds / 60);
  //   const secs = seconds % 60;
  //   return `${mins}:${secs.toString().padStart(2, '0')}`;
  // };

  // const handleGenerateTempPassword = async () => {
  //   try {
  //     LoaderService.show();
  //     const response = await PostGenerateTempPassword(userDetails.userId, 30);
  //     if (response.status === "success") {
  //       setTempPassword(response.data.temporary_password);
  //       setTimer(30 * 60); // 30 minutes in seconds
  //       toast.success(response.message || "Temporary password generated!");
  //     }
  //   } catch (error) {
  //     toast.error("Failed to generate temporary password");
  //   } finally {
  //     LoaderService.hide();
  //   }
  // };

  const [models, setModels] = useState<Models>({
    popUpCancel: false,
    popUpSubmit: false,
    popUpCandidate: false,
    reasonAndGapModal: false,
    manaulMatchProcessMainModal: false,
    row: {},
  });

  const [queueFilters, setQueueFilters] = useState({
    min_age: "",
    max_age: "",
    city: [] as string[],
    state: [] as string[],
    profession: [] as string[],
    family_type: "",
    family_background: "",
    manglik: [] as string[],
    proximity_range: "all" as [number, number] | string,
  });
  const [skippedFilters, setSkippedFilters] = useState({
    min_age: "",
    max_age: "",
    city: [] as string[],
    state: [] as string[],
    family_type: "",
    family_background: "",
    profession: [] as string[],
    occupation: [] as string[],
    manglik: [] as string[],
    score_status: "all" as "all" | "failed" | "passed",
  });

  const [matchedFilters, setMatchedFilters] = useState({
    min_age: "",
    max_age: "",
    city: [] as string[],
    state: [] as string[],
    profession: [] as string[],
    manglik: [] as string[],
    publish: "",
    score_range: "all" as string,
    proximity_range: "all" as [number, number] | string,
  });
  const [potentialFilters, setPotentialFilters] = useState({
    name: "",
    matrimonial_id: "",
    city: [] as string[],
    city_query: "",
    profession: [] as string[],
    profession_query: "",
    family_background: "",
    age_order: "default" as "default" | "asc" | "desc",
  });
  const [matchesTotals, setMatchesTotals] = useState({
    total_matches: 0,
    total_queue: 0,
    total_skipped: 0,
    special_pref_skipped: 0,
    special_pref_reactivated: 0,
    special_pref_inserted_to_queue: 0,
    special_pref_requeued_to_existing_queue: 0,
    published_matches: 0,
    unpublished_matches: 0,
    active_matches: 0,
    compatibility_breakdown: {
      EXCELLENT: 0,
      VERY_GOOD: 0,
      GOOD: 0,
      FAIR: 0,
      POOR: 0,
    },
  });
  const [matchesDataLoaded, setMatchesDataLoaded] = useState(false);
  const commonProfessionOptions = useMemo(
    () => [
      "Software Engineer",
      "Product Manager",
      "Project Manager",
      "Data Analyst",
      "Data Scientist",
      "Doctor",
      "Dentist",
      "Pharmacist",
      "Nurse",
      "Chartered Accountant",
      "Financial Analyst",
      "Bank Manager",
      "Teacher",
      "Professor",
      "Lawyer",
      "Civil Engineer",
      "Mechanical Engineer",
      "Architect",
      "Business Owner",
      "Entrepreneur",
      "Government Officer",
      "Sales Manager",
      "Marketing Manager",
      "HR Manager",
      "Consultant",
      "Freelancer",
    ],
    [],
  );

  const [matchApiFilters, setMatchApiFilters] = useState({
    filter_type: "all",
    match_status: "all",
    compatibility_level: "all",
    filter_dislikes: false,
  });
  const [rowsPerPage, setRowsPerPage] = useState(12);

  const refreshData = useCallback(async () => {
    try {
      const userDetails = JSON.parse(
        localStorage.getItem("userDetails") || "{}",
      );
      let { userId } = userDetails;
      if (IsNull(userIdParams) === false) {
        userId = userIdParams;
      }
      LoaderService.show();
      const response: any = await GetProfileDetails(userId);

      // Check for "Profile not found" error and redirect to user list
      /*
      // Check for "Profile not found" error and redirect to user list
      if (response?.detail === "Profile not found" || response?.message === "Profile not found") {
        toast.error("Profile not found");
        LoaderService.hide();
        navigate("/users");
        return;
      }
      */

      // Removed redundant GetPotentialMatches call as it's now handled by fetchMatchesData with the new API
      // let skippedMatches_local: any[] = [];

      if (response.status === "success" || response.status === true) {
        const {
          profile: apiProfile,
          matrimonial,
          info = {},
          preference = {},
          preferences = {},
          photos = [],
          bioData = "",
          bio_data = "",
          rejected_profiles = [],
          instruction = "",
          instructions = [],
          dislike_reasons = [],
        } = response.data || {};

        // Use matrimonial as profile if profile key is missing (typical for v2 API)
        const profile = apiProfile || matrimonial || {};

        console.log("Profile Data: ", response.data);

        const rejectedProfiles_local = rejected_profiles.map((p: any) => ({
          userId: p.userId || p.UserId,
          name: p.name || p.Name,
          gender: p.gender || p.Gender,
          age: (p.age || p.Age || "").toString(), // ensure age is a number
          city: p.city || p.City,
          state: p.state || p.State,
          publish:
            p.published !== undefined
              ? p.published
                ? "yes"
                : "no"
              : p.publish || p.Publish,
          decision: p.decision || p.Decision,
          score: (p.match_score || p.score || p.Score || "").toString(),
          llmScore: (p.llm_score || p.LlmScore || "0").toString(),
          gptScore: (p.gunn_score || p.gptScore || p.GptScore || "").toString(),
          proximity: (p.proximity || p.Proximity || "0").toString(),
          date: p.published_at || p.created_at || p.date || p.Date,
          icon:
            p.photo_url ||
            p.profile_pic?.compressed ||
            p.icon ||
            p.photo ||
            p.Photo,
          reason: p.reason || p.summary || p.Reason || {},
        }));

        if (IsNull(profile) === false) {
          const mappedProfile = {
            ...profile,
            userId:
              profile.UserId ||
              profile.userId ||
              profile.user_id ||
              info.userId ||
              userId,
            name: profile.Name || profile.name || info.username,
            gender: profile.Gender || profile.gender || info.gender,
            Gender: profile.Gender || profile.gender || info.gender,
            age: profile.Age || profile.age,
            phone: profile.Phone || profile.phone || info.phone,
            matrimonial_phone:
              profile.MatrimonialPhone ||
              profile.matrimonial_phone ||
              info.matrimonial_phone ||
              profile.matrimonialPhone,
            user_phone:
              profile.UserPhone ||
              profile.user_phone ||
              info.user_phone ||
              profile.userPhone,
            email: profile.Email || profile.email || info.email,
            status: profile.Status || profile.status || profile.is_active,
            occupation: profile.Occupation || profile.occupation,
            Occupation: profile.Occupation || profile.occupation,
            working_with:
              profile.WorkingWith ||
              profile.working_with ||
              profile.working_as ||
              profile.working_with,
            manglik:
              profile.Manglik || profile.manglik || profile.manglik_status,
            qualification:
              profile.Qualification || profile.qualification || profile.degree,
            min_income:
              profile.min_income ||
              profile.annual_income_min ||
              profile["Annual Income Min"] ||
              profile.AnnualIncomeMin ||
              profile.min_salary ||
              profile.income ||
              profile.annual_income ||
              profile.AnnualIncome ||
              "N/A",
            max_income:
              profile.max_income ||
              profile.annual_income_max ||
              profile["Annual Income Max"] ||
              profile.AnnualIncomeMax ||
              profile.max_salary ||
              "N/A",
            totalCredits: profile.TotalCredits || profile.totalCredits || 0,
            creditsUsed: profile.CreditsUsed || profile.creditsUsed || 0,
            isPremium: profile.isPremium || profile.is_premium || false,
            version: profile.Version || profile.version,
            platform: profile.Platform || profile.platform,
            city: profile.City || profile.city,
            state: profile.State || profile.state,
            photo:
              profile.photo ||
              profile.Photo ||
              photos?.find((p: any) => p.profile || p.is_profile)?.url ||
              "",
            location:
              profile.Address ||
              profile.location ||
              profile.Location ||
              `${profile.City || ""}, ${profile.State || ""}`
                .trim()
                .replace(/^, |, $/g, "") ||
              "N/A",
            adminNote:
              profile.AdminNote ||
              profile.admin_note ||
              profile.note ||
              instruction ||
              "",
            adminInstructions: Array.isArray(instructions) ? instructions : [],
            dislikeReasons: Array.isArray(dislike_reasons)
              ? dislike_reasons
              : [],
            has_child: profile.has_child ?? profile.HasChild,
            children: profile.children || profile.Children || [],
            verified:
              profile.verified ||
              profile.Verified ||
              info?.verified ||
              info?.is_verified ||
              userDetails.verified ||
              false,
            createdAt:
              profile.CreatedAt ||
              profile.created_at ||
              profile.createdAt ||
              info?.created_at,
            progress: (() => {
              const p =
                profile.progress ??
                profile.Progress ??
                info?.progress ??
                info?.Progress ??
                userDetails.progress ??
                0;
              return typeof p === "string" ? parseFloat(p.replace("%", "")) : p;
            })(),
          };

          setUserDetails(mappedProfile);

          // Also update the full profile view state
          setUserProfileData({
            profile: profile,
            preference:
              Object.keys(preferences).length > 0 ? preferences : preference,
            photos: photos,
            bioData: bio_data || bioData,
            sammelanRegistrations: response.data.sammelan_registrations || [],
          });
        }

        setTableData((prev: any) => ({
          ...prev,
          "Rejected Profiles": rejectedProfiles_local,
        }));
      }
    } catch (error: any) {
    } finally {
      LoaderService.hide();
    }
  }, [userIdParams]);

  const handleProfilePhotoUpdate = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/webp",
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error("Please select a JPG, PNG, GIF, or WEBP image.");
      event.target.value = "";
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image size must be less than 10MB.");
      event.target.value = "";
      return;
    }

    const currentUserId = userIdParams || userDetails.userId;
    if (!currentUserId) {
      toast.error("User ID is missing.");
      event.target.value = "";
      return;
    }

    setIsPhotoUploading(true);
    try {
      const response = await PostUpdateProfilePhoto(currentUserId, file);

      if (response?.status === "success" || response?.success === true) {
        await refreshData();
      }
    } catch (error) {
      console.error("Error updating profile photo", error);
      toast.error("Failed to update profile photo.");
    } finally {
      setIsPhotoUploading(false);
      event.target.value = "";
    }
  };

  const handlePhoneCall = async (candidateId: string) => {
    try {
      setPhoneCallModal((prev) => ({
        ...prev,
        isOpen: true,
        loading: true,
        candidateId,
      }));
      const response = await GetPhoneCallEvents(
        userIdParams || userDetails.userId,
        candidateId,
      );
      if (
        response.status === "success" &&
        response.data &&
        response.data.length > 0
      ) {
        const callData = response.data[0];
        setPhoneCallModal((prev) => ({
          ...prev,
          loading: false,
          data: callData,
          feedback: callData.feedback || "",
          adminName: callData.feedback_given_by || "",
        }));
      } else {
        setPhoneCallModal((prev) => ({ ...prev, loading: false, data: null }));
        toast.error("No call events found or failed to fetch.");
      }
    } catch (error) {
      console.error("Error fetching phone call events", error);
      setPhoneCallModal((prev) => ({ ...prev, loading: false }));
      toast.error("Failed to fetch phone call events.");
    }
  };

  const handleAddFeedback = async () => {
    if (!phoneCallModal.adminName) {
      toast.error("Please select an admin name.");
      return;
    }
    if (!phoneCallModal.feedback) {
      toast.error("Please enter feedback.");
      return;
    }

    try {
      LoaderService.show();
      const payload = {
        user_id: userIdParams || userDetails.userId,
        candidate_id: phoneCallModal.candidateId,
        feedback: phoneCallModal.feedback,
        feedback_given_by: phoneCallModal.adminName,
      };
      const response = await PostPhoneCallFeedback(payload);
      if (response.status === "success" || response.success === true) {
        if (response.message === "No call record found to update feedback") {
          toast.error(response.message);
        } else {
          toast.success(
            phoneCallModal.data?.feedback
              ? "Feedback updated successfully."
              : "Feedback added successfully.",
          );
          setPhoneCallModal((prev) => ({ ...prev, isOpen: false }));
        }
      } else {
        toast.error(
          response.message ||
          (phoneCallModal.data?.feedback
            ? "Failed to update feedback."
            : "Failed to add feedback."),
        );
      }
    } catch (error) {
      console.error("Error processing feedback", error);
      toast.error(
        phoneCallModal.data?.feedback
          ? "An error occurred while updating feedback."
          : "An error occurred while adding feedback.",
      );
    } finally {
      LoaderService.hide();
    }
  };

  const fetchPublishedMatchCount = useCallback(async () => {
    const currentUserId = userIdParams || userDetails.userId;
    if (!currentUserId) return;

    try {
      const response = await getMatchPublishSummary(currentUserId);
      const payload = response?.data ?? response;
      const totals = payload?.totals ?? {};
      const published = Number(
        totals.total_published_matches ?? totals.published_matches ?? 0,
      );
      const unpublished = Number(
        totals.total_unpublished_matches ?? totals.unpublished_matches ?? 0,
      );

      setMatchesTotals((prev) => ({
        ...prev,
        published_matches: published,
        unpublished_matches: unpublished,
      }));
    } catch (error) {
      console.error("Error fetching published match count:", error);
    }
  }, [userIdParams, userDetails.userId]);

  const fetchMatchesData = useCallback(
    async (force = false) => {
      // Skip if data is already loaded and not forced
      if (matchesDataLoaded && !force) {
        return;
      }
      try {
        LoaderService.show();
        const currentUserId = userIdParams || userDetails.userId;
        const [
          response,
          specialPreferenceActivityResponse,
          matchmakingFeedRows,
        ]: [any, any, any[]] =
          await Promise.all([
            GetUserMatches(currentUserId, 1000, 0, matchApiFilters),
            GetUserSpecialPreferenceActivity(currentUserId, 1000, 0).catch(
              (error) => {
                console.error(
                  "Error fetching special preference activity:",
                  error,
                );
                return null;
              },
            ),
            fetchMatchmakingFeedRowsForUser(currentUserId).catch((error) => {
              console.error("Error fetching matchmaking result feed:", error);
              return [];
            }),
          ]);

        if (response.success === true || response.status === true) {
          const matchesData = response.data || {};
          const matchesList = matchesData.matches || [];
          const queueList = matchesData.queue || [];
          const specialPreferenceActivity =
            specialPreferenceActivityResponse?.data || {};
          const specialPreferenceTotals =
            specialPreferenceActivity.totals || {};
          const publishAtByCandidateId = new Map<string, string>();
          // Populate from matchmaking feed rows (uid-based fetch returns published_at for all matches)
          matchmakingFeedRows.forEach((row: any) => {
            const candidateId = row?.candidate?.user_id || row?.candidate_id;
            const publishAt = row?.published_at || row?.publishedAt;
            if (candidateId && publishAt) {
              publishAtByCandidateId.set(candidateId, publishAt);
            }
          });
          const publishedFromList = matchesList.filter(
            (match: any) => match.published === true || match.publish === true,
          ).length;
          const unpublishedFromList = matchesList.filter(
            (match: any) => match.published === false || match.publish === false,
          ).length;
          const stats = {
            total_matches:
              matchesData.total_matches ||
              matchesData.totals?.total_matches ||
              matchesData.statistics?.total_matches ||
              0,
            total_queue:
              matchesData.total_queue ||
              matchesData.totals?.total_queue ||
              matchesData.statistics?.total_queue ||
              0,
            total_skipped:
              matchesData.total_skipped ||
              matchesData.totals?.total_skipped ||
              matchesData.statistics?.total_skipped ||
              0,
            special_pref_skipped:
              specialPreferenceTotals.currently_skipped_count || 0,
            special_pref_reactivated:
              specialPreferenceTotals.reactivated_count || 0,
            special_pref_inserted_to_queue:
              specialPreferenceTotals.inserted_to_queue_count || 0,
            special_pref_requeued_to_existing_queue:
              specialPreferenceTotals.requeued_to_existing_queue_count || 0,
            published_matches:
              matchesData.published_matches ||
              matchesData.totals?.published_matches ||
              matchesData.statistics?.published_matches ||
              publishedFromList,
            unpublished_matches:
              matchesData.unpublished_matches ||
              matchesData.totals?.unpublished_matches ||
              matchesData.statistics?.unpublished_matches ||
              unpublishedFromList,
            active_matches:
              matchesData.active_matches ||
              matchesData.totals?.active_matches ||
              matchesData.statistics?.active_matches ||
              0,
            compatibility_breakdown:
              matchesData.compatibility_breakdown ||
              matchesData.statistics?.compatibility_breakdown ||
              {},
          };

          setMatchesTotals(stats);
          setMatchesDataLoaded(true);

          // WhatsApp delivery status per candidate (one batched call) for the badge.
          GetMatchNotificationLogBatch(currentUserId)
            .then((res: any) => setMatchDeliveryStatuses(res?.data || {}))
            .catch(() => { });

          const matches = matchesList.map((p: any) => {
            const details = p.candidate_details || {};
            const reasonObj = p.reason || p.summary || {};
            const userLikeStatus = (p.like_status ?? p.user_like_status ?? "")
              .toString()
              .toLowerCase();
            const candidateLikeStatus = (p.candidate_like_status ?? "")
              .toString()
              .toLowerCase();
            // Determine city/state from location string if not provided separately
            let city = p.city || details.city;
            let state = p.state || details.state;
            if (!city && p.location) {
              const parts = p.location.split(",");
              city = parts[0]?.trim();
              state = parts[1]?.trim();
            }

            return {
              icon: p.profile_pic?.compressed || p.photo_url || p.icon,
              likeReason: p.like_reason || p.user_like_reason || "",
              likedNotCalled48h: p.liked_not_called_48h === true,
              like:
                userLikeStatus === "liked" || p.liked === true
                  ? true
                  : userLikeStatus === "disliked" || p.disliked === true
                    ? false
                    : userLikeStatus === "neutral"
                      ? null
                      : p.like !== undefined
                        ? p.like
                        : null,
              candidateLikeReason: p.candidate_like_reason || "",
              candidateLike:
                candidateLikeStatus === "liked" || p.candidate_liked === true
                  ? true
                  : candidateLikeStatus === "disliked" ||
                    p.candidate_disliked === true
                    ? false
                    : candidateLikeStatus === "neutral"
                      ? null
                      : p.candidate_like !== undefined
                        ? p.candidate_like
                        : null,
              call: p.call || { called: false, last_call_at: null },
              userId: p.candidate_id || p.userId,
              name: p.candidate_name || p.name,
              age: (p.age || details.age || "").toString(),
              location:
                p.location ||
                `${city || ""}${city && state ? ", " : ""}${state || ""}` ||
                "N/A",
              city: city || "N/A",
              state: state || "N/A",
              publish:
                p.published === false || p.publish === false
                  ? "no"
                  : p.status === "Active" ||
                    p.published === true ||
                    p.publish === true
                    ? "yes"
                    : "no",
              score: (p.match_score || p.score || "0").toString(),
              gptScore: (
                p.gun_score ||
                p.gunn_score ||
                p.astrology?.gunn_score ||
                "0"
              ).toString(),
              llmScore: (p.llm_score || "0").toString(),
              match_at:
                p.matched_at ||
                p.updated_at ||
                p.created_at ||
                "",
              publish_at:
                publishAtByCandidateId.get(p.candidate_id || p.userId) ||
                p.published_at ||
                p.publishedAt ||
                "",
              created_by: p.created_by || p.createdBy || "",
              date: p.matched_at || p.created_at,
              profession:
                p.occupation || p.profession || details.occupation || "N/A",
              occupation_location:
                p.occupation_location ||
                p.occupationLocation ||
                details.occupation_location ||
                details.occupationLocation ||
                "N/A",
              qualification:
                p.degree || p.qualification || details.qualification || "N/A",
              managed_by: p.managed_by || "System",
              height:
                p.height ||
                (details.height_cm ? `${details.height_cm}cm` : "N/A"),
              income: p.income || details.annual_income || "N/A",
              marital_status:
                p.marital_status || details.marital_status || "N/A",
              dietary: p.dietary || "N/A",
              manglik: p.manglik || details.manglik || "N/A",
              intercaste: p.Intercaste ?? p.intercaste, // Added Intercaste
              maternal_gotra: p.MaternalGotra ?? p.maternal_gotra, // Added MaternalGotra
              about_me:
                p.about_me ||
                reasonObj.overview ||
                reasonObj.reason ||
                "No bio provided",
              reason: reasonObj,
              forceMatch:
                p.forceMatch ||
                p.force_match ||
                (isRequested(p.requested) ? "Requested" : "Force Match"),
              requested: p.requested || "not_requested",
              HasError: p.HasError || p.has_error || 0,
              proximity: (p.proximity || p.proximity_range || "0").toString(),
              compatibility: p.compatibility_level || "GOOD",
              gender: p.gender || details.gender || "N/A",
              has_child: details.has_child ?? p.has_child ?? p.HasChild,
              children: details.children || p.children || p.Children || [],
              source_service: p.source_service || "",
            };
          });

          const queue = queueList.map((p: any) => {
            const details = p.candidate_details || {};
            const reasonObj = p.reason || p.summary || {};

            let city = p.city || details.city;
            let state = p.state || details.state;
            if (!city && p.location) {
              const parts = p.location.split(",");
              city = parts[0]?.trim();
              state = parts[1]?.trim();
            }

            return {
              icon: p.profile_pic?.compressed || p.photo_url || p.icon,
              userId: p.candidate_id || p.userId,
              name: p.candidate_name || p.name,
              age: (p.age || details.age || "").toString(),
              location:
                p.location ||
                `${city || ""}${city && state ? ", " : ""}${state || ""}` ||
                "N/A",
              city: city || "",
              state: state || "",
              date: p.created_at,
              forceMatch:
                p.forceMatch ||
                p.force_to_match ||
                p.force_match ||
                (p.requested === "Yes" ? "Requested" : "Force Match"),
              requested: p.requested || "No",
              HasError: p.HasError || p.has_error || 0,
              reason: reasonObj,
              profession:
                p.profession || p.occupation || details.occupation || "",
              occupation_location:
                p.occupation_location ||
                p.occupationLocation ||
                details.occupation_location ||
                details.occupationLocation ||
                "",
              family_type: p.family_type || details.family_type || "",
              family_background:
                p.family_background || details.family_background || "",
              proximity: (p.proximity || "0").toString(),
              priority: p.priority || 0,
              gptScore: (p.gunn_score || p.gun_score || "0").toString(),
              llmScore: (p.llm_score || "0").toString(),
              manglik: p.manglik || details.manglik || "",
              intercaste: p.Intercaste ?? p.intercaste, // Added Intercaste
              maternal_gotra: p.MaternalGotra ?? p.maternal_gotra, // Added MaternalGotra
              gender: p.gender || details.gender || "N/A",
              source_service: p.source_service || "",
            };
          });

          // Map skipped matches
          const skippedList = matchesData.skipped || [];
          const skipped = skippedList.map((p: any) => {
            let city = p.city;
            let state = p.state;
            if (!city && p.location) {
              const parts = p.location.split(",");
              city = parts[0]?.trim();
              state = parts[1]?.trim();
            }

            return {
              icon: p.profile_pic?.compressed || p.photo_url || p.icon,
              userId: p.candidate_id || p.userId,
              name: p.name || "Unknown",
              age: (p.age || "").toString(),
              gender: p.gender || "N/A",
              location:
                p.location ||
                `${city || ""}${city && state ? ", " : ""}${state || ""}` ||
                "N/A",
              city: city || "Unknown",
              state: state || "Unknown",
              occupation: p.occupation || "N/A",
              profession: p.profession || p.occupation || "N/A",
              occupation_location:
                p.occupation_location || p.occupationLocation || "N/A",
              reason: p.reason || {},
              skip_category: p.skip_category || "N/A",
              auto_skip:
                p.auto_skip !== undefined
                  ? p.auto_skip
                    ? "Yes"
                    : "No"
                  : "N/A",
              skipped_at: p.skipped_at,
              family_type: p.family_type || "Unknown",
              family_background: p.family_background || "Unknown",
              intercaste: p.Intercaste ?? p.intercaste, // Added Intercaste
              maternal_gotra: p.MaternalGotra ?? p.maternal_gotra, // Added MaternalGotra
              source: "SkippedMatches",
              forceMatch: p.forceMatch || p.force_match || "Force Match",
              requested: p.requested || "No",
              HasError: p.HasError || p.has_error || 0,
              source_service: p.source_service || "",
            };
          });

          const specialPreferenceSkippedList =
            specialPreferenceActivity.currently_skipped || [];
          const specialPreferenceSkipped = specialPreferenceSkippedList.map(
            (p: any) => ({
              icon: p.profile_pic?.compressed || p.photo_url || p.icon,
              userId: p.candidate_id || p.userId,
              name: p.name || "Unknown",
              age: (p.age || "").toString(),
              gender: p.gender || "N/A",
              location:
                p.location ||
                `${p.city || ""}${p.city && p.state ? ", " : ""}${p.state || ""}` ||
                "N/A",
              city: p.city || "Unknown",
              state: p.state || "Unknown",
              occupation: p.occupation || "N/A",
              profession: p.profession || p.occupation || "N/A",
              reason: p.reason || {},
              skip_category: p.skip_category || "PREFERENCE_MISMATCH",
              auto_skip:
                p.auto_skip !== undefined
                  ? p.auto_skip
                    ? "Yes"
                    : "No"
                  : "N/A",
              skipped_at: p.skipped_at,
              family_type: p.family_type || "Unknown",
              family_background: p.family_background || "Unknown",
              manglik: p.manglik || "",
              source: "SpecialPreference",
              specialPreferenceStatus: p.special_preference_status,
              queue_source_service: p.queue_source_service || "",
              forceMatch: p.forceMatch || p.force_match || "Force Match",
              requested: p.requested || "No",
              HasError: p.HasError || p.has_error || 0,
            }),
          );

          const specialPreferenceReactivatedList =
            specialPreferenceActivity.reactivated || [];
          const specialPreferenceReactivated =
            specialPreferenceReactivatedList.map((p: any) => ({
              icon: p.profile_pic?.compressed || p.photo_url || p.icon,
              userId: p.candidate_id || p.userId,
              name: p.name || "Unknown",
              age: (p.age || "").toString(),
              gender: p.gender || "N/A",
              location:
                p.location ||
                `${p.city || ""}${p.city && p.state ? ", " : ""}${p.state || ""}` ||
                "N/A",
              city: p.city || "Unknown",
              state: p.state || "Unknown",
              occupation: p.occupation || "N/A",
              profession: p.profession || p.occupation || "N/A",
              reason: p.reason || {},
              skip_category: p.skip_category || "PREFERENCE_MISMATCH",
              auto_skip:
                p.auto_skip !== undefined
                  ? p.auto_skip
                    ? "Yes"
                    : "No"
                  : "N/A",
              skipped_at: p.skipped_at,
              reactivated_at: p.reactivated_at,
              reactivation_mode: p.reactivation_mode || "requeued",
              queue_source_service: p.queue_source_service || "",
              family_type: p.family_type || "Unknown",
              family_background: p.family_background || "Unknown",
              manglik: p.manglik || "",
              source: "SpecialPreferenceReactivated",
              specialPreferenceStatus: p.special_preference_status,
              HasError: p.HasError || p.has_error || 0,
            }));

          setTableData((prev: any) => ({
            ...prev,
            "Matched Profiles": matches,
            "Queued Profiles": queue,
            "Skipped Matchess": skipped,
            "Special Pref Skipped": specialPreferenceSkipped,
            "Special Pref Requeued": specialPreferenceReactivated,
          }));

          // Fetch strict gun scores from astrology API for matched profiles
          if (matches.length > 0 && currentUserId) {
            const fetchStrictScores = async () => {
              try {
                const updatedMatches = await Promise.all(
                  matches.map(async (match: any) => {
                    try {
                      const astroRes: any =
                        await PostMatchMakingAstrologyDetails({
                          user_id: currentUserId,
                          candidate_id: match.userId,
                        });
                      const astroCheck = Array.isArray(
                        astroRes?.astrology_check,
                      )
                        ? astroRes.astrology_check[1] || {}
                        : {};
                      const gunMilan = astroCheck?.gun_milan || {};
                      const strictScore = gunMilan?.total_gun_score_strict;
                      return {
                        ...match,
                        gptScore:
                          strictScore !== undefined && strictScore !== null
                            ? strictScore.toString()
                            : match.gptScore,
                      };
                    } catch {
                      return match;
                    }
                  }),
                );
                setTableData((prev: any) => ({
                  ...prev,
                  "Matched Profiles": updatedMatches,
                }));
              } catch (err) {
                console.error("Error fetching strict gun scores:", err);
              }
            };
            fetchStrictScores();
          }
        }
      } catch (error) {
        console.error("Error fetching matches:", error);
      } finally {
        LoaderService.hide();
      }
    },
    [userIdParams, userDetails.userId, matchApiFilters, matchesDataLoaded],
  );

  const fetchLikedMatchesData = useCallback(async () => {
    try {
      LoaderService.show();
      const currentUserId = userIdParams || userDetails.userId;

      const [likedMatchesRes, likedByRes]: [any, any] = await Promise.all([
        GetUserLikedMatches(currentUserId, 100, 0),
        GetProfileLikedBy(currentUserId, 100, 0),
      ]);

      let likedToMapped: any[] = [];
      if (likedMatchesRes.success === true || likedMatchesRes.status === true) {
        const matchesList = likedMatchesRes.data?.matches || [];
        likedToMapped = matchesList.map((p: any) => ({
          icon: p.image || p.profile_pic?.compressed || p.photo_url || p.icon,
          userId: p.user_id || p.candidate_id || p.userId,
          matrimonial_id: p.matrimonial_id,
          name: p.name,
          city: p.city || "N/A",
          profession: p.profession || p.occupation || "N/A",
          mobile: p.mobile || "N/A",
          date: p.likes?.[0]?.liked_at || p.matched_at || p.created_at,
          likedBy: p.likes?.[0]?.liked_by || p.liked_by || null,
          likedTo: p.likes?.[0]?.liked_to || p.liked_to || null,
          like_status:
            p.likes?.[0]?.like_status ||
            p.like_status ||
            (p.liked === true
              ? "liked"
              : p.disliked === true
                ? "disliked"
                : "neutral"),
          like:
            p.likes?.[0]?.like_status === "liked" || p.liked === true
              ? true
              : p.likes?.[0]?.like_status === "disliked" || p.disliked === true
                ? false
                : null,
          likeReason: p.likes?.[0]?.like_reason || p.like_reason || "",
          type: "liked_to",
          // Mock N/A for consistent data structure if needed
          age: "",
          location: p.city || "N/A",
          score: "0",
          gptScore: "0",
        }));
      }

      let likedByMapped: any[] = [];
      if (likedByRes.status === true) {
        const likedByList = likedByRes.data?.liked_by || [];
        likedByMapped = likedByList.map((p: any) => ({
          userId: p.user_id,
          matrimonial_id: p.matrimonial_id,
          name: p.name,
          city: p.city || "N/A",
          profession: p.profession || "N/A",
          mobile: p.mobile || "N/A",
          date: p.liked_at,
          type: "liked_by",
          // Mocking empty fields for shared columns
          icon: p.image || null,
          age: "",
          location: p.city || "N/A",
          score: "0",
          gptScore: "0",
        }));
      }

      setTableData((prev: any) => ({
        ...prev,
        "Like To": likedToMapped,
        "Liked By": likedByMapped,
      }));
    } catch (error) {
      console.error("Error fetching liked matches data:", error);
    } finally {
      LoaderService.hide();
    }
  }, [userIdParams, userDetails.userId]);

  const fetchSearchSkippedMatches = useCallback(async () => {
    const currentUserId = userIdParams || userDetails.userId;
    if (!search || activeTab !== "Skipped Matchess" || !currentUserId) return;
    try {
      LoaderService.show();
      const response: any = await SearchSkippedMatches(currentUserId, search);
      if (response && Array.isArray(response.data)) {
        const mapped = response.data.map((p: any) => ({
          icon: p.profile_pic?.compressed || p.photo_url || p.icon,
          userId: p.candidate_id || p.userId,
          name: p.user_name || p.name || "Unknown",
          age: (p.age || "").toString(),
          gender: p.gender || "N/A",
          location: p.location || "N/A",
          city: p.city || "Unknown",
          state: p.state || "Unknown",
          occupation: p.occupation || "N/A",
          reason: p.reason
            ? typeof p.reason === "string"
              ? { overview: p.reason }
              : p.reason
            : {},
          skip_category: p.skip_category || "N/A",
          auto_skip:
            p.auto_skip !== undefined ? (p.auto_skip ? "Yes" : "No") : "N/A",
          skipped_at: p.created_at || p.skipped_at,
          family_type: p.family_type || "Unknown",
          family_background: p.family_background || "Unknown",
          source: "SkippedMatches",
          forceMatch: p.forceMatch || p.force_match || "Force Match",
          requested: p.requested || "No",
          HasError: p.HasError || p.has_error || 0,
        }));
        setTableData((prev: any) => ({
          ...prev,
          "Skipped Matchess": mapped,
        }));
      }
    } catch (error) {
      console.error("Error searching skipped matches:", error);
    } finally {
      LoaderService.hide();
    }
  }, [search, activeTab, userIdParams, userDetails.userId]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (search && activeTab === "Skipped Matchess") {
        fetchSearchSkippedMatches();
      } else if (!search && activeTab === "Skipped Matchess") {
        fetchMatchesData(true);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [search, activeTab, fetchSearchSkippedMatches, fetchMatchesData]);

  const fetchTransactionsData = useCallback(async () => {
    try {
      LoaderService.show();
      const response: any = await GetUserTransactions(
        userIdParams || userDetails.userId,
        transactionFilters,
      );
      if (response.status === "success") {
        setTransactionsData(response.data);
        setTableData((prev: any) => ({
          ...prev,
          Transactions: response.data.transactions || [],
        }));
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      LoaderService.hide();
    }
  }, [userIdParams, userDetails.userId, transactionFilters]);

  useEffect(() => {
    if (activeTab !== "Full Profile") {
      setCurrentActive("Full Profile");
    }
    setMatchesDataLoaded(false);
    setMatchesTotals((prev) => ({
      ...prev,
      published_matches: 0,
      unpublished_matches: 0,
    }));
    refreshData();
    fetchSpecialPreferenceNotes();
    fetchPublishedMatchCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIdParams, refreshData, fetchPublishedMatchCount]);

  useEffect(() => {
    if (
      activeTab === "Matched Profiles" ||
      activeTab === "Queued Profiles" ||
      activeTab === "Skipped Matchess" ||
      activeTab === "Special Pref Skipped" ||
      activeTab === "Special Pref Requeued"
    ) {
      fetchMatchesData();
      if (activeTab === "Matched Profiles") {
        fetchLikedMatchesData();
      }
    } else if (activeTab === "Liked Matches") {
      fetchLikedMatchesData();
    }
  }, [activeTab, fetchMatchesData, fetchLikedMatchesData]);

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const response = await GetDropdownData();
        if (response.status === true || response.success === true) {
          setDropdownNotes(response.data?.preference_dropdown_notes || []);
          setDropdownData(response.data || {});
        }
      } catch (error) {
        console.error("Error fetching dropdown data", error);
      }
    };
    fetchDropdownData();
  }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await GetSammelanEvents();
        if (res.status === true) {
          // Only show active events for assignment
          setSammelanEvents((res.data || []).filter((e: any) => e.is_active));
        }
      } catch (err) {
        console.error("Error fetching sammelan events:", err);
      }
    };
    fetchEvents();
  }, []);

  useEffect(() => {
    setMatchesDataLoaded(false);
  }, [matchApiFilters]);

  useEffect(() => {
    if (activeTab === "Transactions") {
      fetchTransactionsData();
    }
  }, [activeTab, fetchTransactionsData]);

  const fetchPotentialMatchesData = useCallback(async () => {
    try {
      LoaderService.show();
      const response: any = await GetPotentialMatchesAdmin(
        userIdParams || userDetails.userId,
        1000,
        0,
      );
      if (response.status === true) {
        const mappedPotential = (response.data || []).map((p: any) => {
          const details = p.candidate_details || {};
          let city = p.city || details.city || "";
          let state = p.state || details.state || "";
          const location = p.location || details.location || "";
          if ((!city || !state) && location) {
            const parts = String(location)
              .split(",")
              .map((x) => x.trim());
            if (!city) city = parts[0] || "";
            if (!state) state = parts[1] || "";
          }

          const normalizedProfession = normalizeText(
            p.profession ||
            p.occupation ||
            details.profession ||
            details.occupation ||
            "",
          );

          return {
            ...p,
            name: normalizeText(p.name || p.candidate_name || details.name),
            age: (p.age || details.age || "").toString(),
            city: normalizeText(city || "N/A"),
            state: normalizeText(state || "N/A"),
            location:
              normalizeText(
                p.location ||
                `${city || ""}${city && state ? ", " : ""}${state || ""}`,
              ) || "N/A",
            occupation: normalizedProfession || "N/A",
            profession: normalizedProfession || "N/A",
            qualification: normalizeText(
              p.qualification || p.degree || details.qualification || "N/A",
            ),
            family_background: normalizeText(
              p.family_background || details.family_background || "",
            ),
          };
        });

        setTableData((prev: any) => ({
          ...prev,
          "Potential Matches": mappedPotential,
        }));
        setPotentialMatchesTotal(response.count || 0);
      }
    } catch (error) {
      console.error("Error fetching potential matches:", error);
    } finally {
      LoaderService.hide();
    }
  }, [userIdParams, userDetails.userId]);

  useEffect(() => {
    if (activeTab === "Potential Matches") {
      fetchPotentialMatchesData();
    }
  }, [activeTab, fetchPotentialMatchesData]);

  const handleTriggerPotentialMatches = async () => {
    try {
      LoaderService.show();
      const currentUserId = userIdParams || userDetails.userId;
      if (!currentUserId) {
        toast.error("User ID not found");
        return;
      }

      const response: any = await PostTriggerPotentialMatches({
        user_id: currentUserId,
      });

      if (response && response.status === true) {
        toast.success("Potential matches triggered successfully!");

        // Show informative messages from the result array if present
        if (Array.isArray(response.result) && response.result.length > 0) {
          response.result.forEach((res: string) => {
            const lowerRes = res.toLowerCase();
            if (
              lowerRes.includes("error") ||
              lowerRes.includes("insufficient") ||
              lowerRes.includes("no potential")
            ) {
              toast.error(res, { duration: 6000 });
            } else {
              toast(res, { icon: "â„¹ï¸" });
            }
          });
        }

        // Refresh the list after triggering
        setTimeout(() => {
          fetchPotentialMatchesData();
        }, 1000);
      } else {
        toast.error(
          response?.message || "Something went wrong while triggering.",
        );
      }
    } catch (error) {
      console.error("Error triggering potential matches:", error);
      toast.error("Failed to trigger potential matches API.");
    } finally {
      LoaderService.hide();
    }
  };

  const setCurrentActive = (tab: string) => {
    setActiveTab(tab);
    setLocalItem("activeTab", tab);
  };

  const allTabOptions = useMemo(() => {
    const getOptions = (key: string) => {
      const data = tableData[key] || [];
      return {
        cities: Array.from(
          new Set(
            data
              .map((item: any) => item.city)
              .filter(Boolean)
              .filter((s: any) => String(s).trim() !== ""),
          ),
        ).sort() as string[],
        states: Array.from(
          new Set(
            data
              .map((item: any) => item.state)
              .filter(Boolean)
              .filter((s: any) => String(s).trim() !== ""),
          ),
        ).sort() as string[],
        professions: Array.from(
          new Set(
            data
              .map((item: any) => item.profession)
              .filter(Boolean)
              .filter((s: any) => String(s).trim() !== ""),
          ),
        ).sort() as string[],
        occupations: Array.from(
          new Set(
            data
              .map((item: any) => item.occupation)
              .filter(Boolean)
              .filter((s: any) => String(s).trim() !== ""),
          ),
        ).sort() as string[],
        familyTypes: Array.from(
          new Set(
            data
              .map((item: any) => item.family_type)
              .filter(Boolean)
              .filter((s: any) => String(s).trim() !== ""),
          ),
        ).sort() as string[],
        familyBackgrounds: Array.from(
          new Set(
            data
              .map((item: any) => item.family_background)
              .filter(Boolean)
              .filter((s: any) => String(s).trim() !== ""),
          ),
        ).sort() as string[],
        manglikOptions: ["Anshik_Manglik", "Not_Manglik", "Manglik"],
      };
    };

    return {
      queue: getOptions("Queued Profiles"),
      matched: getOptions("Matched Profiles"),
      skipped: getOptions("Skipped Matchess"),
      liked: getOptions("Like To"),
      likedBy: getOptions("Liked By"),
      rejected: getOptions("Rejected Profiles"),
      potentialMatches: getOptions("Potential Matches"),
    };
  }, [tableData]);

  const originalData = useMemo(() => {
    if (activeTab === "Matched Profiles") {
      if (matchedSubTab === "Outgoing") return tableData["Like To"] || [];
      if (matchedSubTab === "Incoming") return tableData["Liked By"] || [];
      return tableData["Matched Profiles"] || [];
    }
    return tableData[activeTab] || [];
  }, [activeTab, matchedSubTab, tableData]);

  const filteredData = useMemo(() => {
    let filtered = originalData;

    // Apply search filter
    if (search) {
      const normalizedSearch = search.toLowerCase();
      filtered = filtered.filter((row) =>
        buildRowSearchText(row).includes(normalizedSearch),
      );
    }

    // Apply queue filters only for "Queued Profiles" tab
    if (activeTab === "Queued Profiles") {
      filtered = filtered.filter((row) => {
        const matchesFilters =
          (!queueFilters.min_age ||
            parseInt(row.age) >= parseInt(queueFilters.min_age)) &&
          (!queueFilters.max_age ||
            parseInt(row.age) <= parseInt(queueFilters.max_age)) &&
          (!queueFilters.city.length || queueFilters.city.includes(row.city)) &&
          (!queueFilters.state.length ||
            queueFilters.state.includes(row.state)) &&
          (!queueFilters.profession.length ||
            queueFilters.profession.includes(row.profession)) &&
          (!queueFilters.manglik.length ||
            queueFilters.manglik.includes(row.manglik)) &&
          (!queueFilters.family_type ||
            row.family_type === queueFilters.family_type) &&
          (!queueFilters.family_background ||
            row.family_background === queueFilters.family_background) &&
          (queueFilters.proximity_range === "all" ||
            !Array.isArray(queueFilters.proximity_range)
            ? true
            : parseFloat(row.proximity) >= queueFilters.proximity_range[0] &&
            parseFloat(row.proximity) <= queueFilters.proximity_range[1]);

        return matchesFilters;
      });
    }

    // Apply potential matches filters only for "Potential Matches" tab
    if (activeTab === "Potential Matches") {
      filtered = filtered.filter((row) => {
        const name = String(row.name || "").toLowerCase();
        const matriId = String(row.matrimonial_id || "").toLowerCase();
        const city = String(row.city || "").trim();
        const state = String(row.state || "").trim();
        const profession = String(row.profession || row.occupation || "").trim();
        const professionLower = profession.toLowerCase();
        const familyBackground = String(row.family_background || "")
          .toLowerCase()
          .trim();
        const cityLower = city.toLowerCase();
        const stateLower = state.toLowerCase();
        const cityStateLower = `${city}, ${state}`.toLowerCase();

        return (
          (!potentialFilters.name ||
            name.includes(potentialFilters.name.toLowerCase().trim())) &&
          (!potentialFilters.matrimonial_id ||
            matriId.includes(
              potentialFilters.matrimonial_id.toLowerCase().trim(),
            )) &&
          (!potentialFilters.city.length ||
            potentialFilters.city.some((selected) => {
              const value = String(selected || "").toLowerCase().trim();
              if (!value) return false;
              return (
                cityLower === value ||
                stateLower === value ||
                cityStateLower.includes(value)
              );
            })) &&
          (!potentialFilters.city_query ||
            cityLower.includes(
              potentialFilters.city_query.toLowerCase().trim(),
            ) ||
            stateLower.includes(
              potentialFilters.city_query.toLowerCase().trim(),
            ) ||
            cityStateLower.includes(
              potentialFilters.city_query.toLowerCase().trim(),
            )) &&
          (!potentialFilters.profession.length ||
            potentialFilters.profession.some((selected) => {
              const value = String(selected || "").toLowerCase().trim();
              if (!value) return false;
              return (
                professionLower === value ||
                professionLower.includes(value) ||
                value.includes(professionLower)
              );
            })) &&
          (!potentialFilters.profession_query ||
            professionLower.includes(
              potentialFilters.profession_query.toLowerCase().trim(),
            )) &&
          (!potentialFilters.family_background ||
            familyBackground.includes(
              potentialFilters.family_background.toLowerCase().trim(),
            ))
        );
      });

      if (potentialFilters.age_order !== "default") {
        filtered = [...filtered].sort((a, b) => {
          const ageA = parseInt(a?.age || "0", 10) || 0;
          const ageB = parseInt(b?.age || "0", 10) || 0;
          return potentialFilters.age_order === "asc"
            ? ageA - ageB
            : ageB - ageA;
        });
      }
    }

    // Apply skipped filters only for "Skipped Matchess" tab
    if (activeTab === "Skipped Matchess") {
      filtered = filtered.filter((row) => {
        const matchesFilters =
          (!skippedFilters.min_age ||
            parseInt(row.age) >= parseInt(skippedFilters.min_age)) &&
          (!skippedFilters.max_age ||
            parseInt(row.age) <= parseInt(skippedFilters.max_age)) &&
          (!skippedFilters.city.length ||
            skippedFilters.city.includes(row.city)) &&
          (!skippedFilters.state.length ||
            skippedFilters.state.includes(row.state)) &&
          (!skippedFilters.profession.length ||
            skippedFilters.profession.includes(row.profession)) &&
          (!skippedFilters.occupation.length ||
            skippedFilters.occupation.includes(row.occupation)) &&
          (!skippedFilters.manglik.length ||
            skippedFilters.manglik.includes(row.manglik)) &&
          (!skippedFilters.family_type ||
            row.family_type
              ?.toLowerCase()
              .includes(skippedFilters.family_type.toLowerCase())) &&
          (!skippedFilters.family_background ||
            row.family_background
              ?.toLowerCase()
              .includes(skippedFilters.family_background.toLowerCase()));

        // Score status filter
        if (skippedFilters.score_status !== "all") {
          const reason = row.reason;
          let score: number | null = null;
          let isFailed = false;

          if (
            reason?.score !== undefined &&
            reason?.score !== null &&
            reason?.score !== ""
          ) {
            score = parseFloat(reason.score);
          }
          if (score === null && typeof reason?.reason === "string") {
            const m = reason.reason.match(
              /(?:score|Score)\s*[:(]?\s*(\d+(?:\.\d+)?)/i,
            );
            if (m) score = parseFloat(m[1]);
          }
          const reasonText =
            typeof reason === "string"
              ? reason
              : reason?.reason || reason?.reason_gap || "";
          if (
            typeof reasonText === "string" &&
            /failed|hard\s*block/i.test(reasonText)
          )
            isFailed = true;
          if (
            reason?.nonneg?.failed &&
            Object.keys(reason.nonneg.failed).length > 0
          )
            isFailed = true;

          const isRowFailed = score === 0 || (score === null && isFailed);
          if (skippedFilters.score_status === "failed" && !isRowFailed)
            return false;
          if (skippedFilters.score_status === "passed" && isRowFailed)
            return false;
        }

        return matchesFilters;
      });
    }

    // Apply matched filters only for "Matched Profiles" tab
    if (activeTab === "Matched Profiles") {
      filtered = filtered.filter((row) => {
        const matchesFilters =
          (!matchedFilters.min_age ||
            parseInt(row.age) >= parseInt(matchedFilters.min_age)) &&
          (!matchedFilters.max_age ||
            parseInt(row.age) <= parseInt(matchedFilters.max_age)) &&
          (!matchedFilters.city.length ||
            matchedFilters.city.includes(row.city)) &&
          (!matchedFilters.state.length ||
            matchedFilters.state.includes(row.state)) &&
          (!matchedFilters.profession.length ||
            matchedFilters.profession.includes(row.profession)) &&
          (!matchedFilters.manglik.length ||
            matchedFilters.manglik.includes(row.manglik)) &&
          (!matchedFilters.publish ||
            row.publish?.toLowerCase() ===
            matchedFilters.publish.toLowerCase()) &&
          (matchedFilters.score_range === "all" ||
            (matchedFilters.score_range === "0-20" &&
              parseFloat(row.score) >= 0 &&
              parseFloat(row.score) <= 20) ||
            (matchedFilters.score_range === "21-40" &&
              parseFloat(row.score) >= 21 &&
              parseFloat(row.score) <= 40) ||
            (matchedFilters.score_range === "41-60" &&
              parseFloat(row.score) >= 41 &&
              parseFloat(row.score) <= 60) ||
            (matchedFilters.score_range === "61-80" &&
              parseFloat(row.score) >= 61 &&
              parseFloat(row.score) <= 80) ||
            (matchedFilters.score_range === "81-100" &&
              parseFloat(row.score) >= 81 &&
              parseFloat(row.score) <= 100)) &&
          (matchedFilters.proximity_range === "all" ||
            !Array.isArray(matchedFilters.proximity_range)
            ? true
            : parseFloat(row.proximity) >= matchedFilters.proximity_range[0] &&
            parseFloat(row.proximity) <= matchedFilters.proximity_range[1]);

        return matchesFilters;
      });
    }

    // Apply rejected filters only for "Rejected Profiles" tab
    // if (activeTab === "Rejected Profiles") {
    //   filtered = filtered.filter((row) => {
    //     const matchesFilters =
    //       (!rejectedFilters.min_age || parseInt(row.age) >= parseInt(rejectedFilters.min_age)) &&
    //       (!rejectedFilters.max_age || parseInt(row.age) <= parseInt(rejectedFilters.max_age)) &&
    //       (!rejectedFilters.city || row.city?.toLowerCase().includes(rejectedFilters.city.toLowerCase())) &&
    //       (!rejectedFilters.state || row.state?.toLowerCase().includes(rejectedFilters.state.toLowerCase())) &&
    //       (!rejectedFilters.family_type || row.family_type?.toLowerCase().includes(rejectedFilters.family_type.toLowerCase())) &&
    //       (!rejectedFilters.family_background || row.family_background?.toLowerCase().includes(rejectedFilters.family_background.toLowerCase()));

    //     return matchesFilters;
    //   });
    // }

    return filtered;
  }, [
    search,
    activeTab,
    originalData,
    queueFilters,
    skippedFilters,
    matchedFilters,
    potentialFilters,
  ]);

  const sortedData = useMemo(() => {
    if (!sortConfig) return filteredData;
    const { key, direction } = sortConfig;
    return [...filteredData].sort((a, b) => {
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);

  const paginatedData = useMemo(() => {
    if (!sortedData || !Array.isArray(sortedData)) return [];
    const start = (page - 1) * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  const totalPages = Math.ceil((filteredData?.length || 0) / rowsPerPage);

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (!prev || prev.key !== key) return { key, direction: "asc" };
      if (prev.direction === "asc") return { key, direction: "desc" };
      return null;
    });
  };

  const handleViewReasonButton = (row: any) => {
    console.log("Starting...", row);
    setModels((prev) => ({ ...prev, row: row }));
    setModels((prev) => ({ ...prev, reasonAndGapModal: true }));
  };

  const handleMatchMakingSkip = async () => {
    if (matchMakingStep === 1) {
      console.log("Astro");
      const _status = await generatePrompt(
        candidateUserId,
        PromptGeneraterName.Astrology,
      );
      if (_status) {
        setMatchMakingStep(2);
        setMatchMakingStepText(
          `Astrology Prompt For UserId: ${userId} or CandidateUserId: ${candidateUserId}`,
        );
      }
    } else if (matchMakingStep === 2) {
      console.log("Summary");
      const _status = await generatePrompt(
        candidateUserId,
        PromptGeneraterName.Summary,
      );
      if (_status) {
        setMatchMakingStep(3);
        setMatchMakingStepText(
          `Summary Prompt For UserId: ${userId} or CandidateUserId: ${candidateUserId}`,
        );
      }
    } else if (matchMakingStep === 3) {
      console.log("Final");
      setMatchMakingStepText(
        `Match Making For UserId: ${userId} or CandidateUserId: ${candidateUserId}`,
      );
      setMatchMakingStep(1);
      setShowMatchMakingModal(false);
      window.location.reload();
    }
  };

  const handleMatchMakingSubmit = async (
    userInput: string,
  ): Promise<boolean> => {
    try {
      let res = false;
      let _status: any = false;

      switch (matchMakingStep) {
        case 1:
          res = await storePrompt(
            candidateUserId,
            userInput,
            PromptGeneraterName.MatchMaking,
          );
          if (!res) return false;

          _status = await generatePrompt(
            candidateUserId,
            PromptGeneraterName.Astrology,
          );
          if (_status) {
            setMatchMakingStep(2);
            setMatchMakingStepText(
              `Astrology Prompt For UserId: ${userId} or CandidateUserId: ${candidateUserId}`,
            );
          }
          return true;

        case 2:
          res = await storePrompt(
            candidateUserId,
            userInput,
            PromptGeneraterName.Astrology,
          );
          if (!res) return false;

          _status = await generatePrompt(
            candidateUserId,
            PromptGeneraterName.Summary,
          );
          if (_status) {
            setMatchMakingStep(3);
            setMatchMakingStepText(
              `Summary Prompt For UserId: ${userId} or CandidateUserId: ${candidateUserId}`,
            );
          }
          return true;

        case 3:
          res = await storePrompt(
            candidateUserId,
            userInput,
            PromptGeneraterName.Summary,
          );
          if (!res) return false;

          setMatchMakingStepText(
            `Match Making Completed For UserId: ${userId} or CandidateUserId: ${candidateUserId}`,
          );
          setMatchMakingStep(1);
          setShowMatchMakingModal(false);
          // removeRowByUserId(candidateUserId);
          window.location.reload();
          return true;

        default:
          LoaderService.show();
          toast.error("Unexpected step. Please contact developer.");
          return false;
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
      console.error("handleMatchMakingSubmit error:", error);
      return false;
    }
  };

  const generatePrompt = async (
    _candidateUserId: string,
    promptType: PromptGeneraterName,
  ) => {
    LoaderService.show();
    setCandidateUserId(_candidateUserId);

    try {
      const data = {
        userId: userDetails.userId,
        candidateUserId: _candidateUserId,
        promptType: promptType,
      };
      const response = await PostGeneratePrompt(data);

      if (response.status === "success") {
        setMatchMakingPrompt(response.prompt);
        LoaderService.hide();
        setShowMatchMakingModal(true);

        return true;
      } else if (response.message?.toLowerCase().includes("no tags found")) {
        LoaderService.hide();
        toast.error("No tags found for this user. Matcmaking requires tags.");
      } else {
        LoaderService.hide();
        return false;
      }
    } catch (error: any) {
      LoaderService.hide();
      return false;
    }
  };

  const fetchBothProfileDetails = async (_cid: any = null) => {
    try {
      const cid = typeof _cid === "string" ? _cid : null;
      const uid = userIdParams || userDetails.userId;

      if (!uid) {
        console.error("No user ID available for fetching profile details");
        return;
      }

      LoaderService.show();
      const response: any = await GetProfileDetails(uid);
      if (response.status === "success") {
        const {
          preference,
          profile,
          photos,
          bioData,
          instruction,
          sammelan_registrations,
        } = response.data;
        const mappedProfile = {
          ...profile,
          name: profile.Name || profile.name,
          photo:
            profile.photo ||
            profile.Photo ||
            photos?.find((p: any) => p.profile)?.url ||
            photos?.[0]?.url ||
            "",
          adminNote:
            profile.AdminNote ||
            profile.admin_note ||
            profile.note ||
            instruction ||
            "",
        };
        let temp = {
          profile: mappedProfile,
          preference: preference,
          photos: photos,
          bioData: bioData,
          sammelanRegistrations: sammelan_registrations || [],
        };
        setUserProfileData(temp);
        setLocalItem("UserProfileDetails", JSON.stringify(temp));
      }

      const candidateRes: any = await GetProfileDetails(
        cid ? cid : candidateUserId,
      );
      if (candidateRes.status === "success") {
        const {
          preference,
          profile,
          photos,
          bioData,
          instruction,
          sammelan_registrations,
        } = candidateRes.data;
        const mappedProfile = {
          ...profile,
          name: profile.Name || profile.name,
          photo:
            profile.photo ||
            profile.Photo ||
            photos?.find((p: any) => p.profile)?.url ||
            photos?.[0]?.url ||
            "",
          adminNote:
            profile.AdminNote ||
            profile.admin_note ||
            profile.note ||
            instruction ||
            "",
        };
        let temp = {
          profile: mappedProfile,
          preference: preference,
          photos: photos,
          bioData: bioData,
          sammelanRegistrations: sammelan_registrations || [],
        };
        setCandidateProfileData(temp);
        setLocalItem("CandidateProfileDetails", JSON.stringify(temp));
      }
    } catch (error: any) {
    } finally {
      LoaderService.hide();
    }
  };

  const handleFetchBothProfileDetails = async (_candidate_id: any = null) => {
    // Prevent event objects from being treated as strings
    const cid = typeof _candidate_id === "string" ? _candidate_id : null;

    let user_profile = getLocalItem("UserProfileDetails");
    let candidate_profile = getLocalItem("CandidateProfileDetails");

    if (user_profile && candidate_profile && !cid) {
      setUserProfileData(JSON.parse(user_profile));
      setCandidateProfileData(JSON.parse(candidate_profile));
    } else {
      await fetchBothProfileDetails(cid);
    }

    setComparisonModal({
      status: true,
      type: cid ? "queue" : "match",
    });
  };

  const hanldePublish = async (candidateId: string) => {
    setPublishPending({ type: "individual", candidateId });
    setConfirmPublishModal(true);
    setPublishInstructionData(null);
    setPublishInstructionLoading(true);
    try {
      const res: any = await PostCommonInstructionDislike({
        user_id: userIdParams || userDetails.userId,
        candidate_id: candidateId,
      });
      if (res?.status === true) {
        setPublishInstructionData(res.data);
      }
    } catch (err) {
      console.error("Error fetching instruction/dislike data:", err);
    } finally {
      setPublishInstructionLoading(false);
    }
    return true;
  };

  const actualHandlePublish = async (candidateId: string) => {
    LoaderService.show();
    try {
      const data = {
        userId: userDetails.userId,
        candidateUserId: candidateId,
      };
      const response = await PostPublishProfile(data);
      if (response.status === "success") {
        LoaderService.hide();
        showPublishNotificationToast(response);
        window.location.reload();
        return true;
      } else {
        LoaderService.hide();
        return false;
      }
    } catch (error: any) {
      LoaderService.hide();
      toast.error("Failed to publish profile");
      return false;
    }
  };

  const handleAssignToSammelan = async () => {
    if (!selectedSammelanEvent) {
      toast.error("Please select an event");
      return;
    }

    setIsAssigningSammelan(true);
    LoaderService.show();
    try {
      const response = await PostAssignUserSammelan({
        user_id: userIdParams || userDetails.userId,
        sammelan_event_id: parseInt(selectedSammelanEvent),
      });
      if (response.status === true) {
        toast.success(
          response.message || "User assigned to sammelan successfully",
        );
      } else {
        toast.error(response.message || "Failed to assign user to event");
      }
    } catch (error) {
      console.error("Error assigning to sammelan:", error);
      toast.error("Error assigning user to event");
    } finally {
      setIsAssigningSammelan(false);
      LoaderService.hide();
    }
  };

  const openRemoveSammelanModal = (eventId: number, eventName: string) => {
    setRemoveSammelanTarget({ eventId, eventName });
    setShowRemoveSammelanModal(true);
  };

  const confirmRemoveSammelan = async () => {
    if (!removeSammelanTarget) return;

    setShowRemoveSammelanModal(false);
    LoaderService.show();
    try {
      const response = await PostRemoveUserSammelan({
        user_id: userIdParams || userDetails.userId,
        sammelan_event_id: removeSammelanTarget.eventId,
      });
      if (response.status === true) {
        toast.success(response.message || "User removed from Sammelan");
        await fetchBothProfileDetails();
      } else {
        toast.error(response.message || "Failed to remove user from Sammelan");
      }
    } catch (error) {
      console.error("Error removing user from sammelan:", error);
      toast.error("Error removing user from sammelan");
    } finally {
      LoaderService.hide();
      setRemoveSammelanTarget(null);
    }
  };

  const handleResetMatch = async (candidateId: string) => {
    LoaderService.show();
    try {
      const data = {
        userId: userDetails.userId,
        candidateUserId: candidateId,
      };
      const response = await PostResetMatch(data);
      if (response.status === "success") {
        LoaderService.hide();
        return true;
      } else {
        LoaderService.hide();
        return false;
      }
    } catch (error: any) {
      LoaderService.hide();
      toast.error("Failed to reset match");
      return false;
    }
  };

  const handleViewOppositeMatch = async () => {
    if (!oppositeAstroModalData?.profile?.userId) {
      try {
        LoaderService.show();
        const res: any = await GetMatchedProfile(
          candidateUserId,
          userIdParams || userDetails.userId,
        );
        if (res.status === "success" || res.status === true) {
          setOppositeAstroModalData(res.data);
          setShowOppositeMatch(true);
          return { notificaion: true, showOppositeMatch: true };
        } else {
          return { notificaion: false, showOppositeMatch: false };
        }
      } catch (error) {
        console.error("Error fetching reverse match:", error);
        return { notificaion: false, showOppositeMatch: false };
      } finally {
        LoaderService.hide();
      }
    } else {
      const nextState = !showOppositeMatch;
      setShowOppositeMatch(nextState);
      return { notificaion: true, showOppositeMatch: nextState };
    }
  };

  const handleUpdateDecisionToYes = async (
    _candidateId: string,
    decision: string,
  ) => {
    console.log("Pringin UPdate Decision");
    let data = {
      userId: userId,
      candidateUserId: _candidateId,
      decision: decision,
    };

    if (showOppositeMatch) {
      data = {
        userId: _candidateId,
        candidateUserId: userId,
        decision: decision,
      };
    }
    LoaderService.show();
    const response = await UpdateDecision(data);
    if (response.status === "success") {
      LoaderService.hide();
      toast.success("Decision Updated");
      return true;
    } else {
      LoaderService.hide();
      return false;
    }
  };

  const handleSendMatchPdf = async (
    candidateId: string,
    sendTo: "user" | "candidate" | "both",
  ) => {
    try {
      LoaderService.show();
      let payload;

      if (sendTo === "candidate") {
        payload = {
          user_id: candidateId,
          candidate_id: userIdParams,
          both: false,
        };
      } else if (sendTo === "user") {
        payload = {
          user_id: userIdParams || userDetails.userId,
          candidate_id: candidateId,
          both: false,
        };
      } else {
        // both
        payload = {
          user_id: userIdParams || userDetails.userId,
          candidate_id: candidateId,
          both: true,
        };
      }

      const response = await PostSendMatchNotification(payload);
      if (response.status === "success") {
        toast.success(
          response.message ||
          "Match notification queued for background processing",
        );
      } else {
        toast.error(response.message || "Failed to send match notification");
      }
    } catch (error) {
      console.error("Send PDF error:", error);
      toast.error("Error sending match notification");
    } finally {
      LoaderService.hide();
      setSendPdfModal({ isOpen: false, candidateId: "", candidateName: "", isUserRequested: false });
    }
  };

  const handleSendRequestedMatchTemplate = async (candidateId: string) => {
    const requesterId = userIdParams || userDetails.userId;
    if (!requesterId || !candidateId) {
      toast.error("Missing requester or candidate id");
      return;
    }
    setSendingRequestedTemplate((current) => ({ ...current, [candidateId]: true }));
    try {
      const response = await PostSendRequestedMatchTemplate({
        requester_id: requesterId,
        candidate_id: candidateId,
      });
      if (response.status === "success") {
        toast.success(response.message || "Template sent to candidate");
      } else {
        toast.error(response.message || "Failed to send template");
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || error?.message || "Failed to send template");
    } finally {
      setSendingRequestedTemplate((current) => ({ ...current, [candidateId]: false }));
    }
  };

  const handleLikeDislikeAction = async (
    candidateId: string,
    liked: boolean,
    disliked: boolean,
    reason?: string,
  ) => {
    try {
      LoaderService.show();
      const payload = {
        user_id: userIdParams || userDetails.userId,
        candidate_user_id: candidateId,
        liked: liked,
        disliked: disliked,
        reason: reason || "",
      };

      const res: any = await PostAdminLikeDislike(payload);
      if (res.status === true) {
        toast.success(
          liked
            ? "Profile Liked"
            : disliked
              ? "Profile Disliked"
              : "Engagement Reset",
        );
        setMatchesDataLoaded(false);
        fetchMatchesData();
      } else {
        toast.error(res.message || "Failed to update engagement");
      }
    } catch (error) {
      console.error("Engagement update error:", error);
      toast.error("Error updating engagement status");
    } finally {
      LoaderService.hide();
    }
  };

  const handleForceMatchmaking = async (candidateUserId: string) => {
    try {
      LoaderService.show();
      const payload = {
        userId: userIdParams || userDetails.userId,
        candidateUserId: candidateUserId,
        both: true,
        reason: "Manual admin match",
        priority: 10,
      };

      const res: any = await PostForceMatchmakingAdmin(payload);
      if (res.status === "success" || res.status === true) {
        toast.success(res.message || "Matchmaking queued successfully");
        setMatchesDataLoaded(false);
        fetchMatchesData();
        refreshData();
      } else {
        toast.error(res.message || "Failed to force matchmaking");
      }
    } catch (error: any) {
      console.error("Force matchmaking error:", error);
      toast.error("Error triggering force matchmaking");
    } finally {
      LoaderService.hide();
    }
  };

  // handleMoveToQueue removed as unused

  const handleDeactivatePair = async (candidateId: string) => {
    try {
      LoaderService.show();
      const res: any = await PostDeactivatePair({
        user_id: userIdParams || userDetails.userId,
        candidate_id: candidateId,
      });
      if (res.status === true) {
        toast.success(res.message || "Pair deactivated successfully");
        setMatchesDataLoaded(false);
        fetchMatchesData(true);
        refreshData();
      } else {
        toast.error(res.message || "Failed to deactivate pair");
      }
    } catch (error) {
      toast.error("Error deactivating pair");
    } finally {
      LoaderService.hide();
    }
  };

  const handleMoveToSkipped = async (candidateId: string) => {
    try {
      LoaderService.show();
      const payload = {
        user_id: userIdParams || userDetails.userId,
        candidate_id: candidateId,
        both: true,
      };

      const res: any = await PostMoveToSkipped(payload);
      if (res.status === true) {
        toast.success(res.message || "Moved to skipped matches");
        setMatchesDataLoaded(false);
        fetchMatchesData();
        refreshData();
      } else {
        toast.error(res.message || "Failed to move to skipped matches");
      }
    } catch (error: any) {
      console.error("Move to skipped error:", error);
      toast.error("Error moving to skipped matches");
    } finally {
      LoaderService.hide();
    }
  };

  const handlePublishMatch = async (row: any) => {
    setPublishPending({ type: "match_row", candidateId: row.userId, row });
    setConfirmPublishModal(true);
    setPublishInstructionData(null);
    setPublishViolations(null);
    setPublishOverrideReason("");
    setPublishInstructionLoading(true);
    const baseUser = userIdParams || userDetails.userId;
    try {
      const [instr, preview]: any = await Promise.all([
        PostCommonInstructionDislike({ user_id: baseUser, candidate_id: row.userId }),
        GetPublishPreview(baseUser, row.userId).catch(() => null),
      ]);
      if (instr?.status === true) {
        setPublishInstructionData(instr.data);
      }
      if (preview?.status === true) {
        setPublishViolations(preview.data);
      }
    } catch (err) {
      console.error("Error fetching instruction/preview data:", err);
    } finally {
      setPublishInstructionLoading(false);
    }
  };

  const renderDeactivatePairButton = (candidateId: string) => {
    if (currentUserRole !== "SUPER_ADMIN") return null;

    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          setPendingDeactivateCandidateId(candidateId);
          setConfirmDeactivatePairModal(true);
        }}
        className="flex-1 px-3 py-1.5 bg-red-900/20 hover:bg-red-700/40 text-red-400 border border-red-700/30 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all shadow-sm active:scale-95"
      >
        Deactivate
      </button>
    );
  };

  // Admin force-publish that overrides waivable gate violations (sense + caste + marital) with a
  // recorded reason. The server still refuses disliked/blocked. Used when the popup shows violations.
  const actualHandlePublishMatchOverride = async (row: any, reason: string) => {
    try {
      LoaderService.show();
      const response: any = await PostPublishWithOverride({
        user_id: userDetails.userId || userIdParams,
        candidate_id: row.userId,
        both: true,
        reason,
      });
      if (response?.status === true) {
        showPublishNotificationToast(response);
        setTableData((prev) => ({
          ...prev,
          "Matched Profiles": prev["Matched Profiles"].map((p) =>
            p.userId === row.userId
              ? { ...p, publish: "yes", publish_at: new Date().toISOString() }
              : p,
          ),
        }));
        void fetchPublishedMatchCount();
      } else {
        toast.error(response?.message || "Could not publish — this block cannot be overridden");
      }
    } catch (error) {
      console.error("Override publish error:", error);
      toast.error("An error occurred while publishing");
    } finally {
      LoaderService.hide();
    }
  };

  const actualHandlePublishMatch = async (row: any) => {
    try {
      LoaderService.show();
      const payload = {
        matches: [
          {
            user_id: userDetails.userId || userIdParams,
            candidate_id: row.userId,
            llm_score: parseFloat(row.llmScore || row.score || 0),
            gunn_score: parseFloat(row.gptScore || 0),
            both: true,
          },
        ],
      };

      const response = await PostPublishMultiple(payload);
      if (response.status === true || response.status === "success") {
        toast.success("Publish job queued. Check the delivery badge/log after the job completes.");
        // Update local state to show "yes" and hide the button
        setTableData((prev) => ({
          ...prev,
          "Matched Profiles": prev["Matched Profiles"].map((p) =>
            p.userId === row.userId
              ? { ...p, publish: "yes", publish_at: new Date().toISOString() }
              : p,
          ),
        }));
        void fetchPublishedMatchCount();
      } else {
        toast.error(response.message || "Failed to publish match");
      }
    } catch (error) {
      console.error("Publish error:", error);
      toast.error("An error occurred while publishing");
    } finally {
      LoaderService.hide();
    }
  };

  const deleteCurrentUser = async () => {
    const targetId = userIdParams || userDetails.userId;
    console.log("Hard Deleting User", targetId);
    try {
      LoaderService.show();
      const res = await HardDeleteUser(targetId);
      if (res.status === true) {
        toast.success(res.message || "User permanently deleted");
        window.location.href = "/users";
        setConfirmDeleteModal(false);
      } else {
        toast.error(res.message || "Failed to delete user");
      }
    } catch (error) {
      toast.error("An error occurred while deleting user");
    } finally {
      LoaderService.hide();
    }
  };

  const enableCurrentUser = async () => {
    const targetId = userIdParams || userDetails.userId;
    console.log("Enabling User", targetId);
    try {
      LoaderService.show();
      const res = await EnableUser(targetId);
      if (res.status === true) {
        toast.success(res.message || "User enabled successfully");
        window.location.reload();
        setConfirmEnableModal(false);
      } else {
        toast.error(res.message || "Failed to enable user");
      }
    } catch (error) {
      toast.error("An error occurred while enabling user");
    } finally {
      LoaderService.hide();
    }
  };

  const disableCurrentUser = async () => {
    const targetId = userIdParams || userDetails.userId;
    console.log("Disabling current user", targetId);
    try {
      LoaderService.show();
      const res = await DisableUser(targetId);
      if (res.status === true) {
        toast.success(res.message || "User disabled successfully");
        window.location.reload();
        setConfirmDisableModal(false);
      } else {
        toast.error(res.message || "Failed to disable user");
      }
    } catch (error) {
      toast.error("An error occurred while disabling user");
    } finally {
      LoaderService.hide();
    }
  };

  const holdCurrentUser = async () => {
    const targetId = userIdParams || userDetails.userId;
    const duration = Number(holdDurationDays);

    if (!targetId) {
      toast.error("User ID not found");
      return;
    }

    if (!Number.isFinite(duration) || duration < 1) {
      toast.error("Enter hold duration in days");
      return;
    }

    try {
      LoaderService.show();
      const startedAt = new Date().toISOString();
      setHoldStartedAt(startedAt);
      const res = await PatchHoldUser(targetId, startedAt, duration, holdReason);
      if (res.status === true) {
        toast.success(res.message || "User put on hold successfully");
        setHoldUserModal(false);
        setTimeout(() => window.location.reload(), 800);
      } else {
        toast.error(res.message || "Failed to hold user");
      }
    } catch (error) {
      toast.error("An error occurred while holding user");
    } finally {
      LoaderService.hide();
    }
  };

  const handleVerifyUser = async () => {
    const targetId = userIdParams || userDetails.userId;
    console.log("Verifying User", targetId);
    try {
      LoaderService.show();
      const response = await PostTriggerServices(targetId, true);
      if (response.status === "success" || response.status === true) {
        toast.success(
          response.message || "User approved and services triggered",
        );
        setConfirmVerifyModal(false);
        setUserDetails((prev) => ({ ...prev, verified: true }));
        const currentDetails = JSON.parse(
          localStorage.getItem("userDetails") || "{}",
        );
        localStorage.setItem(
          "userDetails",
          JSON.stringify({ ...currentDetails, verified: true }),
        );
      } else {
        setErrorModal({
          show: true,
          message: buildErrorMessage(response),
          variant: "error",
        });
      }
    } catch (error) {
      console.error("Verification failed:", error);
      setErrorModal({
        show: true,
        message: "An error occurred while verifying user",
        variant: "error",
      });
    } finally {
      LoaderService.hide();
      setConfirmVerifyModal(false);
    }
  };

  const handleResetMatchingData = async () => {
    const targetId = userIdParams || userDetails.userId;
    try {
      LoaderService.show();
      const response = await PostResetMatchingData({
        user_id: targetId,
        run_common_service: true,
      });
      if (response.status === true) {
        toast.success(
          response.message || "Matchmaking data reset successfully",
        );
        setConfirmResetMatchingModal(false);
        setTimeout(() => window.location.reload(), 1500);
      } else {
        toast.error(response.message || "Failed to reset matching data");
      }
    } catch (error: any) {
      toast.error("Error resetting matching data");
    } finally {
      LoaderService.hide();
    }
  };

  // const handleAddPayment = async ()=>{
  //   console.log("adding payment details");
  //   const response = await AddPayment() ;

  // }

  const handleShowPassword = async () => {
    if (!password) {
      const res = await GetPassword(userIdParams || userDetails.userId);
      if (res.status === "success") {
        setPassword(res.decrypted_password || res.password);
        setTimeout(() => {
          setPassword("");
        }, 60000);
      }
    }
  };

  // â”€â”€ Special Preference Notes handlers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const fetchSpecialPreferenceNotes = useCallback(async () => {
    const currentUserId = userIdParams || userDetails.userId;
    try {
      const res: any = await (GetSpecialPreferenceNotes as any)(currentUserId);
      let normalizedNotes: Record<string, any> = {};
      if (res?.status === true && res?.data?.special_preference_notes) {
        normalizedNotes = normalizeSpecialPreferenceNotes(
          res.data.special_preference_notes,
        );
        setSpecialPreferenceNotes(normalizedNotes);
      } else {
        setSpecialPreferenceNotes({});
      }
      return normalizedNotes;
    } catch (err) {
      setSpecialPreferenceNotes({});
      return {};
    }
  }, [userIdParams, userDetails.userId]);

  const getSpecialPreferenceSnapshot = (notes: Record<string, any>) =>
    JSON.stringify(normalizeSpecialPreferenceNotes(notes));

  const syncSpecialPreferenceMutationResult = async (
    previousSnapshot: string,
    successMessage: string,
    errorMessage: string,
  ) => {
    const latestNotes = await fetchSpecialPreferenceNotes();
    const latestSnapshot = getSpecialPreferenceSnapshot(latestNotes || {});

    if (latestSnapshot !== previousSnapshot) {
      toast.success(successMessage);
      return true;
    }

    toast.error(errorMessage);
    return false;
  };

  // Helper function to format special preference data for API
  const formatSpecialPreferenceData = (rawData: any) => {
    const notes: Record<string, any> = {};
    const includes: Record<string, any> = {};
    const excludes: Record<string, any> = {};

    const normalizeValueToArray = (value: any): string[] => {
      if (Array.isArray(value)) {
        return value.map((item) => String(item).trim()).filter(Boolean);
      }
      if (typeof value === "string") {
        return value
          .split(",")
          .map((s: string) => s.trim())
          .filter(Boolean);
      }
      if (typeof value === "number" || typeof value === "boolean") {
        return [String(value)];
      }
      return [];
    };

    if (!rawData || typeof rawData !== "object") {
      return notes;
    }

    Object.entries(rawData).forEach(([key, value]) => {
      if (key === "includes" && value && typeof value === "object") {
        Object.entries(value).forEach(([includeKey, includeValue]) => {
          const values = normalizeValueToArray(includeValue);
          if (values.length > 0) {
            includes[includeKey] = values;
          }
        });
        if (Object.keys(includes).length > 0) {
          notes.includes = includes;
        }
        return;
      }

      if (key === "excludes" && value && typeof value === "object") {
        Object.entries(value).forEach(([excludeKey, excludeValue]) => {
          const values = normalizeValueToArray(excludeValue);
          if (values.length > 0) {
            excludes[excludeKey] = values;
          }
        });
        if (Object.keys(excludes).length > 0) {
          notes.excludes = excludes;
        }
        return;
      }

      const values = normalizeValueToArray(value);
      if (values.length > 0) {
        includes[key] = values;
      }
    });

    if (Object.keys(includes).length > 0) {
      notes.includes = includes;
    }
    if (Object.keys(excludes).length > 0) {
      notes.excludes = excludes;
    }

    return notes;
  };

  const handleSaveSpecialPreference = async (rawData: Record<string, any>) => {
    const currentUserId = userIdParams || userDetails.userId;
    const previousSnapshot = getSpecialPreferenceSnapshot(
      specialPreferenceNotes,
    );
    const isAlreadyFormatted = Object.entries(rawData).every(([key, value]) =>
      key === "excludes" || key === "includes"
        ? Boolean(value) &&
        typeof value === "object" &&
        Object.values(value as Record<string, any>).every((item) =>
          Array.isArray(item),
        )
        : Array.isArray(value),
    );

    const notes = isAlreadyFormatted
      ? rawData
      : formatSpecialPreferenceData(rawData);
    if (Object.keys(notes).length === 0) {
      toast.error("Please provide at least one preference");
      return;
    }
    try {
      LoaderService.show();
      const res: any = await (PostSpecialPreferenceNotes as any)(
        { notes, merge: false },
        currentUserId,
      );
      if (res?.status === true) {
        toast.success("Special preference saved");
        await fetchSpecialPreferenceNotes();
      } else {
        await syncSpecialPreferenceMutationResult(
          previousSnapshot,
          "Special preference saved",
          res?.message || "Failed to save special preference",
        );
      }
    } catch (err: any) {
      await syncSpecialPreferenceMutationResult(
        previousSnapshot,
        "Special preference saved",
        err?.message || "Error saving special preference",
      );
    } finally {
      LoaderService.hide();
    }
  };

  const handlePatchSpecialPreference = async (rawData: Record<string, any>) => {
    const currentUserId = userIdParams || userDetails.userId;
    const previousSnapshot = getSpecialPreferenceSnapshot(
      specialPreferenceNotes,
    );
    const isAlreadyFormatted = Object.entries(rawData).every(([key, value]) =>
      key === "excludes" || key === "includes"
        ? Boolean(value) &&
        typeof value === "object" &&
        Object.values(value as Record<string, any>).every((item) =>
          Array.isArray(item),
        )
        : Array.isArray(value),
    );
    const notes = isAlreadyFormatted
      ? rawData
      : formatSpecialPreferenceData(rawData);
    if (Object.keys(notes).length === 0) {
      toast.error("Please provide at least one preference");
      return;
    }
    try {
      LoaderService.show();
      const res: any = await (PatchSpecialPreferenceNotes as any)(
        { notes, merge: true },
        currentUserId,
      );
      if (res?.status === true) {
        toast.success("Special preference updated");
        await fetchSpecialPreferenceNotes();
      } else {
        await syncSpecialPreferenceMutationResult(
          previousSnapshot,
          "Special preference updated",
          res?.message || "Failed to update special preference",
        );
      }
    } catch (err: any) {
      await syncSpecialPreferenceMutationResult(
        previousSnapshot,
        "Special preference updated",
        err?.message || "Error updating special preference",
      );
    } finally {
      LoaderService.hide();
    }
  };

  const handleDeleteSpecialPreference = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to clear all special preference notes?",
    );
    if (!confirmed) return;

    const currentUserId = userIdParams || userDetails.userId;
    const previousSnapshot = getSpecialPreferenceSnapshot(
      specialPreferenceNotes,
    );
    try {
      LoaderService.show();
      const res: any = await (DeleteSpecialPreferenceNotes as any)(
        currentUserId,
      );
      if (res?.status === true) {
        toast.success("Special preference cleared");
        setSpecialPreferenceNotes({});
      } else {
        await syncSpecialPreferenceMutationResult(
          previousSnapshot,
          "Special preference cleared",
          res?.message || "Failed to clear special preference",
        );
      }
    } catch (err: any) {
      await syncSpecialPreferenceMutationResult(
        previousSnapshot,
        "Special preference cleared",
        err?.message || "Error clearing special preference",
      );
    } finally {
      LoaderService.hide();
    }
  };

  const handleRemoveSpecialPreferenceKey = async (
    key: string,
    value?: string,
    mode: "includes" | "excludes" = "includes",
  ) => {
    const label = value ? `"${value}"` : `"${key}"`;
    const confirmed = window.confirm(
      value
        ? `Are you sure you want to remove ${label} from special preference notes?`
        : `Are you sure you want to clear ${label} from special preference notes?`,
    );
    if (!confirmed) return;

    const currentUserId = userIdParams || userDetails.userId;
    const previousSnapshot = getSpecialPreferenceSnapshot(
      specialPreferenceNotes,
    );
    try {
      LoaderService.show();
      const res: any = await (PostRemoveSpecialPreferenceKey as any)(
        {
          key,
          value: value ?? null,
          is_exclude: mode === "excludes",
          is_include: mode === "includes",
        },
        currentUserId,
      );
      if (res?.status === true) {
        toast.success("Preference entry removed");
        await fetchSpecialPreferenceNotes();
      } else {
        await syncSpecialPreferenceMutationResult(
          previousSnapshot,
          "Preference entry removed",
          res?.message || "Failed to remove preference entry",
        );
      }
    } catch (err: any) {
      await syncSpecialPreferenceMutationResult(
        previousSnapshot,
        "Preference entry removed",
        err?.message || "Error removing preference entry",
      );
    } finally {
      LoaderService.hide();
    }
  };

  const handleClearSpecialPreferenceSection = async (
    mode: "includes" | "excludes",
  ) => {
    const sectionLabel = mode === "excludes" ? "Excludes" : "Includes";
    const confirmed = window.confirm(
      `Are you sure you want to clear all ${sectionLabel.toLowerCase()} from special preference notes?`,
    );
    if (!confirmed) return;

    const nextNotes = {
      ...specialPreferenceNotes,
      [mode]: {},
    };

    if (mode === "includes") {
      delete nextNotes.includes;
    } else {
      delete nextNotes.excludes;
    }

    if (Object.keys(nextNotes).length === 0) {
      await handleDeleteSpecialPreference();
      return;
    }

    const currentUserId = userIdParams || userDetails.userId;
    const previousSnapshot = getSpecialPreferenceSnapshot(
      specialPreferenceNotes,
    );

    try {
      LoaderService.show();
      const res: any = await (PostSpecialPreferenceNotes as any)(
        { notes: nextNotes, merge: false },
        currentUserId,
      );

      if (res?.status === true) {
        toast.success(`${sectionLabel} cleared`);
        await fetchSpecialPreferenceNotes();
      } else {
        await syncSpecialPreferenceMutationResult(
          previousSnapshot,
          `${sectionLabel} cleared`,
          res?.message || `Failed to clear ${sectionLabel.toLowerCase()}`,
        );
      }
    } catch (err: any) {
      await syncSpecialPreferenceMutationResult(
        previousSnapshot,
        `${sectionLabel} cleared`,
        err?.message || `Error clearing ${sectionLabel.toLowerCase()}`,
      );
    } finally {
      LoaderService.hide();
    }
  };

  const storePrompt = async (
    _candidateUserId: string,
    promptResponse: any,
    promptType: PromptGeneraterName,
  ) => {
    LoaderService.show();
    try {
      const data = {
        userId: userDetails.userId,
        candidateUserId: _candidateUserId,
        promptResponse: promptResponse,
        promptType: promptType,
      };
      const response = await PostStorePrompt(data);

      if (response.message.includes("saved as eliminate")) {
        // removeRowByUserId(_candidateUserId);
        setMatchMakingStep(1);
        setShowMatchMakingModal(false);
        window.location.reload();
      }

      if (response.status === "success") {
        LoaderService.hide();
        return true;
      } else {
        LoaderService.hide();
        return false;
      }
    } catch (error: any) {
      LoaderService.hide();
      return false;
    }
  };

  const handleRowClick = async (row: any, classtype: string) => {
    if (classtype === classType.astroProfile.toString()) {
      await handleShowMatchReport(row.userId);
    } else if (
      classtype === classType.machingProfile.toString() ||
      classtype === classType.rejectProfile.toString()
    ) {
      await handleFetchBothProfileDetails(row.userId);
    }
  };

  const handleShowMatchReport = async (candidateId: string) => {
    try {
      LoaderService.show();
      setCandidateUserId(candidateId);

      // Fetch both reports in parallel
      const [forwardRes, reverseRes] = await Promise.all([
        GetMatchedProfile(userIdParams || userDetails.userId, candidateId),
        GetMatchedProfile(candidateId, userIdParams || userDetails.userId),
      ]);

      if (forwardRes.status === "success" || forwardRes.status === true) {
        setAstroModalData(forwardRes.data);

        if (reverseRes.status === "success" || reverseRes.status === true) {
          setOppositeAstroModalData(reverseRes.data);
        }

        setShowAstroModal(true);
      } else {
        toast.error(forwardRes.message || "Failed to fetch match report");
      }
    } catch (error) {
      console.error("Error fetching match report:", error);
      toast.error("Failed to fetch match report");
    } finally {
      LoaderService.hide();
    }
  };

  const handleOpenAstrologyDetails = async (row: any) => {
    const currentUserId = userIdParams || userDetails.userId;
    const candidateId = row?.userId;

    if (!currentUserId || !candidateId) {
      toast.error("User or candidate id is missing");
      return;
    }

    setAstrologyDetailsModal({
      open: true,
      loading: true,
      error: "",
      data: null,
      candidateName: row?.name || "",
    });

    try {
      const res: any = await PostMatchMakingAstrologyDetails({
        user_id: currentUserId,
        candidate_id: candidateId,
      });

      if (res?.error) {
        setAstrologyDetailsModal((prev) => ({
          ...prev,
          loading: false,
          error: res.error,
        }));
        return;
      }

      setAstrologyDetailsModal((prev) => ({
        ...prev,
        loading: false,
        data: res,
      }));
    } catch (error: any) {
      setAstrologyDetailsModal((prev) => ({
        ...prev,
        loading: false,
        error: error?.message || "Failed to fetch astrology details",
      }));
    }
  };

  const handlePhotoComparison = () => {
    setShowPhotoComparison(true);
    setUserPhotoZoom(1);
    setMatchedPhotoZoom(1);
    setUserPhotoPos({ x: 0, y: 0 });
    setMatchedPhotoPos({ x: 0, y: 0 });
  };

  const handleMouseDown = (e: React.MouseEvent, isUser: boolean) => {
    setIsDragging((prev) => ({ ...prev, [isUser ? "user" : "matched"]: true }));
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e: React.MouseEvent, isUser: boolean) => {
    if (!isDragging[isUser ? "user" : "matched"]) return;
    e.preventDefault();

    const deltaX = (e.clientX - dragStart.x) * 0.5;
    const deltaY = (e.clientY - dragStart.y) * 0.5;

    setUserPhotoPos((prev) => ({ x: prev.x + deltaX, y: prev.y + deltaY }));
    setMatchedPhotoPos((prev) => ({ x: prev.x + deltaX, y: prev.y + deltaY }));

    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setIsDragging({ user: false, matched: false });
  };

  const handlePhotoWheel = (e: React.WheelEvent, isUser: boolean) => {
    e.preventDefault();
    e.stopPropagation();

    const delta = e.deltaY > 0 ? -0.05 : 0.05;
    const newZoom = Math.max(
      0.5,
      Math.min(10, (isUser ? userPhotoZoom : matchedPhotoZoom) + delta),
    );
    setUserPhotoZoom(newZoom);
    setMatchedPhotoZoom(newZoom);
  };

  const handleSendSpecificTemplate = async () => {
    try {
      const response = await fetch(`${"https://api.v2.production.smartmatrimony.ai"}/admin/send-user-template`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          user_id: userDetails.userId,
          template_name: "call_status_reminder",
          template_language: "en"
        }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || "Template sent successfully");
      } else {
        toast.error(data.detail || "Failed to send template");
      }
    } catch (error) {
      toast.error("An error occurred while sending template");
    }
  };

  const quickActionItems = [
    {
      label: "Missed Call Follow-up",
      description: "Send a WhatsApp message asking the user to call or reply when available.",
      icon: MessageSquare,
      tone: "purple",
      onClick: handleSendSpecificTemplate,
    },
    {
      label: "Send Match Pdf",
      description: "Generate and send the latest match PDF",
      icon: FileText,
      tone: "indigo",
      onClick: () => setConfirmAddWhatsappPdfModal(true),
      disabled: matchesTotals?.published_matches === 0,
    },
    {
      label: "Add payment",
      description: "Add credits or a new payment entry",
      icon: PlusCircle,
      tone: "emerald",
      onClick: () => setConfirmAddPaymentModal(true),
    },
    {
      label: "Hold User",
      description: "Pause this user for a selected duration",
      icon: PauseCircle,
      tone: "blue",
      onClick: () => {
        setHoldStartedAt(new Date().toISOString());
        setHoldReason("");
        setHoldUserModal(true);
      },
    },
    {
      label:
        userDetails.status?.toLowerCase() === "active"
          ? "Disable Profile"
          : "Enable Profile",
      description:
        userDetails.status?.toLowerCase() === "active"
          ? "Temporarily disable this profile"
          : "Bring this profile back online",
      icon: userDetails.status?.toLowerCase() === "active" ? UserX : UserCheck,
      tone: userDetails.status?.toLowerCase() === "active" ? "amber" : "blue",
      onClick: () =>
        userDetails.status?.toLowerCase() === "active"
          ? setConfirmDisableModal(true)
          : setConfirmEnableModal(true),
    },
    {
      label: "Reset Password",
      description: "Set a new password for this profile",
      icon: Edit2,
      tone: "orange",
      onClick: () => setResetPasswordModal(true),
    },
    {
      label: "Reset Data",
      description: "Clear generated profile data and sync again",
      icon: RefreshCw,
      tone: "rose",
      onClick: () => setConfirmResetMatchingModal(true),
    },
    {
      label: "Deactivated Users",
      description: "Review matched users that were deactivated",
      icon: RefreshCw,
      tone: "pink",
      onClick: () => setDeactivatedMatchesModalOpen(true),
    },
    {
      label: "History",
      description: "Open the user activity timeline",
      icon: History,
      tone: "slate",
      onClick: () => {
        setHistoryModalOpen(true);
        fetchUserHistory(userDetails.userId);
      },
    },
  ];

  return (
    <div className="min-h-screen bg-transparent text-slate-100 selection:bg-blue-500/30">
      {/* Top Header / Action Bar */}
      <div className="flex flex-col md:flex-row items-center justify-between p-6 border-b border-slate-800 bg-slate-950/50 sticky top-0 z-10 backdrop-blur-md">
        <div className="flex items-center gap-4 mb-4 md:mb-0">
          <button
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors text-slate-400 group"
            onClick={() => {
              if (location.state?.from === "transactions") {
                navigate("/transactions");
              } else if (location.state?.from === "dashboard") {
                navigate("/dashboard");
              } else if (location.state?.from === "whatsapp") {
                navigate("/whatsapp");
              } else {
                // Use navigate(-1) to preserve the previous page's state/params
                navigate(-1);
              }
            }}
            title="Back to list"
          >
            <span className="group-hover:-translate-x-1 inline-block transition-transform">
              <ArrowLeftCircle />
            </span>
          </button>
          <div className="h-8 w-px bg-slate-800 mx-2 hidden md:block"></div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-white leading-none flex items-center gap-2">
                {userDetails.name || "User Profile"}
                {userDetails.isPremium && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-400 text-[10px] font-black uppercase tracking-widest shadow-[0_0_12px_rgba(245,158,11,0.15)]">
                    ⭐ Premium
                  </span>
                )}
                {userDetails.verified ? (
                  <Tooltip title="Verified" placement="top" arrow>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-blue-500 flex-shrink-0"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </Tooltip>
                ) : (
                  (() => {
                    const isComplete = (userDetails.progress || 0) >= 100;
                    return (
                      <Tooltip
                        title={
                          isComplete
                            ? "Pending Verification - Click to Verify"
                            : "Profile must be 100% complete to verify"
                        }
                        placement="top"
                        arrow
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isComplete) setConfirmVerifyModal(true);
                          }}
                          disabled={!isComplete}
                          className={`transition-all flex-shrink-0 ${isComplete ? "hover:scale-110 cursor-pointer text-slate-600 hover:text-yellow-500" : "cursor-not-allowed opacity-50 text-slate-700"}`}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                            />
                          </svg>
                        </button>
                      </Tooltip>
                    );
                  })()
                )}
              </h1>
              {userDetails.gender && (
                <span
                  className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${userDetails.gender === "Male" ? "bg-blue-500/10 text-blue-400 border border-blue-500/20" : "bg-pink-500/10 text-pink-400 border border-pink-500/20"}`}
                >
                  {userDetails.gender}
                </span>
              )}

              {/* Sammelan Assignment Dropdown */}
              <div className="relative group ml-1 flex items-center bg-slate-900/60 border border-white/10 group-hover:border-indigo-500/30 rounded-2xl px-3 py-1.5 transition-all shadow-xl backdrop-blur-xl ring-1 ring-white/5 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-tr from-indigo-500/5 to-transparent pointer-events-none" />
                <Calendar
                  size={13}
                  className="text-indigo-400/80 mr-2 shrink-0"
                />
                <div className="relative flex items-center">
                  <select
                    className="bg-transparent border-none text-[10px] font-black text-slate-300 uppercase tracking-widest focus:outline-none cursor-pointer appearance-none pr-5 min-w-[120px] relative z-10"
                    value={selectedSammelanEvent}
                    onChange={(e) => setSelectedSammelanEvent(e.target.value)}
                    title="Select Sammelan"
                  >
                    <option value="" className="bg-slate-950 text-slate-500">
                      Pick Event
                    </option>
                    {sammelanEvents.map((event: any) => (
                      <option
                        key={event.id}
                        value={event.id}
                        className="bg-slate-950 text-slate-200"
                      >
                        {event.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={10}
                    className="text-slate-500 absolute right-0 pointer-events-none"
                  />
                </div>
                <div className="w-[1px] h-4 bg-white/10 mx-2" />
                <button
                  onClick={handleAssignToSammelan}
                  disabled={!selectedSammelanEvent || isAssigningSammelan}
                  className={`p-1 rounded-lg transition-all flex items-center justify-center relative ${!selectedSammelanEvent || isAssigningSammelan
                    ? "text-slate-700 opacity-20"
                    : "text-indigo-400 hover:text-white hover:bg-indigo-600/40 hover:scale-110 active:scale-95 group/btn shadow-[0_0_10px_rgba(79,70,229,0.1)]"
                    }`}
                  title="Assign User to Event"
                >
                  <PlusCircle size={15} strokeWidth={2.5} />
                  {selectedSammelanEvent && !isAssigningSammelan && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-indigo-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(79,70,229,0.6)]" />
                  )}
                </button>
              </div>

              {userProfileData.sammelanRegistrations &&
                userProfileData.sammelanRegistrations.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2 items-center">
                    {userProfileData.sammelanRegistrations.map(
                      (reg: any, idx: number) => {
                        const eventId = Number(
                          reg.sammelan_event_id ?? reg.event_id ?? reg.id ?? 0,
                        );
                        return (
                          <div
                            key={`${reg.event_name}-${idx}`}
                            className={`flex items-center gap-2 rounded-full border border-white/10 ${reg.is_registered ? "bg-slate-900/80" : "bg-slate-900/40 opacity-60"} px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-200 shadow-sm`}
                          >
                            <span className="font-black text-slate-200">
                              {reg.event_name}
                            </span>
                            {reg.is_registered && (
                              <button
                                type="button"
                                onClick={() =>
                                  openRemoveSammelanModal(
                                    eventId,
                                    reg.event_name ||
                                    reg.name ||
                                    "Sammelan Event",
                                  )
                                }
                                className="p-1 rounded-full text-slate-400 hover:text-white hover:bg-rose-500/10 transition"
                                title={`Remove ${reg.event_name}`}
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        );
                      },
                    )}
                  </div>
                )}
            </div>
            <p className="text-xs text-slate-500 mt-1 uppercase tracking-tighter">
              Account ID: {userDetails.userId}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <div ref={actionMenuRef} className="relative sm:static">
            <button
              type="button"
              onClick={() => setIsActionMenuOpen((prev) => !prev)}
              className="group relative flex h-12 w-[min(100%,180px)] items-center justify-between overflow-hidden rounded-2xl border border-white/10 bg-slate-950/80 shadow-[0_16px_50px_rgba(0,0,0,0.35)] backdrop-blur-xl transition-all duration-300 hover:border-indigo-500/30 hover:shadow-[0_18px_60px_rgba(79,70,229,0.18)] sm:h-14 sm:w-auto sm:min-w-[150px]"
              style={{ perspective: "900px" }}
            >
              <span className="absolute inset-0 bg-gradient-to-br from-indigo-500/15 via-slate-950 to-cyan-500/10 opacity-90" />
              <span className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.16),transparent_48%)] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              <span
                className="relative z-10 block h-full w-full transition-transform duration-500"
                style={{
                  transform: `rotateY(${isActionMenuOpen ? 180 : 0}deg)`,
                  transformStyle: "preserve-3d",
                }}
              >
                <span
                  className="absolute inset-0 flex items-center justify-between gap-3 px-2 sm:px-3"
                  style={{ backfaceVisibility: "hidden" }}
                >
                  <span className="flex items-center gap-2.5">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5 shadow-inner shadow-black/20">
                      <Layers
                        size={16}
                        className={`text-indigo-300 transition-transform duration-300 ${isActionMenuOpen ? "rotate-12 scale-110" : "rotate-0"}`}
                      />
                    </span>
                    <span className="flex flex-col items-start leading-none whitespace-nowrap">
                      <span className="text-[9px] font-black uppercase tracking-[0.35em] text-slate-400">
                        Action
                      </span>
                      <span className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-100 mt-1">
                        Menu
                      </span>
                    </span>
                  </span>
                  <ChevronDown size={14} className="text-slate-400 shrink-0 mr-1 sm:mr-1.5" />
                </span>

                <span
                  className="absolute inset-0 flex items-center justify-between gap-3 px-2 sm:px-3"
                  style={{
                    transform: "rotateY(180deg)",
                    backfaceVisibility: "hidden",
                  }}
                >
                  <span className="flex items-center gap-2.5">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5 shadow-inner shadow-black/20">
                      <X size={15} className="text-slate-100" />
                    </span>
                    <span className="flex flex-col items-start leading-none whitespace-nowrap">
                      <span className="text-[9px] font-black uppercase tracking-[0.35em] text-slate-400">
                        Action
                      </span>
                      <span className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-100 mt-1">
                        Close
                      </span>
                    </span>
                  </span>
                  <ChevronDown
                    size={14}
                    className="rotate-180 text-slate-400 shrink-0 mr-1 sm:mr-1.5"
                  />
                </span>
              </span>
            </button>

            <div
              className={`fixed inset-x-3 bottom-3 z-50 max-h-[70vh] overflow-hidden rounded-3xl border border-white/10 bg-slate-950/95 p-3 shadow-[0_24px_90px_rgba(0,0,0,0.5)] backdrop-blur-xl transition-all duration-300 sm:absolute sm:left-1/2 sm:top-[calc(100%+12px)] sm:bottom-auto sm:inset-x-auto sm:w-[min(92vw,560px)] sm:-translate-x-1/2 sm:max-h-none sm:overflow-visible sm:p-4 ${isActionMenuOpen
                ? "pointer-events-auto translate-y-0 scale-100 opacity-100"
                : "pointer-events-none translate-y-4 scale-95 opacity-0"
                }`}
            >
              <div className="flex items-start justify-between gap-3 border-b border-white/5 pb-3">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.35em] text-slate-400">
                    Quick Actions
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    Tap an option to open its workflow
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsActionMenuOpen(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-slate-400 transition hover:border-white/20 hover:bg-white/10 hover:text-white"
                  aria-label="Close quick actions"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="mt-3 grid max-h-[calc(70vh-5.5rem)] grid-cols-1 gap-2 overflow-y-auto pr-1 sm:max-h-none sm:grid-cols-2 sm:overflow-visible">
                {quickActionItems.map((item) => {
                  const Icon = item.icon;
                  const toneClasses =
                    quickActionToneClasses[
                    item.tone as keyof typeof quickActionToneClasses
                    ];

                  return (
                    <button
                      key={item.label}
                      type="button"
                      disabled={item.disabled}
                      onClick={() => {
                        setIsActionMenuOpen(false);
                        item.onClick();
                      }}
                      className={`group flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-3 text-left transition-all duration-200 ${item.disabled ? "opacity-50 cursor-not-allowed" : "hover:-translate-y-0.5 hover:border-white/20 hover:bg-white/[0.06]"}`}
                    >
                      <span
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border ${toneClasses} transition-transform duration-200 ${item.disabled ? "" : "group-hover:scale-105"}`}
                      >
                        <Icon size={16} />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[10px] font-black uppercase tracking-[0.22em] text-slate-100 sm:text-[11px]">
                          {item.label}
                        </span>
                        <span className="mt-1 block text-[10px] leading-snug text-slate-500 sm:text-[11px]">
                          {item.description}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1700px] mx-auto p-6 space-y-6">
        {/* Horizontal User Info Bar */}
        <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-800 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-6 shadow-2xl animate-fade-in">
          <div className="flex items-center gap-8 flex-wrap">
            {/* City & State */}
            <div className="flex items-center gap-3 group">
              <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center border border-indigo-500/20 group-hover:bg-indigo-500/20 transition-all">
                <MapPin size={18} className="text-indigo-400" />
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest leading-none mb-1">
                  Location
                </p>
                <p className="text-sm font-bold text-slate-200">
                  {userDetails.city || "N/A"}, {userDetails.state || "N/A"}
                </p>
              </div>
            </div>

            <div className="h-10 w-px bg-slate-800 hidden md:block"></div>

            {/* Manglik Status */}
            <div className="flex items-center gap-3 group">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center border border-amber-500/20 group-hover:bg-amber-500/20 transition-all">
                <Compass size={18} className="text-amber-400" />
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest leading-none mb-1">
                  Is Manglik?
                </p>
                <p className="text-sm font-bold text-slate-200">
                  {userDetails.manglik || "N/A"}
                </p>
              </div>
            </div>

            <div className="h-10 w-px bg-slate-800 hidden md:block"></div>

            {/* Profession */}
            <div className="flex items-center gap-3 group">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20 group-hover:bg-emerald-500/20 transition-all">
                <Briefcase size={18} className="text-emerald-400" />
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest leading-none mb-1">
                  Profession
                </p>
                <p className="text-sm font-bold text-slate-200">
                  {userDetails.occupation || "N/A"}
                  {userDetails.working_with && (
                    <span className="text-[10px] text-slate-500 ml-2 font-medium">
                      ({userDetails.working_with.replace(/_/g, " ")})
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className="h-10 w-px bg-slate-800 hidden md:block"></div>

            {/* Qualification */}
            <div className="flex items-center gap-3 group">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center border border-purple-500/20 group-hover:bg-purple-500/20 transition-all">
                <GraduationCap size={18} className="text-purple-400" />
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest leading-none mb-1">
                  Education
                </p>
                <p className="text-sm font-bold text-slate-200">
                  {userDetails.qualification || "N/A"}
                </p>
              </div>
            </div>

            <div className="h-10 w-px bg-slate-800 hidden md:block"></div>

            {/* Salary Range */}
            <div className="flex items-center gap-3 group">
              <div className="w-10 h-10 rounded-xl bg-rose-500/10 flex items-center justify-center border border-rose-500/20 group-hover:bg-rose-500/20 transition-all">
                <Banknote size={18} className="text-rose-400" />
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest leading-none mb-1">
                  Annual Salary
                </p>
                <div className="flex items-baseline gap-1.5">
                  {userDetails.min_income &&
                    userDetails.min_income !== "N/A" ? (
                    <span className="text-sm font-bold text-slate-200">
                      {userDetails.min_income} -{" "}
                      {userDetails.max_income || userDetails.min_income}
                    </span>
                  ) : (
                    <span className="text-sm font-bold text-slate-400 italic opacity-60">
                      Not Disclosed
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* LEFT COLUMN: Metadata & Health */}
          {activeTab === "Full Profile" && (
            <div className="lg:col-span-3 space-y-6 animate-slide-in-left">
              <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-visible shadow-2xl relative group/photo-card">
                <div className="aspect-square bg-slate-950 flex items-center justify-center text-4xl font-bold text-slate-700 rounded-t-2xl overflow-hidden relative border-b border-slate-800">
                  {userDetails.photo ? (
                    <img
                      src={userDetails.photo}
                      alt={userDetails.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover/photo-card:scale-105"
                    />
                  ) : userDetails.name ? (
                    userDetails.name[0]
                  ) : (
                    "U"
                  )}
                  <div className="absolute inset-0 bg-indigo-600/10 opacity-0 group-hover/photo-card:opacity-100 transition-opacity pointer-events-none"></div>

                  <button
                    type="button"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={isPhotoUploading}
                    title="Update profile photo"
                    className="absolute bottom-3 right-3 z-20 inline-flex items-center justify-center w-10 h-10 rounded-full bg-slate-950/90 border border-slate-700/70 text-indigo-300 shadow-lg backdrop-blur-sm hover:bg-slate-900 hover:text-white transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isPhotoUploading ? (
                      <RefreshCw size={16} className="animate-spin" />
                    ) : (
                      <Camera size={16} />
                    )}
                  </button>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                    onChange={handleProfilePhotoUpdate}
                  />

                  {/* Version Badge on Photo */}
                  <div className="absolute top-4 right-4 z-10 px-2.5 py-1 bg-slate-950/80 backdrop-blur-xl border border-white/10 rounded-full shadow-2xl flex items-center gap-1.5 group-hover/photo-card:scale-110 transition-transform">
                    <div className="w-1 h-1 rounded-full bg-indigo-400 animate-pulse"></div>
                    <span className="text-[10px] font-black text-white uppercase tracking-widest whitespace-nowrap">
                      v{userDetails.version || "1.0.0"}
                    </span>
                  </div>
                </div>

                {/* Dynamic Side Preview Panel */}
                {userDetails.photo && (
                  <div className="absolute left-[calc(100%+1rem)] top-0 w-[350px] aspect-[3/4] rounded-[2rem] overflow-hidden border border-slate-700/50 shadow-[0_0_50px_rgba(0,0,0,0.5)] opacity-0 -translate-x-4 pointer-events-none group-hover/photo-card:opacity-100 group-hover/photo-card:translate-x-0 transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] z-[100] hidden lg:block bg-slate-900/90 backdrop-blur-xl">
                    <img
                      src={userDetails.photo}
                      className="w-full h-full object-contain"
                      alt="Full View"
                    />
                    <div className="absolute bottom-0 inset-x-0 p-6 bg-gradient-to-t from-slate-950 via-slate-950/80 to-transparent">
                      <span className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.3em] block mb-1">
                        Full Perspective
                      </span>
                      <span className="text-xs text-white font-bold opacity-80 italic">
                        Uncropped Image View
                      </span>
                    </div>
                  </div>
                )}
                <div className="p-5 space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-medium text-slate-500 uppercase tracking-wider text-[10px]">
                      Status
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${userDetails.status === "Active" ? "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20" : "bg-red-500/10 text-red-500 border border-red-500/20"}`}
                    >
                      {userDetails.status || "Unknown"}
                    </span>
                  </div>

                  <div className="pt-2">
                    <div className="text-[10px] font-bold text-indigo-400 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                      <Zap size={12} className="text-amber-500" /> Wallet
                      Overview
                    </div>
                    <div className="space-y-4 bg-slate-950/40 p-4 rounded-2xl border border-white/[0.03]">
                      <div className="flex justify-between items-center px-1">
                        <div className="flex flex-col">
                          <span className="text-[10px] text-emerald-400 font-black uppercase tracking-widest">
                            Current Balance
                          </span>
                          <span className="text-[9px] text-slate-500 font-bold uppercase">
                            Available to use
                          </span>
                        </div>
                        <div className="text-2xl font-black text-white font-mono">
                          {userDetails.totalCredits || 0}
                        </div>
                      </div>

                      <div className="w-full bg-slate-800/50 h-2 rounded-full overflow-hidden">
                        <div
                          className="bg-emerald-500 h-full rounded-full transition-all duration-700 ease-out"
                          style={{
                            width: `${Math.min(100, (userDetails.totalCredits / (userDetails.totalCredits + userDetails.creditsUsed || 1)) * 100)}%`,
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-800 space-y-3">
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500 font-bold uppercase tracking-wider text-[9px]">
                        Age
                      </span>
                      <span className="text-slate-200 font-mono font-bold">
                        {userDetails.age || "N/A"} yrs
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500 font-bold uppercase tracking-wider text-[9px]">
                        Platform
                      </span>
                      <span className="text-indigo-400 font-black uppercase tracking-widest text-[10px] italic">
                        {userDetails.platform || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500 font-bold uppercase tracking-wider text-[9px]">
                        Location
                      </span>
                      <span className="text-slate-200">
                        {userDetails.location ||
                          `${userDetails.city || ""}, ${userDetails.state || ""}`
                            .trim()
                            .replace(/^, |, $/g, "") ||
                          "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500 font-bold uppercase tracking-wider text-[9px]">
                        Gender
                      </span>
                      <span className="text-slate-200">
                        {userDetails.gender || userDetails.Gender || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500 font-bold uppercase tracking-wider text-[9px]">
                        Created
                      </span>
                      <span className="text-slate-200">
                        {userDetails.createdAt
                          ? new Date(userDetails.createdAt).toLocaleDateString()
                          : "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500 font-bold uppercase tracking-wider text-[9px]">
                        Days Ago
                      </span>
                      <span className="text-slate-200 font-mono">
                        {userDetails.createdAt
                          ? Math.floor(
                            (Date.now() -
                              new Date(userDetails.createdAt).getTime()) /
                            (1000 * 60 * 60 * 24),
                          )
                          : "N/A"}{" "}
                        days
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-4">
                  Quick Diagnostic
                </div>
                <div className="space-y-2">
                  <button
                    className="w-full flex items-center justify-between p-3 bg-slate-800 hover:bg-slate-700 text-white rounded-xl border border-slate-700 transition-all"
                    onClick={handleShowPassword}
                  >
                    <span className="text-xs font-bold">
                      {password
                        ? `Pass: ${password}`
                        : "Security: Reveal Password"}
                    </span>
                    <span className="text-[10px] opacity-30 text-white uppercase font-black">
                      Decrypt
                    </span>
                  </button>

                  {/* TEMPORARY ACCESS - COMMENTED OUT
                <div className="pt-2 border-t border-slate-800/50 mt-2">
                    {!tempPassword ? (
                       <button
                          className="w-full flex items-center justify-center gap-2 p-3 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 rounded-xl border border-indigo-500/20 transition-all text-xs font-black uppercase tracking-widest"
                          onClick={handleGenerateTempPassword}
                       >
                          <Zap size={14} className="text-amber-400" />
                          Generate Temporary Pass
                       </button>
                    ) : (
                       <div className="bg-slate-950 p-3 rounded-xl border border-indigo-500/30 flex flex-col gap-2">
                          <div className="flex justify-between items-center">
                             <span className="text-[10px] text-slate-500 font-black uppercase tracking-widest">Temporary Access</span>
                             <span className="text-[11px] font-mono font-bold text-amber-500 bg-amber-500/10 px-2 py-0.5 rounded-full ring-1 ring-amber-500/20">
                                {formatTime(timer)}
                             </span>
                          </div>
                          <div className="flex justify-between items-center bg-white/5 p-2 rounded-lg border border-white/5">
                             <span className="text-sm font-black text-white font-mono tracking-wider selection:bg-indigo-500/30">{tempPassword}</span>
                             <button 
                                onClick={() => {
                                   navigator.clipboard.writeText(tempPassword);
                                   toast.success("Password copied!");
                                }}
                                className="text-[10px] text-indigo-400 font-black uppercase hover:text-indigo-300 transition-colors"
                             >
                                Copy
                             </button>
                          </div>
                          <p className="text-[9px] text-slate-500 italic leading-tight">
                             Valid for 30 minutes. User can use this or their permanent password.
                          </p>
                       </div>
                    )}
                 </div>
                 */}
                </div>

                {/* Admin Instructions Section */}
                <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 mt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <FileText size={12} className="text-slate-500" />
                      <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                        Admin Instructions
                      </span>
                      {(userDetails.adminInstructions?.length || 0) > 0 && (
                        <span className="text-[9px] font-black text-indigo-400 bg-indigo-500/10 px-1.5 py-0.5 rounded border border-indigo-500/20">
                          {userDetails.adminInstructions?.length}
                        </span>
                      )}
                    </div>
                    {!isEditingNote ? (
                      <button
                        onClick={() => {
                          setIsEditingNote(true);
                          setTempNote("");
                          setIsAddingOthers(false);
                        }}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 bg-indigo-500/10 hover:bg-indigo-500/20 rounded-lg text-indigo-400 transition-all border border-indigo-500/20 text-[9px] font-black uppercase tracking-widest"
                        title="Add Instruction"
                      >
                        <PlusCircle size={11} />
                        Add
                      </button>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setIsEditingNote(false);
                            setIsAddingOthers(false);
                          }}
                          className="p-1.5 bg-rose-500/10 hover:bg-rose-500/20 rounded-lg text-rose-500 transition-all border border-rose-500/20"
                          title="Cancel"
                        >
                          <X size={12} />
                        </button>
                        <button
                          onClick={async () => {
                            if (!tempNote.trim()) {
                              toast.error("Instruction cannot be empty");
                              return;
                            }
                            try {
                              LoaderService.show();
                              const response = await PostUpdateUserInstructions(
                                {
                                  user_id: userDetails.userId,
                                  instructions: tempNote,
                                },
                              );
                              if (response.status === true) {
                                const newInstruction = {
                                  instruction: tempNote,
                                  date: new Date().toISOString(),
                                };
                                setUserDetails((prev: any) => ({
                                  ...prev,
                                  adminNote: tempNote,
                                  adminInstructions: [
                                    ...(prev.adminInstructions || []),
                                    newInstruction,
                                  ],
                                }));

                                // Auto-assign if CUSTOMER_SUPPORT or CALLING_TEAM_LEAD
                                if (
                                  (currentUserRole === "CUSTOMER_SUPPORT" ||
                                    currentUserRole === "CALLING_TEAM_LEAD") &&
                                  currentUsername
                                ) {
                                  try {
                                    await PatchAssignUserInstruction(
                                      userDetails.userId,
                                      currentUsername,
                                    );
                                    toast.success(
                                      "Profile automatically assigned to you",
                                    );
                                    // Update local state to show assigned to
                                    setUserDetails((prev: any) => ({
                                      ...prev,
                                      assigned_to: currentUsername,
                                    }));
                                  } catch (assignError) {
                                    console.error(
                                      "Auto-assignment failed",
                                      assignError,
                                    );
                                  }
                                }

                                setIsEditingNote(false);
                                setTempNote("");
                                setIsAddingOthers(false);
                                toast.success(
                                  response.message ||
                                  "Instruction added successfully",
                                );
                              } else {
                                toast.error(
                                  response.message ||
                                  "Failed to add instruction",
                                );
                              }
                            } catch (error) {
                              toast.error("Error adding instruction");
                            } finally {
                              LoaderService.hide();
                            }
                          }}
                          className="p-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 rounded-lg text-emerald-500 transition-all border border-emerald-500/20"
                          title="Save Instruction"
                        >
                          <Check size={12} />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Add new instruction form */}
                  {isEditingNote && (
                    <div className="mb-4 space-y-3">
                      <select
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-xs text-slate-300 focus:outline-none focus:ring-1 focus:ring-indigo-500/50 font-medium"
                        value={isAddingOthers ? "others" : tempNote}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "others") {
                            setIsAddingOthers(true);
                            setTempNote("");
                          } else {
                            setIsAddingOthers(false);
                            setTempNote(value);
                          }
                        }}
                      >
                        <option value="" disabled>
                          Select an instruction...
                        </option>
                        {dropdownNotes.map((note, index) => (
                          <option key={index} value={note}>
                            {note}
                          </option>
                        ))}
                        <option value="others">Add Others</option>
                      </select>

                      {isAddingOthers && (
                        <textarea
                          className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-xs text-slate-300 focus:outline-none focus:ring-1 focus:ring-indigo-500/50 min-h-[80px] resize-none font-medium leading-relaxed"
                          placeholder="Add a new instruction for this user..."
                          value={tempNote}
                          onChange={(e) => setTempNote(e.target.value)}
                          autoFocus
                        />
                      )}
                    </div>
                  )}

                  {/* Instructions Timeline */}
                  {(userDetails.adminInstructions?.length || 0) > 0 ? (
                    <div className="space-y-0 max-h-[300px] overflow-y-auto custom-scrollbar">
                      {[...(userDetails.adminInstructions || [])]
                        .sort(
                          (a: any, b: any) =>
                            new Date(b.date).getTime() -
                            new Date(a.date).getTime(),
                        )
                        .map((item: any, idx: number) => (
                          <div
                            key={idx}
                            className="relative flex gap-3 group/item"
                          >
                            {/* Timeline dot */}
                            <div className="flex flex-col items-center">
                              <div className="w-2 h-2 rounded-full bg-indigo-500/60 border border-indigo-400/30 mt-1.5 shrink-0 z-10" />
                              {idx <
                                (userDetails.adminInstructions?.length || 0) -
                                1 && (
                                  <div className="w-px flex-1 bg-slate-800 min-h-[20px]" />
                                )}
                            </div>
                            {/* Content */}
                            <div className="pb-4 flex-1 min-w-0">
                              <p className="text-xs text-slate-300 leading-relaxed font-medium break-words">
                                {item.instruction}
                              </p>
                              <span className="text-[9px] text-slate-600 font-bold uppercase tracking-tighter mt-1 inline-block">
                                {new Date(item.date).toLocaleDateString([], {
                                  day: "2-digit",
                                  month: "short",
                                  year: "2-digit",
                                })}{" "}
                                &bull;{" "}
                                {new Date(item.date).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : (
                    !isEditingNote && (
                      <div
                        className="bg-slate-950/50 rounded-xl p-4 border border-slate-800/50 text-center cursor-pointer hover:bg-slate-950/80 transition-all"
                        onClick={() => {
                          setIsEditingNote(true);
                          setTempNote("");
                        }}
                      >
                        <p className="text-[10px] text-slate-600 italic font-medium">
                          No instructions yet. Click to add one...
                        </p>
                      </div>
                    )
                  )}
                  <div className="mt-3 flex items-center gap-1.5 opacity-40">
                    <Info size={10} className="text-slate-500" />
                    <span className="text-[9px] font-bold text-slate-600 uppercase tracking-tighter">
                      Only visible to administrators
                    </span>
                  </div>
                </div>
              </div>
              {/* Special Preference Notes */}
              <div className="w-full">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2 block">
                  Special Preference Notes
                </label>

                {/* Existing saved notes */}
                {Object.keys(specialPreferenceNotes).length > 0 && (
                  <div className="mb-3 space-y-2">
                    {Object.entries(specialPreferenceNotes).map(
                      ([key, values]: [string, any]) => (
                        <div
                          key={key}
                          className="bg-slate-900/60 border border-slate-700/50 rounded-xl p-2.5"
                        >
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[9px] font-black text-indigo-400 uppercase tracking-widest">
                              {key === "excludes"
                                ? "Excludes"
                                : key === "includes"
                                  ? "Includes"
                                  : key}
                            </span>
                            <button
                              onClick={() =>
                                handleClearSpecialPreferenceSection(
                                  key === "excludes" ? "excludes" : "includes",
                                )
                              }
                              className="text-[8px] text-rose-400 hover:text-rose-300 font-black uppercase tracking-widest"
                            >
                              Clear
                            </button>
                          </div>
                          <div className="space-y-2">
                            {Array.isArray(values) && (
                              <div className="flex flex-wrap gap-1">
                                {values.map((v: string) => (
                                  <span
                                    key={v}
                                    className="flex items-center gap-1 px-2 py-0.5 bg-indigo-500/10 border border-indigo-500/20 rounded-lg text-[10px] text-indigo-300 font-bold"
                                  >
                                    {v}
                                    <button
                                      onClick={() =>
                                        handleRemoveSpecialPreferenceKey(
                                          key,
                                          v,
                                          "includes",
                                        )
                                      }
                                      className="text-indigo-400 hover:text-rose-400 transition-colors"
                                    >
                                      <X size={9} />
                                    </button>
                                  </span>
                                ))}
                              </div>
                            )}

                            {values &&
                              typeof values === "object" &&
                              !Array.isArray(values) && (
                                <div className="space-y-2">
                                  {Object.entries(values).map(
                                    ([subKey, subValues]: [string, any]) => (
                                      <div
                                        key={subKey}
                                        className="rounded-xl border border-slate-700/50 p-2 bg-slate-950/30"
                                      >
                                        <div className="text-[8px] text-slate-400 uppercase tracking-widest mb-1">
                                          {subKey}
                                        </div>
                                        <div className="flex flex-wrap gap-1">
                                          {Array.isArray(subValues) ? (
                                            subValues.map((v: string) => (
                                              <span
                                                key={v}
                                                className={`flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold ${key === "excludes"
                                                  ? "bg-rose-500/10 border border-rose-500/20 text-rose-300"
                                                  : "bg-emerald-500/10 border border-emerald-500/20 text-emerald-300"
                                                  }`}
                                              >
                                                {v}
                                                <button
                                                  onClick={() =>
                                                    handleRemoveSpecialPreferenceKey(
                                                      subKey,
                                                      v,
                                                      key === "excludes"
                                                        ? "excludes"
                                                        : "includes",
                                                    )
                                                  }
                                                  className={
                                                    key === "excludes"
                                                      ? "text-rose-300 hover:text-white transition-colors"
                                                      : "text-emerald-300 hover:text-white transition-colors"
                                                  }
                                                >
                                                  <X size={9} />
                                                </button>
                                              </span>
                                            ))
                                          ) : (
                                            <span className="px-2 py-0.5 bg-slate-800 rounded-lg text-[10px] text-slate-300">
                                              {String(subValues)}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    ),
                                  )}
                                </div>
                              )}
                          </div>
                        </div>
                      ),
                    )}
                    <button
                      onClick={handleDeleteSpecialPreference}
                      className="w-full py-1.5 text-[9px] font-black uppercase tracking-widest text-rose-400 hover:text-rose-300 border border-rose-500/20 hover:border-rose-500/40 rounded-xl transition-all"
                    >
                      Clear All Preferences
                    </button>
                  </div>
                )}

                {/* Add new preference */}
                <div
                  className={`rounded-2xl p-3 space-y-3 transition-all duration-300 ${specialPreferenceMode === "includes"
                    ? "border border-emerald-500/20 bg-emerald-500/5 shadow-[0_0_40px_rgba(16,185,129,0.08)]"
                    : "border border-rose-500/20 bg-rose-500/5 shadow-[0_0_40px_rgba(244,63,94,0.08)]"
                    }`}
                >
                  <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-800 bg-slate-900/60 p-1">
                    {[
                      { value: "includes", label: "Include" },
                      { value: "excludes", label: "Exclude" },
                    ].map((mode) => (
                      <button
                        key={mode.value}
                        type="button"
                        onClick={() =>
                          setSpecialPreferenceMode(
                            mode.value as "includes" | "excludes",
                          )
                        }
                        className={`flex-1 rounded-lg px-3 py-2 text-[10px] font-black uppercase tracking-widest transition-all ${specialPreferenceMode === mode.value
                          ? mode.value === "includes"
                            ? "bg-emerald-500/20 text-emerald-100 border border-emerald-400/50 shadow-[0_0_20px_rgba(16,185,129,0.25)]"
                            : "bg-rose-500/20 text-rose-100 border border-rose-400/50 shadow-[0_0_20px_rgba(244,63,94,0.25)]"
                          : "text-slate-500 hover:text-slate-300 bg-transparent border border-transparent"
                          }`}
                      >
                        {mode.label}
                      </button>
                    ))}
                  </div>
                  <div className="space-y-2 ">
                    <div className="flex items-center justify-between">
                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">
                        Choose Preference Types
                      </span>
                      {selectedSpecialPreference && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedSpecialPreference("");
                            resetSpecialPreferenceDraft();
                          }}
                          className="text-[8px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-300"
                        >
                          Reset
                        </button>
                      )}
                    </div>
                    <input
                      type="text"
                      placeholder="Search preference type..."
                      value={specialPreferenceSearch}
                      onChange={(e) =>
                        setSpecialPreferenceSearch(e.target.value)
                      }
                      className="w-full bg-slate-900/90 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/50"
                    />
                    <div className="flex flex-wrap gap-2">
                      {filteredSpecialPreferenceOptions.map((option) => (
                        <SelectionChip
                          key={option.value}
                          label={option.label}
                          active={selectedSpecialPreference === option.value}
                          onClick={() => {
                            setSelectedSpecialPreference(option.value);
                            resetSpecialPreferenceDraft();
                          }}
                        />
                      ))}
                    </div>
                  </div>

                  {selectedSpecialPreference && (
                    <div
                      className={`rounded-2xl p-3 space-y-3 transition-all duration-300 ${specialPreferenceMode === "includes"
                        ? "border border-emerald-500/20 bg-emerald-500/10 shadow-[0_0_30px_rgba(16,185,129,0.10)]"
                        : "border border-rose-500/20 bg-rose-500/10 shadow-[0_0_30px_rgba(244,63,94,0.10)]"
                        }`}
                    >
                      {" "}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-[9px] font-black text-indigo-300 uppercase tracking-[0.25em]">
                            Adding
                          </p>
                          <h4 className="text-sm font-black text-white mt-1">
                            {specialPreferenceOptions.find(
                              (opt) => opt.value === selectedSpecialPreference,
                            )?.label || "Preference"}
                          </h4>
                        </div>
                        <div className="text-right text-[9px] text-slate-400">
                          Quick chips for common picks
                        </div>
                      </div>
                      {selectedSpecialPreferenceValues.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">
                            Selected Values
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {selectedSpecialPreferenceValues.map((value) => (
                              <span
                                key={value}
                                className="flex items-center gap-1 px-2 py-1 bg-indigo-500/10 border border-indigo-500/20 rounded-lg text-[10px] text-indigo-200 font-bold"
                              >
                                {prettifyDropdownOption(value)}
                                <button
                                  type="button"
                                  onClick={() =>
                                    toggleSpecialPreferenceDraftValue(value)
                                  }
                                  className="text-indigo-300 hover:text-white transition-colors"
                                >
                                  <X size={10} />
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {selectedSpecialPreference === "country" && (
                        <div className="space-y-2">
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="Search country or type custom (e.g. Out of India, USA, Canada)..."
                              value={countrySearch}
                              onChange={(e) => setCountrySearch(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && countrySearch.trim()) {
                                  e.preventDefault();
                                  if (
                                    !selectedSpecialPreferenceValues.includes(
                                      countrySearch.trim(),
                                    )
                                  ) {
                                    toggleSpecialPreferenceDraftValue(
                                      countrySearch.trim(),
                                    );
                                  }
                                  setCountrySearch("");
                                }
                              }}
                              className="flex-1 bg-slate-900 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/50"
                            />
                            {countrySearch.trim() && (
                              <button
                                type="button"
                                onClick={() => {
                                  if (
                                    !selectedSpecialPreferenceValues.includes(
                                      countrySearch.trim(),
                                    )
                                  ) {
                                    toggleSpecialPreferenceDraftValue(
                                      countrySearch.trim(),
                                    );
                                  }
                                  setCountrySearch("");
                                }}
                                className="px-3 py-1 bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 rounded-xl text-xs font-semibold hover:bg-indigo-500/30"
                              >
                                Select
                              </button>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto pr-1">
                            {filteredCountryOptions.map((co) => (
                              <SelectionChip
                                key={co}
                                label={co}
                                active={selectedSpecialPreferenceValues.includes(
                                  co,
                                )}
                                onClick={() =>
                                  toggleSpecialPreferenceDraftValue(co)
                                }
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      {(selectedSpecialPreference === "location" || selectedSpecialPreference === "working_location") && (
                        <div className="space-y-2">
                          <input
                            type="text"
                            placeholder="Search locations or type custom location..."
                            value={locationSearch}
                            onChange={(e) => setLocationSearch(e.target.value)}
                            className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/50"
                          />
                          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto pr-1">
                            {filteredLocationOptions.map((loc) => (
                              <SelectionChip
                                key={loc}
                                label={loc}
                                active={selectedSpecialPreferenceValues.includes(
                                  loc,
                                )}
                                onClick={() =>
                                  toggleSpecialPreferenceDraftValue(loc)
                                }
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      {selectedSpecialPreference === "state" && (
                        <div className="space-y-2">
                          <input
                            type="text"
                            placeholder="Search states or type custom state..."
                            value={stateSearch}
                            onChange={(e) => setStateSearch(e.target.value)}
                            className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/50"
                          />
                          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto pr-1">
                            {filteredStateOptions.map((st) => (
                              <SelectionChip
                                key={st}
                                label={st}
                                active={selectedSpecialPreferenceValues.includes(
                                  st,
                                )}
                                onClick={() =>
                                  toggleSpecialPreferenceDraftValue(st)
                                }
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      {selectedSpecialPreference === "qualification" && (
                        <div className="space-y-2">
                          <input
                            type="text"
                            placeholder="Search qualifications..."
                            value={qualificationSearch}
                            onChange={(e) =>
                              setQualificationSearch(e.target.value)
                            }
                            className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/50"
                          />
                          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto pr-1">
                            {filteredQualificationOptions.map((q) => (
                              <SelectionChip
                                key={q}
                                label={q}
                                active={selectedSpecialPreferenceValues.includes(
                                  q,
                                )}
                                onClick={() =>
                                  toggleSpecialPreferenceDraftValue(q)
                                }
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      {selectedSpecialPreference === "profession" && (
                        <div className="space-y-2">
                          <input
                            type="text"
                            placeholder="Search professions..."
                            value={professionSearch}
                            onChange={(e) =>
                              setProfessionSearch(e.target.value)
                            }
                            className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/50"
                          />
                          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto pr-1">
                            {filteredProfessionOptions.map((profession) => (
                              <SelectionChip
                                key={profession}
                                label={profession}
                                active={selectedSpecialPreferenceValues.includes(
                                  profession,
                                )}
                                onClick={() =>
                                  toggleSpecialPreferenceDraftValue(profession)
                                }
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      {selectedSpecialPreference === "height" && (
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            <div className="space-y-1">
                              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">
                                Min Height
                              </label>
                              <select
                                value={heightMin}
                                onChange={(e) => setHeightMin(e.target.value)}
                                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white focus:outline-none focus:border-indigo-500/50"
                              >
                                <option value="">Select minimum</option>
                                {heightRangeOptions.map((option) => (
                                  <option key={`min-${option}`} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="space-y-1">
                              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">
                                Max Height
                              </label>
                              <select
                                value={heightMax}
                                onChange={(e) => setHeightMax(e.target.value)}
                                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white focus:outline-none focus:border-indigo-500/50"
                              >
                                <option value="">Select maximum</option>
                                {heightRangeOptions.map((option) => (
                                  <option key={`max-${option}`} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">
                                Height
                              </span>
                              <span className="text-[9px] text-slate-500">
                                Quick pick
                              </span>
                            </div>
                            <input
                              type="text"
                              placeholder="Search height..."
                              value={heightSearch}
                              onChange={(e) => setHeightSearch(e.target.value)}
                              className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/50"
                            />
                          </div>

                          <div className="space-y-2">
                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">
                              Quick Select
                            </span>
                            <div className="flex flex-wrap gap-2">
                              {filteredHeightOptions.length > 0 ? (
                                filteredHeightOptions.map((option) => (
                                  <SelectionChip
                                    key={option}
                                    label={option}
                                    active={selectedSpecialPreferenceValues.includes(
                                      option,
                                    )}
                                    onClick={() =>
                                      toggleSpecialPreferenceDraftValue(option)
                                    }
                                  />
                                ))
                              ) : (
                                <span className="text-slate-500 text-xs">
                                  No matching heights found.
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      {selectedSpecialPreference === "age" && (
                        <div className="space-y-3">
                          <div className="space-y-2">
                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">
                              Range Type
                            </span>
                            <div className="flex flex-wrap gap-2">
                              {ageOptions.map((option) => (
                                <SelectionChip
                                  key={option}
                                  label={option}
                                  active={selectedAge === option}
                                  onClick={() => {
                                    setSelectedAge(option);
                                    setAgeMin("");
                                    setAgeMax("");
                                  }}
                                />
                              ))}
                            </div>
                          </div>

                          {selectedAge && (
                            <>
                              <div className="space-y-2">
                                <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">
                                  Quick Select
                                </span>
                                <div className="flex flex-wrap gap-2">
                                  {(selectedAge === "between"
                                    ? ageRangePresets.map(([min, max]) => ({
                                      label: `${min}-${max} years`,
                                      min: String(min),
                                      max: String(max),
                                    }))
                                    : agePresetValues.map((value) => ({
                                      label: `${value} years`,
                                      min: String(value),
                                      max: "",
                                    }))
                                  ).map((preset) => (
                                    <SelectionChip
                                      key={`${preset.min}-${preset.max || "single"}`}
                                      label={preset.label}
                                      active={
                                        ageMin === preset.min &&
                                        ageMax === preset.max
                                      }
                                      onClick={() => {
                                        setAgeMin(preset.min);
                                        setAgeMax(preset.max);
                                      }}
                                    />
                                  ))}
                                </div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                <input
                                  type="number"
                                  min="18"
                                  max="80"
                                  placeholder={
                                    selectedAge === "between"
                                      ? "Min age"
                                      : "Age"
                                  }
                                  value={ageMin}
                                  onChange={(e) => setAgeMin(e.target.value)}
                                  className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/50"
                                />
                                {selectedAge === "between" && (
                                  <input
                                    type="number"
                                    min="18"
                                    max="80"
                                    placeholder="Max age"
                                    value={ageMax}
                                    onChange={(e) =>
                                      setAgeMax(e.target.value)
                                    }
                                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/50"
                                  />
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      {selectedSpecialPreference === "salary" && (
                        <div className="space-y-3">
                          <div className="space-y-2">
                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">
                              Range Type
                            </span>
                            <div className="flex flex-wrap gap-2">
                              {salaryOptions.map((option) => (
                                <SelectionChip
                                  key={option}
                                  label={option}
                                  active={selectedSalary === option}
                                  onClick={() => {
                                    setSelectedSalary(option);
                                    setSalaryMin("");
                                    setSalaryMax("");
                                  }}
                                />
                              ))}
                            </div>
                          </div>

                          {selectedSalary && (
                            <>
                              <div className="space-y-2">
                                <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">
                                  Quick Select
                                </span>
                                <div className="flex flex-wrap gap-2">
                                  {(selectedSalary === "between"
                                    ? salaryRangePresets.map(([min, max]) => ({
                                      label: `${formatLakhsLabel(min)} - ${formatLakhsLabel(max)}`,
                                      min: String(min),
                                      max: String(max),
                                    }))
                                    : salaryPresetValues.map((value) => ({
                                      label: formatLakhsLabel(value),
                                      min: String(value),
                                      max: "",
                                    }))
                                  ).map((preset) => (
                                    <SelectionChip
                                      key={`${preset.min}-${preset.max || "single"}`}
                                      label={preset.label}
                                      active={
                                        salaryMin === preset.min &&
                                        salaryMax === preset.max
                                      }
                                      onClick={() => {
                                        setSalaryMin(preset.min);
                                        setSalaryMax(preset.max);
                                      }}
                                    />
                                  ))}
                                </div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                <input
                                  type="number"
                                  placeholder={
                                    selectedSalary === "between"
                                      ? "Min salary (₹)"
                                      : "Amount (₹)"
                                  }
                                  value={salaryMin}
                                  onChange={(e) => setSalaryMin(e.target.value)}
                                  className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/50"
                                />
                                {selectedSalary === "between" && (
                                  <input
                                    type="number"
                                    placeholder="Max salary (₹)"
                                    value={salaryMax}
                                    onChange={(e) =>
                                      setSalaryMax(e.target.value)
                                    }
                                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/50"
                                  />
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      {selectedSpecialPreference === "proximity_range" && (
                        <div className="space-y-3">
                          <div className="space-y-2">
                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">
                              Range Type
                            </span>
                            <div className="flex flex-wrap gap-2">
                              {proximityOptions.map((option) => (
                                <SelectionChip
                                  key={option}
                                  label={option}
                                  active={selectedProximity === option}
                                  onClick={() => {
                                    setSelectedProximity(option);
                                    setProximityMin("");
                                    setProximityMax("");
                                  }}
                                />
                              ))}
                            </div>
                          </div>

                          {selectedProximity && (
                            <>
                              <div className="space-y-2">
                                <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">
                                  Quick Select
                                </span>
                                <div className="flex flex-wrap gap-2">
                                  {(selectedProximity === "between"
                                    ? proximityRangePresets.map(
                                      ([min, max]) => ({
                                        label: `${min}-${max} km`,
                                        min: String(min),
                                        max: String(max),
                                      }),
                                    )
                                    : proximityPresetValues.map((value) => ({
                                      label: `${value} km`,
                                      min: String(value),
                                      max: "",
                                    }))
                                  ).map((preset) => (
                                    <SelectionChip
                                      key={`${preset.min}-${preset.max || "single"}`}
                                      label={preset.label}
                                      active={
                                        proximityMin === preset.min &&
                                        proximityMax === preset.max
                                      }
                                      onClick={() => {
                                        setProximityMin(preset.min);
                                        setProximityMax(preset.max);
                                      }}
                                    />
                                  ))}
                                </div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                <input
                                  type="number"
                                  placeholder={
                                    selectedProximity === "between"
                                      ? "Min distance (km)"
                                      : "Distance (km)"
                                  }
                                  value={proximityMin}
                                  onChange={(e) =>
                                    setProximityMin(e.target.value)
                                  }
                                  className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/50"
                                />
                                {selectedProximity === "between" && (
                                  <input
                                    type="number"
                                    placeholder="Max distance (km)"
                                    value={proximityMax}
                                    onChange={(e) =>
                                      setProximityMax(e.target.value)
                                    }
                                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/50"
                                  />
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      {Boolean(selectedSpecialPreference) &&
                        selectedSpecialPreference !== "location" &&
                        selectedSpecialPreference !== "working_location" &&
                        selectedSpecialPreference !== "state" &&
                        selectedSpecialPreference !== "qualification" &&
                        selectedSpecialPreference !== "profession" &&
                        selectedSpecialPreference !== "height" &&
                        selectedSpecialPreference !== "age" &&
                        selectedSpecialPreference !== "salary" &&
                        selectedSpecialPreference !== "proximity_range" && (
                          <div className="space-y-2">
                            {mappedSpecialPreferenceOptions.length > 0 ? (
                              <>
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">
                                  Select Value
                                </label>
                                <input
                                  type="text"
                                  placeholder={`Search ${specialPreferenceOptions.find((opt) => opt.value === selectedSpecialPreference)?.label || "value"}...`}
                                  value={mappedPreferenceSearch}
                                  onChange={(e) =>
                                    setMappedPreferenceSearch(e.target.value)
                                  }
                                  className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/50"
                                />
                                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto pr-1">
                                  {filteredMappedSpecialPreferenceOptions.map(
                                    (option) => (
                                      <SelectionChip
                                        key={option}
                                        label={prettifyDropdownOption(option)}
                                        active={selectedSpecialPreferenceValues.includes(
                                          option,
                                        )}
                                        onClick={() =>
                                          toggleSpecialPreferenceDraftValue(
                                            option,
                                          )
                                        }
                                      />
                                    ),
                                  )}
                                </div>
                              </>
                            ) : (
                              <>
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">
                                  Enter Value
                                </label>
                                <input
                                  type="text"
                                  placeholder={`Enter ${specialPreferenceOptions.find((opt) => opt.value === selectedSpecialPreference)?.label || "value"}...`}
                                  value={customSpecialPreferenceValue}
                                  onChange={(e) =>
                                    setCustomSpecialPreferenceValue(
                                      e.target.value,
                                    )
                                  }
                                  className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/50"
                                />
                              </>
                            )}

                            {!mappedSpecialPreferenceOptions.length && (
                              <button
                                type="button"
                                onClick={() => {
                                  const parsedValues =
                                    customSpecialPreferenceValue
                                      .split(",")
                                      .map((item) => item.trim())
                                      .filter(Boolean);
                                  parsedValues.forEach((value) =>
                                    toggleSpecialPreferenceDraftValue(value),
                                  );
                                  setCustomSpecialPreferenceValue("");
                                }}
                                disabled={!customSpecialPreferenceValue.trim()}
                                className="px-3 py-2 rounded-xl border border-indigo-500/30 text-[10px] font-black uppercase tracking-widest text-indigo-300 hover:bg-indigo-500/10 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                              >
                                Add To List
                              </button>
                            )}
                          </div>
                        )}
                      <div className="flex gap-2 pt-1">
                        <button
                          onClick={async () => {
                            let valuesToAdd: string[] = [];
                            if (
                              selectedSpecialPreference === "country" ||
                              selectedSpecialPreference === "location" ||
                              selectedSpecialPreference === "working_location"
                            ) {
                              const draft = [...selectedSpecialPreferenceValues];
                              if (
                                selectedSpecialPreference === "country" &&
                                countrySearch.trim() &&
                                !draft.includes(countrySearch.trim())
                              ) {
                                draft.push(countrySearch.trim());
                              }
                              valuesToAdd = draft;
                            } else if (
                              selectedSpecialPreference === "state"
                            ) {
                              valuesToAdd = selectedSpecialPreferenceValues;
                            } else if (
                              selectedSpecialPreference === "qualification"
                            ) {
                              valuesToAdd = selectedSpecialPreferenceValues;
                            } else if (
                              selectedSpecialPreference === "profession"
                            ) {
                              valuesToAdd = selectedSpecialPreferenceValues;
                            } else if (selectedSpecialPreference === "height") {
                              valuesToAdd = selectedSpecialPreferenceValues;
                            } else if (selectedSpecialPreference === "age") {
                              if (
                                !selectedAge ||
                                !ageMin ||
                                (selectedAge === "between" && !ageMax)
                              )
                                return;
                              valuesToAdd = [
                                selectedAge === "between"
                                  ? `${selectedAge} ${ageMin} - ${ageMax}`
                                  : `${selectedAge} ${ageMin}`,
                              ];
                            } else if (selectedSpecialPreference === "salary") {
                              if (
                                !selectedSalary ||
                                !salaryMin ||
                                (selectedSalary === "between" && !salaryMax)
                              )
                                return;
                              valuesToAdd = [
                                selectedSalary === "between"
                                  ? `${selectedSalary} ${salaryMin} - ${salaryMax}`
                                  : `${selectedSalary} ${salaryMin}`,
                              ];
                            } else if (
                              selectedSpecialPreference === "proximity_range"
                            ) {
                              if (
                                !selectedProximity ||
                                !proximityMin ||
                                (selectedProximity === "between" &&
                                  !proximityMax)
                              )
                                return;
                              valuesToAdd = [
                                selectedProximity === "between"
                                  ? `${selectedProximity} ${proximityMin} - ${proximityMax}`
                                  : `${selectedProximity} ${proximityMin}`,
                              ];
                            } else {
                              valuesToAdd =
                                selectedSpecialPreferenceValues.length > 0
                                  ? selectedSpecialPreferenceValues
                                  : customSpecialPreferenceValue
                                    .split(",")
                                    .map((item) => item.trim())
                                    .filter(Boolean);
                            }

                            const normalizedValues = Array.from(
                              new Set(
                                valuesToAdd
                                  .map((value) => value.trim())
                                  .filter(Boolean),
                              ),
                            );
                            if (normalizedValues.length === 0) return;

                            const bucket = specialPreferenceMode;
                            const existing: string[] =
                              (specialPreferenceNotes?.[bucket]?.[
                                selectedSpecialPreference
                              ] as string[]) || [];
                            const existingSet = new Set(
                              existing.map((value) => value.toLowerCase().trim()),
                            );
                            const newValues = normalizedValues.filter(
                              (value) => !existingSet.has(value.toLowerCase().trim()),
                            );
                            if (newValues.length === 0) {
                              toast.error("Selected values are already added");
                              return;
                            }
                            const merged: Record<string, any> = {
                              ...specialPreferenceNotes,
                              [bucket]: {
                                ...(specialPreferenceNotes?.[bucket] || {}),
                                [selectedSpecialPreference]: [
                                  ...existing,
                                  ...newValues,
                                ],
                              },
                            };
                            await handlePatchSpecialPreference(merged);
                            resetSpecialPreferenceDraft();
                            setSelectedSpecialPreference("");
                          }}
                          disabled={
                            (selectedSpecialPreference === "country" &&
                              selectedSpecialPreferenceValues.length === 0 &&
                              !countrySearch.trim()) ||
                            ((selectedSpecialPreference === "location" || selectedSpecialPreference === "working_location") &&
                              selectedSpecialPreferenceValues.length === 0) ||
                            (selectedSpecialPreference === "qualification" &&
                              selectedSpecialPreferenceValues.length === 0) ||
                            (selectedSpecialPreference === "profession" &&
                              selectedSpecialPreferenceValues.length === 0) ||
                            (selectedSpecialPreference === "height" &&
                              selectedSpecialPreferenceValues.length === 0) ||
                            (selectedSpecialPreference === "age" &&
                              (!selectedAge ||
                                !ageMin ||
                                (selectedAge === "between" && !ageMax))) ||
                            (selectedSpecialPreference === "salary" &&
                              (!selectedSalary ||
                                !salaryMin ||
                                (selectedSalary === "between" &&
                                  !salaryMax))) ||
                            (selectedSpecialPreference === "proximity_range" &&
                              (!selectedProximity ||
                                !proximityMin ||
                                (selectedProximity === "between" &&
                                  !proximityMax))) ||
                            (Boolean(selectedSpecialPreference) &&
                              selectedSpecialPreference !== "country" &&
                              selectedSpecialPreference !== "location" &&
                              selectedSpecialPreference !== "working_location" &&
                              selectedSpecialPreference !== "state" &&
                              selectedSpecialPreference !== "qualification" &&
                              selectedSpecialPreference !== "profession" &&
                              selectedSpecialPreference !== "height" &&
                              selectedSpecialPreference !== "age" &&
                              selectedSpecialPreference !== "salary" &&
                              selectedSpecialPreference !== "proximity_range" &&
                              selectedSpecialPreferenceValues.length === 0 &&
                              !customSpecialPreferenceValue.trim())
                          }
                          className="flex-1 py-2 bg-indigo-500/10 hover:bg-indigo-500/20 disabled:opacity-40 disabled:cursor-not-allowed text-indigo-400 border border-indigo-500/20 rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2"
                        >
                          <Check size={12} /> Add{" "}
                          {specialPreferenceMode === "includes"
                            ? "Include"
                            : "Exclude"}
                        </button>
                        <button
                          onClick={() => {
                            setSelectedSpecialPreference("");
                            resetSpecialPreferenceDraft();
                          }}
                          className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-400 border border-slate-700 rounded-xl text-xs font-black uppercase tracking-widest transition-all"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Dislike Reasons */}
              {userDetails.dislikeReasons &&
                userDetails.dislikeReasons.length > 0 && (
                  <div className="bg-slate-900/50 backdrop-blur-xl rounded-3xl border border-white/5 overflow-hidden shadow-2xl mt-6 relative">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-rose-500/5 rounded-full blur-3xl mix-blend-screen mix-blend-screen pointer-events-none" />
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-sm font-black text-rose-500 uppercase tracking-widest flex items-center gap-2">
                          <X size={16} className="text-rose-400" />
                          Dislike Reasons
                        </h3>
                      </div>
                      <div className="space-y-0 max-h-[300px] overflow-y-auto custom-scrollbar">
                        {[...(userDetails.dislikeReasons || [])].map(
                          (item: any, idx: number) => (
                            <div
                              key={idx}
                              className="relative flex gap-3 group/item mt-4"
                            >
                              <div className="flex flex-col items-center">
                                <div className="w-2 h-2 rounded-full bg-rose-500/60 border border-rose-400/30 mt-1.5 shrink-0 z-10" />
                                {idx <
                                  (userDetails.dislikeReasons?.length || 0) -
                                  1 && (
                                    <div className="w-px flex-1 bg-slate-800 min-h-[20px]" />
                                  )}
                              </div>
                              <div className="pb-4 flex-1 min-w-0">
                                <span className="text-[10px] text-rose-400 font-bold uppercase tracking-widest">
                                  {item.name || item.user || "Unknown User"}{" "}
                                  {item.matrimonial_id
                                    ? `(${item.matrimonial_id})`
                                    : ""}
                                </span>
                                <p className="text-xs text-slate-300 leading-relaxed font-medium break-words mt-1">
                                  {item.reason}
                                </p>
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                )}
            </div>
          )}

          {/* RIGHT COLUMN: Workspace area */}
          <div
            className={`${activeTab === "Full Profile" ? "lg:col-span-9" : "lg:col-span-12"} space-y-6 transition-all duration-500 ease-in-out`}
          >
            {/* Tabs Navigation */}
            <div className="flex items-center gap-1 border-b border-slate-800">
              {tabs.map((tab) => {
                let count = 0;
                if (tab === "Matched Profiles")
                  count = matchesTotals.total_matches;
                if (tab === "Queued Profiles")
                  count = matchesTotals.total_queue;
                if (tab === "Potential Matches") count = potentialMatchesTotal;
                if (tab === "Skipped Matchess")
                  count = matchesTotals.total_skipped;
                if (tab === "Special Pref Skipped")
                  count = matchesTotals.special_pref_skipped;
                if (tab === "Special Pref Requeued")
                  count = matchesTotals.special_pref_reactivated;
                if (tab === "Engagement Hub")
                  count =
                    (tableData["Liked Matches"]?.length || 0) +
                    (tableData["Liked By"]?.length || 0);

                return (
                  <button
                    key={tab}
                    className={`px-4 py-3 text-sm font-medium transition-all relative flex items-center gap-2 ${activeTab === tab
                      ? "text-blue-500"
                      : "text-slate-500 hover:text-white"
                      }`}
                    onClick={() => {
                      setCurrentActive(tab);
                      setSearch("");
                      setPage(1);
                    }}
                  >
                    {tab}
                    {count > 0 && (
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${activeTab === tab ? "bg-blue-500/10 text-blue-500" : "bg-slate-800 text-slate-400"}`}
                      >
                        {count}
                      </span>
                    )}
                    {activeTab === tab && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500 shadow-[0_-2px_6px_rgba(59,130,246,0.5)]"></div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Main Workspace Area Content */}
            <div className="animate-fade-in space-y-4">
              {(activeTab === "Special Pref Skipped" ||
                activeTab === "Special Pref Requeued") && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      {[
                        {
                          label: "Currently Skipped",
                          value: matchesTotals.special_pref_skipped,
                          tone: "text-rose-400 border-rose-500/20 bg-rose-500/5",
                        },
                        {
                          label: "Reactivated Total",
                          value: matchesTotals.special_pref_reactivated,
                          tone: "text-indigo-400 border-indigo-500/20 bg-indigo-500/5",
                        },
                        {
                          label: "Inserted To Queue",
                          value: matchesTotals.special_pref_inserted_to_queue,
                          tone: "text-emerald-400 border-emerald-500/20 bg-emerald-500/5",
                        },
                        {
                          label: "Requeued Existing",
                          value:
                            matchesTotals.special_pref_requeued_to_existing_queue,
                          tone: "text-amber-400 border-amber-500/20 bg-amber-500/5",
                        },
                      ].map((item) => (
                        <div
                          key={item.label}
                          className={`rounded-2xl border p-4 ${item.tone}`}
                        >
                          <div className="text-[10px] font-black uppercase tracking-[0.18em] opacity-70">
                            {item.label}
                          </div>
                          <div className="mt-2 text-2xl font-black">
                            {item.value || 0}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="p-4 bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-3xl shadow-2xl">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center border border-violet-500/30">
                            <Activity size={18} className="text-violet-400" />
                          </div>
                          <div>
                            <h3 className="text-sm font-black text-white uppercase tracking-widest italic">
                              Special Preference Activity
                            </h3>
                            <p className="text-[11px] text-slate-500">
                              Search by user ID, name, location, queue source, or
                              reason text.
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end sm:flex-1">
                          <div className="w-full sm:max-w-md relative">
                            <input
                              type="text"
                              placeholder="Search user ID, name, location, queue source, or reason..."
                              value={search}
                              onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(1);
                              }}
                              className="w-full h-11 bg-slate-950 border border-slate-800 rounded-xl px-4 pr-10 text-[11px] font-bold text-white placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-violet-500/30 transition-all"
                            />
                            {search && (
                              <button
                                onClick={() => {
                                  setSearch("");
                                  setPage(1);
                                }}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                              >
                                <X size={12} />
                              </button>
                            )}
                          </div>

                          <button
                            onClick={() => {
                              setSearch("");
                              setPage(1);
                            }}
                            className="px-4 py-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-500 border border-rose-500/20 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap flex items-center justify-center gap-2 group"
                          >
                            <RefreshCw
                              size={12}
                              className="group-hover:rotate-180 transition-transform duration-500"
                            />
                            Clear Search
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              {/* Conditional Filter Panels */}
              {activeTab === "Queued Profiles" && (
                <div className="space-y-4">
                  <div className="p-6 bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-3xl shadow-2xl space-y-6 overflow-visible relative z-[50]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center border border-indigo-500/30">
                          <Activity size={18} className="text-indigo-400" />
                        </div>
                        <h3 className="text-sm font-black text-white uppercase tracking-widest italic">
                          Advanced Filtering
                        </h3>
                      </div>
                      <div className="flex-1 max-w-md mx-4 relative">
                        <input
                          type="text"
                          placeholder="Search User ID or Name..."
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          className="w-full h-10 bg-slate-950 border border-slate-800 rounded-xl px-4 text-[11px] font-bold text-white placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500/30 transition-all"
                        />
                        {search && (
                          <button
                            onClick={() => setSearch("")}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                          >
                            <X size={12} />
                          </button>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setQueueFilters({
                            min_age: "",
                            max_age: "",
                            city: [],
                            state: [],
                            profession: [],
                            family_type: "",
                            family_background: "",
                            manglik: [],
                            proximity_range: "all",
                          });
                          setPage(1);
                        }}
                        className="px-4 py-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-500 border border-rose-500/20 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap flex items-center gap-2 group"
                      >
                        <RefreshCw
                          size={12}
                          className="group-hover:rotate-180 transition-transform duration-500"
                        />
                        Reset Filters
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-4">
                      {/* Age Range Group */}
                      <div className="col-span-1 sm:col-span-2 grid grid-cols-2 gap-2 p-2 bg-slate-950/50 rounded-2xl border border-slate-800/50">
                        <div className="space-y-1">
                          <label className="text-[9px] font-black text-slate-500 uppercase px-1 tracking-tighter">
                            Min Age
                          </label>
                          <input
                            type="number"
                            placeholder="Ex: 21"
                            value={queueFilters.min_age}
                            onChange={(e) => {
                              setQueueFilters({
                                ...queueFilters,
                                min_age: e.target.value,
                              });
                              setPage(1);
                            }}
                            className="w-full h-9 bg-slate-900 border border-slate-700/50 text-white text-[11px] font-bold px-3 rounded-xl focus:outline-none focus:ring-1 focus:ring-indigo-500/50 transition-all"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[9px] font-black text-slate-500 uppercase px-1 tracking-tighter">
                            Max Age
                          </label>
                          <input
                            type="number"
                            placeholder="Ex: 35"
                            value={queueFilters.max_age}
                            onChange={(e) => {
                              setQueueFilters({
                                ...queueFilters,
                                max_age: e.target.value,
                              });
                              setPage(1);
                            }}
                            className="w-full h-9 bg-slate-900 border border-slate-700/50 text-white text-[11px] font-bold px-3 rounded-xl focus:outline-none focus:ring-1 focus:ring-indigo-500/50 transition-all"
                          />
                        </div>
                      </div>

                      <MultiSelectDropdown
                        label="City"
                        options={allTabOptions.queue.cities}
                        selectedValues={queueFilters.city}
                        onToggle={(val) => {
                          const newCities = queueFilters.city.includes(val)
                            ? queueFilters.city.filter((c) => c !== val)
                            : [...queueFilters.city, val];
                          setQueueFilters({ ...queueFilters, city: newCities });
                          setPage(1);
                        }}
                        onReset={() => {
                          setQueueFilters({ ...queueFilters, city: [] });
                          setPage(1);
                        }}
                        icon={<MapPin size={12} />}
                      />
                      <MultiSelectDropdown
                        label="State"
                        options={allTabOptions.queue.states}
                        selectedValues={queueFilters.state}
                        onToggle={(val) => {
                          const newStates = queueFilters.state.includes(val)
                            ? queueFilters.state.filter((s) => s !== val)
                            : [...queueFilters.state, val];
                          setQueueFilters({
                            ...queueFilters,
                            state: newStates,
                          });
                          setPage(1);
                        }}
                        onReset={() => {
                          setQueueFilters({ ...queueFilters, state: [] });
                          setPage(1);
                        }}
                        icon={<MapPin size={12} />}
                      />
                      <MultiSelectDropdown
                        label="Profession"
                        options={allTabOptions.queue.professions}
                        selectedValues={queueFilters.profession}
                        onToggle={(val) => {
                          const newProfs = queueFilters.profession.includes(val)
                            ? queueFilters.profession.filter((p) => p !== val)
                            : [...queueFilters.profession, val];
                          setQueueFilters({
                            ...queueFilters,
                            profession: newProfs,
                          });
                          setPage(1);
                        }}
                        onReset={() => {
                          setQueueFilters({ ...queueFilters, profession: [] });
                          setPage(1);
                        }}
                        icon={<Briefcase size={12} />}
                      />
                      {[
                        {
                          label: "Family Type",
                          value: queueFilters.family_type,
                          options: allTabOptions.queue.familyTypes,
                          icon: <Users size={12} />,
                          key: "family_type",
                        },
                        {
                          label: "Family BG",
                          value: queueFilters.family_background,
                          options: allTabOptions.queue.familyBackgrounds,
                          icon: <Layers size={12} />,
                          key: "family_background",
                        },
                      ].map((f) => (
                        <div
                          key={f.key}
                          className="space-y-1.5 p-1 bg-slate-950/30 rounded-2xl border border-transparent hover:border-slate-800 transition-colors"
                        >
                          <div className="flex items-center gap-1.5 px-2">
                            <span className="text-indigo-500/50">{f.icon}</span>
                            <label className="text-[9px] font-black text-slate-500 uppercase tracking-tighter">
                              {f.label}
                            </label>
                          </div>
                          <select
                            value={f.value}
                            onChange={(e) => {
                              setQueueFilters({
                                ...queueFilters,
                                [f.key]: e.target.value,
                              });
                              setPage(1);
                            }}
                            className="w-full h-9 bg-slate-900 border border-slate-700/30 text-white text-[10px] font-bold px-3 rounded-xl focus:outline-none focus:ring-1 focus:ring-indigo-500/50 transition-all appearance-none cursor-pointer"
                          >
                            <option value="">All {f.label}s</option>
                            {f.options.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        </div>
                      ))}

                      <MultiSelectDropdown
                        label="Manglik"
                        options={allTabOptions.queue.manglikOptions}
                        selectedValues={queueFilters.manglik}
                        onToggle={(val) => {
                          const newManglik = queueFilters.manglik.includes(val)
                            ? queueFilters.manglik.filter((p) => p !== val)
                            : [...queueFilters.manglik, val];
                          setQueueFilters({
                            ...queueFilters,
                            manglik: newManglik,
                          });
                          setPage(1);
                        }}
                        onReset={() => {
                          setQueueFilters({ ...queueFilters, manglik: [] });
                          setPage(1);
                        }}
                        icon={<Star size={12} />}
                      />

                      {/* Proximity Special */}
                      <div className="space-y-1.5 p-1 bg-slate-950/30 rounded-2xl border border-transparent hover:border-slate-800 transition-colors">
                        <div className="flex items-center gap-1.5 px-2">
                          <Compass size={12} className="text-amber-500/50" />
                          <label className="text-[9px] font-black text-slate-500 uppercase tracking-tighter">
                            Proximity
                          </label>
                        </div>
                        <select
                          value={
                            typeof queueFilters.proximity_range === "string"
                              ? queueFilters.proximity_range
                              : JSON.stringify(queueFilters.proximity_range)
                          }
                          onChange={(e) => {
                            const val = e.target.value;
                            setQueueFilters({
                              ...queueFilters,
                              proximity_range:
                                val === "all" ? "all" : JSON.parse(val),
                            });
                            setPage(1);
                          }}
                          className="w-full h-9 bg-slate-900 border border-slate-700/30 text-white text-[10px] font-bold px-3 rounded-xl focus:outline-none focus:ring-1 focus:ring-indigo-500/50 transition-all appearance-none cursor-pointer"
                        >
                          <option value="all">Any Distance</option>
                          <option value={JSON.stringify([0, 300])}>
                            0 - 300
                          </option>
                          <option value={JSON.stringify([300, 500])}>
                            300 - 500
                          </option>
                          <option value={JSON.stringify([500, 1000])}>
                            500 - 1000
                          </option>
                          <option value={JSON.stringify([1000, 2000])}>
                            1000 - 2000
                          </option>
                          <option value={JSON.stringify([2000, 10000])}>
                            2000 - 5000
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "Matched Profiles" && (
                <div className="space-y-6">
                  {/* Enhanced Dashboard Sub-Navigation */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[
                      {
                        id: "AI Matches",
                        label: "ALL",
                        sub: "",
                        icon: <TrendingUp size={20} />,
                        color: "emerald",
                        count: matchesTotals.total_matches,
                      },
                      {
                        id: "Outgoing",
                        label: "Like To",
                        sub: "",
                        icon: <Heart size={20} />,
                        color: "rose",
                        count: tableData["Like To"]?.length || 0,
                      },
                      {
                        id: "Incoming",
                        label: "Liked By",
                        sub: "",
                        icon: <Star size={20} />,
                        color: "amber",
                        count: tableData["Liked By"]?.length || 0,
                      },
                    ].map((card) => (
                      <button
                        key={card.id}
                        onClick={() => {
                          setMatchedSubTab(card.id as any);
                          setPage(1);
                        }}
                        className={`group relative p-6 rounded-[2.5rem] border transition-all duration-500 overflow-hidden text-left ${matchedSubTab === card.id
                          ? `bg-${card.color}-500/10 border-${card.color}-500/30 shadow-[0_20px_40px_rgba(0,0,0,0.4),0_0_20px_rgba(255,255,255,0.03)] ring-1 ring-${card.color}-500/20`
                          : "bg-slate-900/40 border-slate-800/50 hover:bg-slate-900/60 hover:border-slate-700 hover:shadow-2xl hover:-translate-y-1"
                          }`}
                      >
                        <div className="flex items-center gap-5 relative z-10">
                          <div
                            className={`w-14 h-14 rounded-3xl flex items-center justify-center border transition-all duration-700 ${matchedSubTab === card.id
                              ? `bg-${card.color}-500/20 border-${card.color}-500/30 ring-4 ring-${card.color}-500/10 scale-110`
                              : "bg-slate-950 border-slate-800 group-hover:scale-105 group-hover:shadow-lg"
                              }`}
                          >
                            <div
                              className={
                                matchedSubTab === card.id
                                  ? `text-${card.color}-400`
                                  : "text-slate-500 group-hover:text-slate-300 transition-colors"
                              }
                            >
                              {card.icon}
                            </div>
                          </div>
                          <div className="flex-1">
                            {card.sub && (
                              <p
                                className={`text-[9px] font-black uppercase tracking-[0.25em] mb-1 transition-colors ${matchedSubTab === card.id ? `text-${card.color}-500/80` : "text-slate-500 group-hover:text-slate-400"}`}
                              >
                                {card.sub}
                              </p>
                            )}
                            <h3
                              className={`text-lg font-black uppercase tracking-tighter transition-all ${matchedSubTab === card.id
                                ? "text-white scale-105 origin-left"
                                : "text-slate-400 group-hover:text-slate-200"
                                }`}
                            >
                              {card.label}
                            </h3>
                          </div>
                          <div
                            className={`text-2xl font-black transition-all ${matchedSubTab === card.id
                              ? `text-${card.color}-400`
                              : "text-slate-800 group-hover:text-slate-600"
                              }`}
                          >
                            {card.count.toString().padStart(2, "0")}
                          </div>
                        </div>

                        {/* Cool Hover/Active Glow */}
                        <div
                          className={`absolute -right-8 -bottom-8 w-32 h-32 rounded-full blur-[80px] transition-all duration-1000 opacity-0 group-hover:opacity-100 ${matchedSubTab === card.id
                            ? `bg-${card.color}-500/30 opacity-100`
                            : `bg-${card.color}-500/10`
                            }`}
                        ></div>
                      </button>
                    ))}
                  </div>
                  {matchedSubTab === "AI Matches" && (
                    <div className="p-6 bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-3xl shadow-2xl space-y-6 overflow-visible relative z-[50]">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center border border-emerald-500/30">
                            <TrendingUp
                              size={18}
                              className="text-emerald-400"
                            />
                          </div>
                          <h3 className="text-sm font-black text-white uppercase tracking-widest italic">
                            Match Insights Cloud
                          </h3>
                        </div>
                        <div className="flex-1 max-w-md mx-4 relative">
                          <input
                            type="text"
                            placeholder="Search User ID or Name..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full h-10 bg-slate-950 border border-slate-800 rounded-xl px-4 text-[11px] font-bold text-white placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-emerald-500/30 transition-all"
                          />
                          {search && (
                            <button
                              onClick={() => setSearch("")}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                            >
                              <X size={12} />
                            </button>
                          )}
                        </div>
                        <button
                          onClick={() => {
                            setMatchedFilters({
                              min_age: "",
                              max_age: "",
                              city: [],
                              state: [],
                              profession: [],
                              manglik: [],
                              publish: "",
                              score_range: "all",
                              proximity_range: "all",
                            });
                            setMatchApiFilters({
                              filter_type: "all",
                              match_status: "all",
                              compatibility_level: "all",
                              filter_dislikes: false,
                            });
                            setPage(1);
                          }}
                          className="px-4 py-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-500 border border-rose-500/20 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap flex items-center gap-2 group"
                        >
                          <RefreshCw
                            size={12}
                            className="group-hover:rotate-180 transition-transform duration-500"
                          />
                          Reset Dashboard
                        </button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 bg-slate-950/20 p-4 rounded-2xl border border-slate-800/30">
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1.5">
                            <label className="text-[9px] font-black text-slate-500 uppercase px-1 tracking-widest">
                              Min Age
                            </label>
                            <input
                              type="number"
                              placeholder="Min"
                              value={matchedFilters.min_age}
                              onChange={(e) => {
                                setMatchedFilters({
                                  ...matchedFilters,
                                  min_age: e.target.value,
                                });
                                setPage(1);
                              }}
                              className="w-full h-10 bg-slate-900 border border-slate-800 text-white text-[11px] font-bold px-3 rounded-xl focus:outline-none focus:ring-1 focus:ring-indigo-500/50 transition-all"
                            />
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-[9px] font-black text-slate-500 uppercase px-1 tracking-widest">
                              Max Age
                            </label>
                            <input
                              type="number"
                              placeholder="Max"
                              value={matchedFilters.max_age}
                              onChange={(e) => {
                                setMatchedFilters({
                                  ...matchedFilters,
                                  max_age: e.target.value,
                                });
                                setPage(1);
                              }}
                              className="w-full h-10 bg-slate-900 border border-slate-800 text-white text-[11px] font-bold px-3 rounded-xl focus:outline-none focus:ring-1 focus:ring-indigo-500/50 transition-all"
                            />
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <label className="text-[9px] font-black text-slate-500 uppercase px-1 tracking-widest">
                            Location Proximity
                          </label>
                          <select
                            value={
                              typeof matchedFilters.proximity_range === "string"
                                ? matchedFilters.proximity_range
                                : JSON.stringify(matchedFilters.proximity_range)
                            }
                            onChange={(e) => {
                              const val = e.target.value;
                              setMatchedFilters({
                                ...matchedFilters,
                                proximity_range:
                                  val === "all" ? "all" : JSON.parse(val),
                              });
                              setPage(1);
                            }}
                            className="w-full h-10 bg-slate-900 border border-slate-800 text-white text-[11px] font-bold px-3 rounded-xl focus:outline-none focus:ring-1 focus:ring-indigo-500/50 transition-all appearance-none cursor-pointer"
                          >
                            <option value="all">Proximity (All)</option>
                            <option value={JSON.stringify([0, 300])}>
                              0 - 300 km
                            </option>
                            <option value={JSON.stringify([300, 500])}>
                              300 - 500 km
                            </option>
                            <option value={JSON.stringify([500, 1000])}>
                              500 - 1000 km
                            </option>
                            <option value={JSON.stringify([1000, 2000])}>
                              1000 - 2000 km
                            </option>
                            <option value={JSON.stringify([2000, 10000])}>
                              2000 - 5000+ km
                            </option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-4">
                        <div className="p-3 bg-slate-950/40 rounded-2xl border border-slate-800/50 hover:border-emerald-500/20 transition-all group">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-emerald-400 group-hover:scale-110 transition-transform"></span>
                              <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                                Match Score Range
                              </span>
                            </div>
                          </div>
                          <select
                            value={
                              typeof matchedFilters.score_range === "string"
                                ? matchedFilters.score_range
                                : JSON.stringify(matchedFilters.score_range)
                            }
                            onChange={(e) => {
                              const val = e.target.value;
                              setMatchedFilters({
                                ...matchedFilters,
                                score_range:
                                  val === "all" ? "all" : JSON.parse(val),
                              });
                              setPage(1);
                            }}
                            className="w-full bg-transparent border-none text-white text-[11px] font-bold focus:outline-none appearance-none cursor-pointer"
                          >
                            <option value="all" className="bg-slate-900">
                              All Scores (0-100)
                            </option>
                            <option
                              value={JSON.stringify([0, 10])}
                              className="bg-slate-900"
                            >
                              0 - 10
                            </option>
                            <option
                              value={JSON.stringify([10, 20])}
                              className="bg-slate-900"
                            >
                              10 - 20
                            </option>
                            <option
                              value={JSON.stringify([20, 30])}
                              className="bg-slate-900"
                            >
                              20 - 30
                            </option>
                            <option
                              value={JSON.stringify([30, 40])}
                              className="bg-slate-900"
                            >
                              30 - 40
                            </option>
                            <option
                              value={JSON.stringify([40, 50])}
                              className="bg-slate-900"
                            >
                              40 - 50
                            </option>
                            <option
                              value={JSON.stringify([50, 60])}
                              className="bg-slate-900"
                            >
                              50 - 60
                            </option>
                            <option
                              value={JSON.stringify([60, 70])}
                              className="bg-slate-900"
                            >
                              60 - 70
                            </option>
                            <option
                              value={JSON.stringify([70, 80])}
                              className="bg-slate-900"
                            >
                              70 - 80
                            </option>
                            <option
                              value={JSON.stringify([80, 90])}
                              className="bg-slate-900"
                            >
                              80 - 90
                            </option>
                            <option
                              value={JSON.stringify([90, 100])}
                              className="bg-slate-900"
                            >
                              90 - 100
                            </option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <MultiSelectDropdown
                          label="Specific City"
                          options={allTabOptions.matched.cities}
                          selectedValues={matchedFilters.city}
                          onToggle={(val) => {
                            const newCities = matchedFilters.city.includes(val)
                              ? matchedFilters.city.filter((c) => c !== val)
                              : [...matchedFilters.city, val];
                            setMatchedFilters({
                              ...matchedFilters,
                              city: newCities,
                            });
                            setPage(1);
                          }}
                          onReset={() => {
                            setMatchedFilters({ ...matchedFilters, city: [] });
                            setPage(1);
                          }}
                          icon={<MapPin size={12} />}
                        />
                        <MultiSelectDropdown
                          label="Specific State"
                          options={allTabOptions.matched.states}
                          selectedValues={matchedFilters.state}
                          onToggle={(val) => {
                            const newStates = matchedFilters.state.includes(val)
                              ? matchedFilters.state.filter((s) => s !== val)
                              : [...matchedFilters.state, val];
                            setMatchedFilters({
                              ...matchedFilters,
                              state: newStates,
                            });
                            setPage(1);
                          }}
                          onReset={() => {
                            setMatchedFilters({ ...matchedFilters, state: [] });
                            setPage(1);
                          }}
                          icon={<Compass size={12} />}
                        />
                        <MultiSelectDropdown
                          label="Core Profession"
                          options={allTabOptions.matched.professions}
                          selectedValues={matchedFilters.profession}
                          onToggle={(val) => {
                            const newProfs = matchedFilters.profession.includes(
                              val,
                            )
                              ? matchedFilters.profession.filter(
                                (p) => p !== val,
                              )
                              : [...matchedFilters.profession, val];
                            setMatchedFilters({
                              ...matchedFilters,
                              profession: newProfs,
                            });
                            setPage(1);
                          }}
                          onReset={() => {
                            setMatchedFilters({
                              ...matchedFilters,
                              profession: [],
                            });
                            setPage(1);
                          }}
                          icon={<Briefcase size={12} />}
                        />

                        <MultiSelectDropdown
                          label="Manglik"
                          options={allTabOptions.matched.manglikOptions}
                          selectedValues={matchedFilters.manglik}
                          onToggle={(val) => {
                            const newManglik = matchedFilters.manglik.includes(
                              val,
                            )
                              ? matchedFilters.manglik.filter((p) => p !== val)
                              : [...matchedFilters.manglik, val];
                            setMatchedFilters({
                              ...matchedFilters,
                              manglik: newManglik,
                            });
                            setPage(1);
                          }}
                          onReset={() => {
                            setMatchedFilters({
                              ...matchedFilters,
                              manglik: [],
                            });
                            setPage(1);
                          }}
                          icon={<Star size={12} />}
                        />

                        {/* Add Publish Filter manually */}
                        <div className="p-3 bg-slate-950/40 rounded-2xl border border-slate-800/50 hover:border-indigo-500/20 transition-all group">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-indigo-400 group-hover:scale-110 transition-transform">
                                <Layers size={12} />
                              </span>
                              <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                                Publish Status
                              </span>
                            </div>
                            {matchedFilters.publish && (
                              <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.5)] animate-pulse" />
                            )}
                          </div>
                          <select
                            value={matchedFilters.publish}
                            onChange={(e) => {
                              setMatchedFilters({
                                ...matchedFilters,
                                publish: e.target.value,
                              });
                              setPage(1);
                            }}
                            className="w-full bg-transparent border-none text-white text-[11px] font-bold focus:outline-none appearance-none cursor-pointer"
                          >
                            <option value="">All Statuses</option>
                            <option value="yes" className="bg-slate-900">
                              Published
                            </option>
                            <option value="no" className="bg-slate-900">
                              Unpublished
                            </option>
                          </select>
                        </div>

                        <div className="p-3 bg-slate-950/40 rounded-2xl border border-slate-800/50 hover:border-amber-500/20 transition-all group">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-amber-400 group-hover:scale-110 transition-transform">
                                <HeartOff size={12} />
                              </span>
                              <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                                Filter Dislikes
                              </span>
                            </div>
                          </div>
                          <select
                            value={String(matchApiFilters.filter_dislikes)}
                            onChange={(e) => {
                              setMatchApiFilters((prev) => ({
                                ...prev,
                                filter_dislikes: e.target.value === "true",
                              }));
                              setPage(1);
                            }}
                            className="w-full bg-transparent border-none text-white text-[11px] font-bold focus:outline-none appearance-none cursor-pointer"
                          >
                            <option value="true" className="bg-slate-900">
                              Yes (Exclude Disliked)
                            </option>
                            <option value="false" className="bg-slate-900">
                              No (Include Disliked)
                            </option>
                          </select>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* {activeTab === "Rejected Profiles" && (
              <div className="space-y-4">
                <div className="p-6 bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-3xl shadow-2xl space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center border border-red-500/30">
                        <HeartOff size={18} className="text-red-400" />
                      </div>
                      <h3 className="text-sm font-black text-white uppercase tracking-widest italic">Rejected Vault Filter</h3>
                    </div>
                    <button
                      onClick={() => {
                        setRejectedFilters({
                          min_age: '', max_age: '', city: '', state: '',
                          family_type: '', family_background: ''
                        });
                        setPage(1);
                      }}
                      className="px-4 py-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-500 border border-rose-500/20 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 group"
                    >
                      <RefreshCw size={12} className="group-hover:rotate-180 transition-transform duration-500" />
                      Reset Vault
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                    <div className="col-span-1 grid grid-cols-2 gap-2 p-2 bg-slate-950/50 rounded-2xl border border-slate-800/50">
                      <div className="space-y-1">
                        <label className="text-[9px] font-black text-slate-500 uppercase px-1">Min</label>
                        <input
                          type="number"
                          value={rejectedFilters.min_age}
                          onChange={(e) => { setRejectedFilters({ ...rejectedFilters, min_age: e.target.value }); setPage(1); }}
                          className="w-full h-9 bg-slate-900 border border-slate-700/50 text-white text-[11px] font-bold px-3 rounded-xl focus:outline-none"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[9px] font-black text-slate-500 uppercase px-1">Max</label>
                        <input
                          type="number"
                          value={rejectedFilters.max_age}
                          onChange={(e) => { setRejectedFilters({ ...rejectedFilters, max_age: e.target.value }); setPage(1); }}
                          className="w-full h-9 bg-slate-900 border border-slate-700/50 text-white text-[11px] font-bold px-3 rounded-xl focus:outline-none"
                        />
                      </div>
                    </div>

                    {[
                      { label: "City", value: rejectedFilters.city, options: allTabOptions.rejected.cities, icon: <MapPin size={12} />, key: 'city' },
                      { label: "State", value: rejectedFilters.state, options: allTabOptions.rejected.states, icon: <MapPin size={12} />, key: 'state' },
                      { label: "Family Type", value: rejectedFilters.family_type, options: allTabOptions.rejected.familyTypes, icon: <Users size={12} />, key: 'family_type' },
                      { label: "Family BG", value: rejectedFilters.family_background, options: allTabOptions.rejected.familyBackgrounds, icon: <Layers size={12} />, key: 'family_background' }
                    ].map((f) => (
                      <div key={f.key} className="space-y-1.5 p-1 bg-slate-950/30 rounded-2xl border border-transparent hover:border-slate-800 transition-colors">
                        <div className="flex items-center gap-1.5 px-2">
                          <span className="text-red-500/50">{f.icon}</span>
                          <label className="text-[9px] font-black text-slate-500 uppercase tracking-tighter">{f.label}</label>
                        </div>
                        <select
                          value={f.value}
                          onChange={(e) => { setRejectedFilters({ ...rejectedFilters, [f.key]: e.target.value }); setPage(1); }}
                          className="w-full h-9 bg-slate-900 border border-slate-700/30 text-white text-[10px] font-bold px-3 rounded-xl appearance-none cursor-pointer"
                        >
                          <option value="">All {f.label}s</option>
                          {f.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )} */}

              {activeTab === "Potential Matches" && (
                <div className="space-y-4">
                  <div className="p-6 bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-3xl shadow-2xl space-y-6 overflow-visible relative z-[120]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center border border-indigo-500/30">
                          <Zap size={18} className="text-indigo-400" />
                        </div>
                        <h3 className="text-sm font-black text-white uppercase tracking-widest italic">
                          Potential Matches Hub
                        </h3>
                      </div>
                      <button
                        onClick={handleTriggerPotentialMatches}
                        className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-[10px] font-black uppercase tracking-[0.2em] transition-all shadow-[0_8px_20px_rgba(79,70,229,0.3)] active:scale-95 flex items-center gap-2 group"
                      >
                        <RefreshCw
                          size={14}
                          className="group-hover:rotate-180 transition-transform duration-500"
                        />
                        Fetch Potential Matches
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                      <div className="space-y-1.5 p-1 bg-slate-950/30 rounded-2xl border border-transparent hover:border-slate-800 transition-colors">
                        <div className="flex items-center gap-1.5 px-2">
                          <span className="text-indigo-500/50">
                            <User size={12} />
                          </span>
                          <label className="text-[9px] font-black text-slate-500 uppercase tracking-tighter">
                            Name
                          </label>
                        </div>
                        <input
                          type="text"
                          value={potentialFilters.name}
                          onChange={(e) => {
                            setPotentialFilters({
                              ...potentialFilters,
                              name: e.target.value,
                            });
                            setPage(1);
                          }}
                          placeholder="Filter by name"
                          className="w-full h-9 bg-slate-900 border border-slate-700/50 text-white text-[11px] font-bold px-3 rounded-xl focus:outline-none"
                        />
                      </div>

                      <div className="space-y-1.5 p-1 bg-slate-950/30 rounded-2xl border border-transparent hover:border-slate-800 transition-colors">
                        <div className="flex items-center gap-1.5 px-2">
                          <span className="text-indigo-500/50">
                            <ShieldCheck size={12} />
                          </span>
                          <label className="text-[9px] font-black text-slate-500 uppercase tracking-tighter">
                            Matri ID
                          </label>
                        </div>
                        <input
                          type="text"
                          value={potentialFilters.matrimonial_id}
                          onChange={(e) => {
                            setPotentialFilters({
                              ...potentialFilters,
                              matrimonial_id: e.target.value,
                            });
                            setPage(1);
                          }}
                          placeholder="e.g. M951772694102"
                          className="w-full h-9 bg-slate-900 border border-slate-700/50 text-white text-[11px] font-bold px-3 rounded-xl focus:outline-none"
                        />
                      </div>

                      <div className="space-y-1.5 p-1 bg-slate-950/30 rounded-2xl border border-transparent hover:border-slate-800 transition-colors">
                        <div className="flex items-center gap-1.5 px-2">
                          <span className="text-indigo-500/50">
                            <Calendar size={12} />
                          </span>
                          <label className="text-[9px] font-black text-slate-500 uppercase tracking-tighter">
                            Age Order
                          </label>
                        </div>
                        <select
                          value={potentialFilters.age_order}
                          onChange={(e) => {
                            setPotentialFilters({
                              ...potentialFilters,
                              age_order: e.target.value as
                                | "default"
                                | "asc"
                                | "desc",
                            });
                            setPage(1);
                          }}
                          className="w-full h-9 bg-slate-900 border border-slate-700/30 text-white text-[10px] font-bold px-3 rounded-xl appearance-none cursor-pointer"
                        >
                          <option value="default">Default</option>
                          <option value="asc">Age: Asc</option>
                          <option value="desc">Age: Desc</option>
                        </select>
                      </div>

                      <MultiSelectDropdown
                        label="City"
                        options={Array.from(
                          new Set([
                            ...(allTabOptions.potentialMatches.cities || []),
                            ...(allTabOptions.potentialMatches.states || []),
                          ]),
                        )}
                        selectedValues={potentialFilters.city}
                        onToggle={(val) => {
                          const newCities = potentialFilters.city.includes(val)
                            ? potentialFilters.city.filter((c) => c !== val)
                            : [...potentialFilters.city, val];
                          setPotentialFilters({
                            ...potentialFilters,
                            city: newCities,
                          });
                          setPage(1);
                        }}
                        onReset={() => {
                          setPotentialFilters({
                            ...potentialFilters,
                            city: [],
                          });
                          setPage(1);
                        }}
                        icon={<MapPin size={12} />}
                        searchValue={potentialFilters.city_query}
                        onSearchChange={(value) => {
                          setPotentialFilters({
                            ...potentialFilters,
                            city_query: value,
                          });
                          setPage(1);
                        }}
                      />

                      <MultiSelectDropdown
                        label="Profession"
                        options={Array.from(
                          new Set([
                            ...(allTabOptions.potentialMatches.professions ||
                              []),
                            ...commonProfessionOptions,
                          ]),
                        )}
                        selectedValues={potentialFilters.profession}
                        onToggle={(val) => {
                          const newProfs = potentialFilters.profession.includes(
                            val,
                          )
                            ? potentialFilters.profession.filter(
                              (p) => p !== val,
                            )
                            : [...potentialFilters.profession, val];
                          setPotentialFilters({
                            ...potentialFilters,
                            profession: newProfs,
                          });
                          setPage(1);
                        }}
                        onReset={() => {
                          setPotentialFilters({
                            ...potentialFilters,
                            profession: [],
                          });
                          setPage(1);
                        }}
                        icon={<Briefcase size={12} />}
                        searchValue={potentialFilters.profession_query}
                        onSearchChange={(value) => {
                          setPotentialFilters({
                            ...potentialFilters,
                            profession_query: value,
                          });
                          setPage(1);
                        }}
                      />

                      <div className="space-y-1.5 p-1 bg-slate-950/30 rounded-2xl border border-transparent hover:border-slate-800 transition-colors">
                        <div className="flex items-center gap-1.5 px-2">
                          <span className="text-indigo-500/50">
                            <Layers size={12} />
                          </span>
                          <label className="text-[9px] font-black text-slate-500 uppercase tracking-tighter">
                            Background
                          </label>
                        </div>
                        <select
                          value={potentialFilters.family_background}
                          onChange={(e) => {
                            setPotentialFilters({
                              ...potentialFilters,
                              family_background: e.target.value,
                            });
                            setPage(1);
                          }}
                          className="w-full h-9 bg-slate-900 border border-slate-700/30 text-white text-[10px] font-bold px-3 rounded-xl appearance-none cursor-pointer"
                        >
                          <option value="">All Backgrounds</option>
                          {allTabOptions.potentialMatches.familyBackgrounds.map(
                            (opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ),
                          )}
                        </select>
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <button
                        onClick={() => {
                          setPotentialFilters({
                            name: "",
                            matrimonial_id: "",
                            city: [],
                            city_query: "",
                            profession: [],
                            profession_query: "",
                            family_background: "",
                            age_order: "default",
                          });
                          setPage(1);
                        }}
                        className="px-4 py-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-500 border border-rose-500/20 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 group"
                      >
                        <RefreshCw
                          size={12}
                          className="group-hover:rotate-180 transition-transform duration-500"
                        />
                        Reset Potential
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "Skipped Matchess" && (
                <div className="space-y-4">
                  <div className="p-6 bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-3xl shadow-2xl space-y-6 overflow-visible relative z-[50]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center border border-amber-500/30">
                          <Zap size={18} className="text-amber-400" />
                        </div>
                        <h3 className="text-sm font-black text-white uppercase tracking-widest italic">
                          Skipped Queue Filter
                        </h3>
                      </div>

                      <div className="flex-1 max-w-md mx-4 relative group">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Activity
                            size={12}
                            className="text-amber-500/50 group-focus-within:text-amber-400 transition-colors"
                          />
                        </div>
                        <input
                          type="text"
                          placeholder="Search User ID or Name in Skipped Matches..."
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          className="w-full h-10 bg-slate-950 border border-slate-800 rounded-xl pl-10 pr-4 text-[11px] font-bold text-white placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-amber-500/30 focus:border-amber-500/30 transition-all shadow-inner"
                        />
                        {search && (
                          <button
                            onClick={() => setSearch("")}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-500 hover:text-white transition-colors"
                          >
                            <X size={12} />
                          </button>
                        )}
                      </div>

                      <button
                        onClick={() => {
                          setSkippedFilters({
                            min_age: "",
                            max_age: "",
                            city: [],
                            state: [],
                            family_type: "",
                            family_background: "",
                            profession: [],
                            occupation: [],
                            manglik: [],
                            score_status: "all",
                          });
                          setPage(1);
                        }}
                        className="px-4 py-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-500 border border-rose-500/20 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 group"
                      >
                        <RefreshCw
                          size={12}
                          className="group-hover:rotate-180 transition-transform duration-500"
                        />
                        Reset Skipped
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                      <div className="col-span-1 grid grid-cols-2 gap-2 p-2 bg-slate-950/50 rounded-2xl border border-slate-800/50">
                        <div className="space-y-1">
                          <label className="text-[9px] font-black text-slate-500 uppercase px-1">
                            Min
                          </label>
                          <input
                            type="number"
                            value={skippedFilters.min_age}
                            onChange={(e) => {
                              setSkippedFilters({
                                ...skippedFilters,
                                min_age: e.target.value,
                              });
                              setPage(1);
                            }}
                            className="w-full h-9 bg-slate-900 border border-slate-700/50 text-white text-[11px] font-bold px-3 rounded-xl focus:outline-none"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[9px] font-black text-slate-500 uppercase px-1">
                            Max
                          </label>
                          <input
                            type="number"
                            value={skippedFilters.max_age}
                            onChange={(e) => {
                              setSkippedFilters({
                                ...skippedFilters,
                                max_age: e.target.value,
                              });
                              setPage(1);
                            }}
                            className="w-full h-9 bg-slate-900 border border-slate-700/50 text-white text-[11px] font-bold px-3 rounded-xl focus:outline-none"
                          />
                        </div>
                      </div>

                      <MultiSelectDropdown
                        label="City"
                        options={allTabOptions.skipped.cities}
                        selectedValues={skippedFilters.city}
                        onToggle={(val) => {
                          const newCities = skippedFilters.city.includes(val)
                            ? skippedFilters.city.filter((c) => c !== val)
                            : [...skippedFilters.city, val];
                          setSkippedFilters({
                            ...skippedFilters,
                            city: newCities,
                          });
                          setPage(1);
                        }}
                        onReset={() => {
                          setSkippedFilters({ ...skippedFilters, city: [] });
                          setPage(1);
                        }}
                        icon={<MapPin size={12} />}
                      />
                      <MultiSelectDropdown
                        label="State"
                        options={allTabOptions.skipped.states}
                        selectedValues={skippedFilters.state}
                        onToggle={(val) => {
                          const newStates = skippedFilters.state.includes(val)
                            ? skippedFilters.state.filter((s) => s !== val)
                            : [...skippedFilters.state, val];
                          setSkippedFilters({
                            ...skippedFilters,
                            state: newStates,
                          });
                          setPage(1);
                        }}
                        onReset={() => {
                          setSkippedFilters({ ...skippedFilters, state: [] });
                          setPage(1);
                        }}
                        icon={<MapPin size={12} />}
                      />
                      <MultiSelectDropdown
                        label="Profession"
                        options={allTabOptions.skipped.professions}
                        selectedValues={skippedFilters.profession}
                        onToggle={(val) => {
                          const newProfs = skippedFilters.profession.includes(
                            val,
                          )
                            ? skippedFilters.profession.filter((p) => p !== val)
                            : [...skippedFilters.profession, val];
                          setSkippedFilters({
                            ...skippedFilters,
                            profession: newProfs,
                          });
                          setPage(1);
                        }}
                        onReset={() => {
                          setSkippedFilters({
                            ...skippedFilters,
                            profession: [],
                          });
                          setPage(1);
                        }}
                        icon={<Briefcase size={12} />}
                      />
                      <MultiSelectDropdown
                        label="Occupation"
                        options={allTabOptions.skipped.occupations}
                        selectedValues={skippedFilters.occupation}
                        onToggle={(val) => {
                          const newOccs = skippedFilters.occupation.includes(
                            val,
                          )
                            ? skippedFilters.occupation.filter((o) => o !== val)
                            : [...skippedFilters.occupation, val];
                          setSkippedFilters({
                            ...skippedFilters,
                            occupation: newOccs,
                          });
                          setPage(1);
                        }}
                        onReset={() => {
                          setSkippedFilters({
                            ...skippedFilters,
                            occupation: [],
                          });
                          setPage(1);
                        }}
                        icon={<ShieldCheck size={12} />}
                      />
                      <MultiSelectDropdown
                        label="Manglik"
                        options={allTabOptions.skipped.manglikOptions}
                        selectedValues={skippedFilters.manglik}
                        onToggle={(val) => {
                          const newManglik = skippedFilters.manglik.includes(
                            val,
                          )
                            ? skippedFilters.manglik.filter((o) => o !== val)
                            : [...skippedFilters.manglik, val];
                          setSkippedFilters({
                            ...skippedFilters,
                            manglik: newManglik,
                          });
                          setPage(1);
                        }}
                        onReset={() => {
                          setSkippedFilters({ ...skippedFilters, manglik: [] });
                          setPage(1);
                        }}
                        icon={<Star size={12} />}
                      />
                      {[
                        {
                          label: "Family Type",
                          value: skippedFilters.family_type,
                          options: allTabOptions.skipped.familyTypes,
                          icon: <Users size={12} />,
                          key: "family_type",
                        },
                        {
                          label: "Family BG",
                          value: skippedFilters.family_background,
                          options: allTabOptions.skipped.familyBackgrounds,
                          icon: <Layers size={12} />,
                          key: "family_background",
                        },
                      ].map((f) => (
                        <div
                          key={f.key}
                          className="space-y-1.5 p-1 bg-slate-950/30 rounded-2xl border border-transparent hover:border-slate-800 transition-colors"
                        >
                          <div className="flex items-center gap-1.5 px-2">
                            <span className="text-amber-500/50">{f.icon}</span>
                            <label className="text-[9px] font-black text-slate-500 uppercase tracking-tighter">
                              {f.label}
                            </label>
                          </div>
                          <select
                            value={f.value}
                            onChange={(e) => {
                              setSkippedFilters({
                                ...skippedFilters,
                                [f.key]: e.target.value,
                              });
                              setPage(1);
                            }}
                            className="w-full h-9 bg-slate-900 border border-slate-700/30 text-white text-[10px] font-bold px-3 rounded-xl appearance-none cursor-pointer"
                          >
                            <option value="">All {f.label}s</option>
                            {f.options.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>

                    {/* Score Status Filter Toggle with Counts */}
                    {(() => {
                      const allSkippedRows =
                        tableData["Skipped Matchess"] || [];
                      let failedCount = 0;
                      let passedCount = 0;
                      allSkippedRows.forEach((row: any) => {
                        const reason = row.reason;
                        let score: number | null = null;
                        let isFailed = false;
                        if (
                          reason?.score !== undefined &&
                          reason?.score !== null &&
                          reason?.score !== ""
                        ) {
                          score = parseFloat(reason.score);
                        }
                        if (
                          score === null &&
                          typeof reason?.reason === "string"
                        ) {
                          const m = reason.reason.match(
                            /(?:score|Score)\s*[:(]?\s*(\d+(?:\.\d+)?)/i,
                          );
                          if (m) score = parseFloat(m[1]);
                        }
                        const reasonText =
                          typeof reason === "string"
                            ? reason
                            : reason?.reason || reason?.reason_gap || "";
                        if (
                          typeof reasonText === "string" &&
                          /failed|hard\s*block/i.test(reasonText)
                        )
                          isFailed = true;
                        if (
                          reason?.nonneg?.failed &&
                          Object.keys(reason.nonneg.failed).length > 0
                        )
                          isFailed = true;
                        const isRowFailed =
                          score === 0 || (score === null && isFailed);
                        if (isRowFailed) failedCount++;
                        else passedCount++;
                      });
                      return (
                        <div className="flex items-center gap-3 pt-4 border-t border-slate-800/50 mt-4">
                          <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">
                            Score:
                          </span>
                          {[
                            {
                              key: "all" as const,
                              label: "All",
                              count: allSkippedRows.length,
                              color: "blue",
                              activeClasses:
                                "bg-blue-500/20 text-blue-400 border-blue-500/40",
                              dotColor: "bg-blue-500",
                            },
                            {
                              key: "failed" as const,
                              label: "Failed (0)",
                              count: failedCount,
                              color: "rose",
                              activeClasses:
                                "bg-rose-500/20 text-rose-400 border-rose-500/40",
                              dotColor: "bg-rose-500",
                            },
                            {
                              key: "passed" as const,
                              label: "Score > 0",
                              count: passedCount,
                              color: "emerald",
                              activeClasses:
                                "bg-emerald-500/20 text-emerald-400 border-emerald-500/40",
                              dotColor: "bg-emerald-500",
                            },
                          ].map((btn) => (
                            <button
                              key={btn.key}
                              onClick={() => {
                                setSkippedFilters({
                                  ...skippedFilters,
                                  score_status: btn.key,
                                });
                                setPage(1);
                              }}
                              className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all duration-300 flex items-center gap-2 border ${skippedFilters.score_status === btn.key
                                ? btn.activeClasses
                                : "bg-slate-900 text-slate-500 border-slate-800 hover:text-slate-300 hover:border-slate-700"
                                }`}
                            >
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${btn.dotColor}`}
                              />
                              {btn.label}
                              <span
                                className={`ml-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-mono ${skippedFilters.score_status === btn.key
                                  ? `${btn.activeClasses.split(" ")[0]} opacity-80`
                                  : "bg-slate-800 text-slate-600"
                                  }`}
                              >
                                {btn.count}
                              </span>
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {activeTab === "Transactions" && (
                <div className="space-y-8">
                  {/* Transaction Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      {
                        label: "Total Purchased",
                        value: transactionsData.summary?.total_purchased || 0,
                        color: "text-emerald-400",
                        subColor: "bg-emerald-500/10 border-emerald-500/20",
                        icon: <TrendingUp size={18} />,
                      },
                      {
                        label: "Total Spent",
                        value: transactionsData.summary?.total_spent || 0,
                        color: "text-rose-400",
                        subColor: "bg-rose-500/10 border-rose-500/20",
                        icon: <Zap size={18} />,
                      },
                      {
                        label: "Transaction Count",
                        value: transactionsData.summary?.transaction_count || 0,
                        color: "text-amber-400",
                        subColor: "bg-amber-500/10 border-amber-500/20",
                        icon: <BarChart3 size={18} />,
                      },
                    ].map((stat, i) => (
                      <div
                        key={i}
                        className={`border p-5 rounded-2xl shadow-sm transition-all group ${stat.subColor} hover:shadow-md hover:scale-[1.02] duration-300`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[10px] text-slate-500 uppercase font-black tracking-widest">
                            {stat.label}
                          </span>
                          <span className="opacity-40 group-hover:opacity-100 transition-opacity text-slate-400">
                            {stat.icon}
                          </span>
                        </div>
                        <div
                          className={`text-2xl font-mono font-black ${stat.color}`}
                        >
                          {stat.label.includes("Count")
                            ? stat.value
                            : `${parseFloat(stat.value).toLocaleString()}`}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Usage Overview */}
                  {transactionsData.usage_analytics && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Left: Usage Summary */}
                      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-[2rem] shadow-xl relative overflow-hidden group">
                        <div className="absolute top-0 right-0 p-6 opacity-[0.03] group-hover:opacity-10 transition-opacity">
                          <Activity size={80} />
                        </div>
                        <div className="relative z-10">
                          <h4 className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                            <Activity size={12} className="text-indigo-500" />{" "}
                            Usage Summary
                          </h4>
                          <div className="grid grid-cols-2 gap-6">
                            <div className="space-y-4">
                              <div>
                                <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block mb-1">
                                  Spent
                                </span>
                                <div className="text-2xl font-black text-rose-400 font-mono tracking-tighter">
                                  {transactionsData.usage_analytics
                                    .total_credits_used || 0}
                                </div>
                              </div>
                              <div>
                                <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block mb-1">
                                  Added
                                </span>
                                <div className="text-2xl font-black text-emerald-400 font-mono tracking-tighter">
                                  {transactionsData.usage_analytics
                                    .total_credits_added || 0}
                                </div>
                              </div>
                            </div>
                            <div className="space-y-4">
                              <div>
                                <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block mb-1">
                                  Top Feature
                                </span>
                                <div className="inline-block px-3 py-1 bg-white/5 border border-white/10 rounded-lg text-[10px] font-black text-white uppercase tracking-tight italic">
                                  {transactionsData.usage_analytics.most_used_feature?.replace(
                                    /_/g,
                                    " ",
                                  ) || "NONE"}
                                </div>
                              </div>
                              {transactionsData.usage_analytics
                                .last_usage_date && (
                                  <div>
                                    <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block mb-1">
                                      Last Used
                                    </span>
                                    <div className="text-[10px] text-indigo-400 font-mono font-bold">
                                      {new Date(
                                        transactionsData.usage_analytics
                                          .last_usage_date,
                                      ).toLocaleDateString()}
                                    </div>
                                  </div>
                                )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Right: Feature Breakdown */}
                      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-[2rem] shadow-xl">
                        <h4 className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                          <BarChart3 size={12} className="text-indigo-500" />{" "}
                          Feature Breakdown
                        </h4>
                        <div className="grid grid-cols-2 gap-3 max-h-[120px] overflow-y-auto pr-2 custom-scrollbar">
                          {Object.entries(
                            transactionsData.usage_analytics.usage_by_type ||
                            {},
                          ).map(([feature, data]: [string, any]) => (
                            <div
                              key={feature}
                              className="p-3 bg-slate-950/30 rounded-xl border border-slate-800/50 flex justify-between items-center group"
                            >
                              <div className="flex flex-col min-w-0">
                                <span className="text-[8px] text-slate-500 font-black uppercase truncate">
                                  {feature.replace(/_/g, " ")}
                                </span>
                                <span className="text-[10px] text-slate-300 font-bold">
                                  {data.count}x
                                </span>
                              </div>
                              <span className="text-sm font-black text-indigo-400 font-mono">
                                {data.total_credits}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Activity Logs (detailed_usages) */}
                  {transactionsData.detailed_usages?.length > 0 && (
                    <div className="bg-slate-900/50 border border-slate-800 rounded-[2rem] overflow-hidden shadow-xl">
                      <div className="px-6 py-4 border-b border-slate-800 flex justify-between items-center bg-slate-900/30">
                        <h4 className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.2em] flex items-center gap-2">
                          <Layers size={12} className="text-indigo-500" />{" "}
                          Recent Activity Logs
                        </h4>
                        <span className="text-[9px] text-slate-500 font-bold uppercase">
                          {transactionsData.detailed_usages.length} Entries
                        </span>
                      </div>
                      <div className="overflow-x-auto max-h-[400px] custom-scrollbar">
                        <table className="w-full text-left border-collapse">
                          <thead className="sticky top-0 bg-slate-900 z-10 shadow-sm">
                            <tr>
                              {[
                                "Type",
                                "Target",
                                "Credits",
                                "Wallet Balance",
                                "Timestamp",
                              ].map((h) => (
                                <th
                                  key={h}
                                  className="px-6 py-3 text-[9px] font-black text-slate-500 uppercase tracking-widest"
                                >
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-800/50">
                            {transactionsData.detailed_usages.map(
                              (log: any) => (
                                <tr
                                  key={log.id}
                                  className="hover:bg-white/[0.02] transition-colors group"
                                >
                                  <td className="px-6 py-4">
                                    <div className="flex flex-col">
                                      <span className="text-[10px] font-black text-slate-200 uppercase">
                                        {log.use_type?.replace(/_/g, " ")}
                                      </span>
                                      <span className="text-[9px] text-slate-500 italic">
                                        {log.meta?.action}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className="text-[10px] font-mono text-blue-400">
                                      {log.candidate_id || "SYSTEM"}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className="text-[11px] font-black text-rose-400 font-mono">
                                      -{log.credit_used}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="flex items-center gap-2">
                                      <span className="text-[10px] font-mono text-slate-500">
                                        {log.balance_before}
                                      </span>
                                      <span className="text-slate-700">
                                        â†’
                                      </span>
                                      <span className="text-[10px] font-mono text-emerald-400 font-bold">
                                        {log.balance_after}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className="text-[10px] text-slate-500 whitespace-nowrap">
                                      {new Date(
                                        log.created_at,
                                      ).toLocaleDateString()}{" "}
                                      {new Date(
                                        log.created_at,
                                      ).toLocaleTimeString([], {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                    </span>
                                  </td>
                                </tr>
                              ),
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Search & Filters */}
                  <div className="flex flex-wrap items-center justify-between gap-4 p-5 bg-slate-900 border border-slate-800 rounded-2xl shadow-inner">
                    <div className="flex flex-wrap gap-4 flex-1">
                      <div className="space-y-1.5 flex-1 min-w-[180px]">
                        <label className="text-[9px] text-slate-500 uppercase font-black tracking-widest px-1">
                          Filter by Status
                        </label>
                        <select
                          value={transactionFilters.status}
                          onChange={(e) =>
                            setTransactionFilters({
                              ...transactionFilters,
                              status: e.target.value,
                            })
                          }
                          className="w-full h-10 bg-slate-950 border border-slate-800 text-white text-xs px-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all appearance-none cursor-pointer hover:bg-slate-900"
                        >
                          <option value="">All Statuses</option>
                          <option value="SUCCESS">Success</option>
                          <option value="PENDING">Pending</option>
                          <option value="FAILED">Failed</option>
                        </select>
                      </div>
                      <div className="space-y-1.5 flex-1 min-w-[180px]">
                        <label className="text-[9px] text-slate-500 uppercase font-black tracking-widest px-1">
                          Filter by Type
                        </label>
                        <select
                          value={transactionFilters.transaction_type}
                          onChange={(e) =>
                            setTransactionFilters({
                              ...transactionFilters,
                              transaction_type: e.target.value,
                            })
                          }
                          className="w-full h-10 bg-slate-950 border border-slate-800 text-white text-xs px-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all appearance-none cursor-pointer hover:bg-slate-900"
                        >
                          <option value="">All Types</option>
                          <option value="PURCHASE">Purchase</option>
                          <option value="SPEND_UNLOCK_MATCH">
                            Match Unlock
                          </option>
                          <option value="SPEND_VIEW_EXPIRED">
                            Expired View
                          </option>
                          <option value="ADMIN_GRANT">Admin Grant</option>
                        </select>
                      </div>
                    </div>
                    <button
                      onClick={() =>
                        setTransactionFilters({
                          status: "",
                          transaction_type: "",
                        })
                      }
                      className="h-10 px-6 bg-slate-800 hover:bg-rose-500/10 hover:text-rose-400 hover:border-rose-500/20 text-slate-400 border border-slate-700 rounded-xl text-xs font-black uppercase tracking-widest transition-all active:scale-95 shadow-lg flex items-center gap-2"
                    >
                      Reset Filters
                    </button>
                  </div>
                </div>
              )}

              {/* Workplace Content Segment */}
              <div className="pt-2 space-y-4">
                <div className="flex items-center justify-between py-2 border-b border-slate-800/50">
                  <div className="text-sm font-semibold text-slate-500 uppercase tracking-tighter">
                    Current View: {activeTab}{" "}
                    {activeTab === "Matched Profiles" &&
                      ` - ${matchedSubTab === "AI Matches" ? "ALL" : matchedSubTab === "Outgoing" ? "Like To" : "Liked By"}`}
                  </div>
                  <div className="relative flex gap-2" onBlur={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget as Node)) setBiodataDownloadMenuOpen(false);
                  }} onKeyDown={(event) => {
                    if (event.key === "Escape") setBiodataDownloadMenuOpen(false);
                  }}>
                    <button
                      disabled={downloadingBiodata || !(userIdParams || userDetails.userId)}
                      aria-expanded={biodataDownloadMenuOpen}
                      aria-haspopup="true"
                      onClick={() => setBiodataDownloadMenuOpen(!biodataDownloadMenuOpen)}
                      className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {downloadingBiodata ? <RefreshCw size={16} className="animate-spin" /> : <FileText size={16} />}
                      {downloadingBiodata ? "Preparing Biodata…" : "Download Biodata"}
                      <ChevronDown size={14} />
                    </button>
                    {biodataDownloadMenuOpen && (
                      <div className="absolute right-0 top-full mt-2 z-50 w-48 rounded-xl border border-slate-700 bg-slate-900 p-1 shadow-xl">
                        {(["original", "custom"] as const).map((kind) => (
                          <button key={kind} className="w-full rounded-lg px-3 py-2 text-left text-sm text-white hover:bg-slate-800 focus:bg-slate-800" onClick={async () => {
                        if (downloadingBiodata) return;
                        setBiodataDownloadMenuOpen(false);
                        setDownloadingBiodata(true);
                        try {
                          const userId = userIdParams || userDetails.userId;
                          const response = await ApiRequest("POST", `admin/v2/users/${encodeURIComponent(userId)}/biodata-download?kind=${kind}`);
                          if (!response?.status || !response?.data?.url) return;
                          const link = document.createElement("a");
                          link.href = response.data.url;
                          link.download = response.data.filename;
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                        } catch {
                          toast.error("Could not download biodata. Please try again.");
                        } finally {
                          setDownloadingBiodata(false);
                        }
                          }}>
                            {kind === "original" ? "Original Biodata" : "Custom Biodata"}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* The Profile or Data Table */}
                {activeTab === "Full Profile" ? (
                  <IndividualProfileView
                    profile={userProfileData?.profile || {}}
                    preference={userProfileData?.preference || {}}
                    photos={userProfileData?.photos || []}
                    biodata={userProfileData?.bioData || ""}
                    preferenceCategories={preferenceCategories}
                  />
                ) : (
                  <>
                    <div
                      className="rounded-xl border border-slate-800 max-h-[70vh] overflow-y-auto overflow-x-auto custom-scrollbar"
                    >
                      <TableCardView
                        data={paginatedData || []}
                        columns={
                          activeTab === "Matched Profiles"
                            ? matchedSubTab === "Outgoing"
                              ? columnMap["Like To"]
                              : matchedSubTab === "Incoming"
                                ? columnMap["Liked By"]
                                : columnMap[activeTab]
                            : columnMap[activeTab]
                        }
                        view="table"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        classtype={
                          activeTab === "Matched Profiles"
                            ? matchedSubTab === "Outgoing"
                              ? "liked_matches_hub"
                              : matchedSubTab === "Incoming"
                                ? "liked_by_hub"
                                : mappingClassType[activeTab]
                            : mappingClassType[activeTab]
                        }
                        onRowClick={handleRowClick}
                      />
                    </div>

                    {!(
                      activeTab === "Matched Profiles" &&
                      matchedSubTab !== "AI Matches"
                    ) &&
                      !["Full Profile", "Transactions"].includes(activeTab) && (
                        <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-6 p-4 border-t border-slate-800 bg-slate-900/10 rounded-xl">
                          <div className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">
                            Registry Results: {sortedData.length} Records
                            detected
                          </div>
                          <div className="flex flex-wrap items-center justify-center gap-3">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setPage(Math.max(1, page - 1))}
                                disabled={page === 1}
                                className="h-8 px-3 bg-slate-900 border border-slate-800 text-slate-400 rounded hover:text-white transition-all disabled:opacity-20"
                              >
                                &lt;
                              </button>
                              {(() => {
                                const totalPages = Math.ceil(
                                  sortedData.length / rowsPerPage,
                                );
                                const pages = [];
                                const showEllipsisStart = page > 4;
                                const showEllipsisEnd = page < totalPages - 3;
                                if (totalPages <= 7) {
                                  for (let i = 1; i <= totalPages; i++)
                                    pages.push(i);
                                } else {
                                  pages.push(1);
                                  if (showEllipsisStart) pages.push("...");
                                  let start = Math.max(2, page - 1);
                                  let end = Math.min(totalPages - 1, page + 1);
                                  if (!showEllipsisStart) {
                                    start = 2;
                                    end = 5;
                                  }
                                  if (!showEllipsisEnd) {
                                    end = totalPages - 1;
                                    start = totalPages - 4;
                                  }
                                  for (let i = start; i <= end; i++)
                                    pages.push(i);
                                  if (showEllipsisEnd) pages.push("...");
                                  pages.push(totalPages);
                                }
                                return pages.map((p, idx) => (
                                  <button
                                    key={idx}
                                    onClick={() =>
                                      typeof p === "number" && setPage(p)
                                    }
                                    disabled={p === "..."}
                                    className={`h-8 w-8 text-[11px] font-bold rounded transiton-all ${page === p
                                      ? "bg-white text-black"
                                      : p === "..."
                                        ? "bg-transparent text-slate-600"
                                        : "bg-slate-900 border border-slate-800 text-slate-400 hover:text-white"
                                      }`}
                                  >
                                    {p}
                                  </button>
                                ));
                              })()}
                              <button
                                onClick={() =>
                                  setPage(
                                    Math.min(
                                      Math.ceil(
                                        sortedData.length / rowsPerPage,
                                      ),
                                      page + 1,
                                    ),
                                  )
                                }
                                disabled={
                                  page >=
                                  Math.ceil(sortedData.length / rowsPerPage)
                                }
                                className="h-8 px-3 bg-slate-900 border border-slate-800 text-slate-400 rounded hover:text-white transition-all disabled:opacity-20"
                              >
                                &gt;
                              </button>
                            </div>

                            <div className="flex items-center gap-3 border-l border-slate-800 pl-4 h-8">
                              <div className="flex items-center gap-2">
                                <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest whitespace-nowrap">
                                  Limit:
                                </span>
                                <select
                                  value={rowsPerPage}
                                  onChange={(e) => {
                                    setRowsPerPage(Number(e.target.value));
                                    setPage(1);
                                  }}
                                  className="h-7 bg-slate-900 border border-slate-800 text-[10px] font-black text-blue-500 px-2 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500/50 uppercase transition-all cursor-pointer"
                                >
                                  {[10, 20, 50, 100, 500, 1000].map((val) => (
                                    <option key={val} value={val}>
                                      {val}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div className="h-4 w-px bg-slate-800 mx-1"></div>

                              <div className="flex items-center gap-2">
                                <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest whitespace-nowrap">
                                  Jump:
                                </span>
                                <select
                                  value={page}
                                  onChange={(e) =>
                                    setPage(Number(e.target.value))
                                  }
                                  className="h-7 bg-slate-900 border border-slate-800 text-[10px] font-black text-blue-500 px-2 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500/50 uppercase transition-all cursor-pointer"
                                >
                                  {Array.from(
                                    {
                                      length: Math.ceil(
                                        sortedData.length / rowsPerPage,
                                      ),
                                    },
                                    (_, i) => i + 1,
                                  ).map((p) => (
                                    <option key={p} value={p}>
                                      P{p}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Overlays & Modals */}
        <ManualMatchProcessMainModel
          isOpen={models.manaulMatchProcessMainModal}
          data={{ uid: userDetails, cid: models.row }}
          models={models}
          setModels={setModels}
        />

        <LogViewer
          userId={"admin"}
          isOpen={showLogs.common}
          onClose={() => setShowLogs({ common: false, userLogs: false })}
        />

        <LogViewer
          userId={userId}
          isOpen={showLogs.userLogs}
          onClose={() => setShowLogs({ common: false, userLogs: false })}
        />

        <AstroMatchModal
          data={showOppositeMatch ? oppositeAstroModalData : astroModalData}
          matchedTags={
            showOppositeMatch
              ? (oppositeAstroModalData?.matchedTags ?? [])
              : (astroModalData?.matchedTags ?? [])
          }
          summary={
            showOppositeMatch
              ? (oppositeAstroModalData?.summary ?? {})
              : (astroModalData?.summary ?? {})
          }
          isOpen={showAstroModal}
          onClose={() => {
            setShowAstroModal(false);
            setAstroModalData(DEFAULT_ASTRO_DATA);
            setOppositeAstroModalData(DEFAULT_ASTRO_DATA);

            if (
              comparisonModal.type === "match" ||
              !comparisonModal.type.trim()
            ) {
              setComparisonModal({ status: false, type: "" });
              setLocalItem("CandidateProfileDetails", "");
              setLocalItem("UserProfileDetails", "");
            }
          }}
          onPublish={hanldePublish}
          onResetMatch={handleResetMatch}
          onViewOppositeMatch={handleViewOppositeMatch}
          onUpdateDecisionToYes={handleUpdateDecisionToYes}
          onComparisionProfileClick={handleFetchBothProfileDetails}
          onPhotoComparison={handlePhotoComparison}
        />

        <ComparisonMatrimonialProfileModal
          isOpen={comparisonModal.status}
          onClose={() => {
            if (comparisonModal.type === "queue") {
              setLocalItem("CandidateProfileDetails", "");
              setLocalItem("UserProfileDetails", "");
            }
            setComparisonModal({ status: false, type: "" });
          }}
          profile1={userProfileData?.profile || {}}
          preference1={userProfileData?.preference || {}}
          profile2={candidateProfileData?.profile || {}}
          preference2={candidateProfileData?.preference || {}}
          sammelanRegistrations1={userProfileData?.sammelanRegistrations || []}
          sammelanRegistrations2={
            candidateProfileData?.sammelanRegistrations || []
          }
        />

        <JsomPromptMatchMakingModal
          isOpen={showMatchMakingModal}
          title={matchMakingStepText}
          prompt={matchMakingPrompt}
          onClose={() => setShowMatchMakingModal(false)}
          onSubmit={handleMatchMakingSubmit}
          onSkip={handleMatchMakingSkip}
        />

        <ConfirmationModal
          title="Publication Quality Check"
          maxWidth="max-w-3xl"
          description={
            <div className="space-y-3 text-left">
              <div className="flex items-center justify-center gap-3 p-3 bg-yellow-500/5 border border-yellow-500/20 rounded-xl">
                <Zap
                  size={18}
                  className="text-yellow-400 animate-pulse shrink-0"
                />
                <div>
                  <p className="font-black text-white text-xs uppercase tracking-widest">
                    Are you sure you want to publish this match?
                  </p>
                  <p className="text-[9px] text-slate-500 font-bold uppercase tracking-[0.2em] mt-0.5 italic">
                    Verify profile against instructions below
                  </p>
                </div>
              </div>

              {/* ===== GATE VIOLATIONS (what publishing this match would override) ===== */}
              {publishViolations &&
                (Object.keys(publishViolations.blocking || {}).length > 0 ||
                  Object.keys(publishViolations.overridable || {}).length > 0) && (
                  <div className="mb-3 space-y-2">
                    {/* Hard blocks — can NEVER be overridden */}
                    {Object.keys(publishViolations.blocking || {}).length > 0 && (
                      <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-3">
                        <p className="text-[9px] font-black uppercase tracking-[0.2em] text-rose-400 mb-1">
                          🚫 Cannot publish — this can never be overridden
                        </p>
                        <ul className="list-disc list-inside space-y-0.5">
                          {Object.entries(publishViolations.blocking).map(
                            ([k, v]: any) => (
                              <li key={k} className="text-[10px] text-rose-300 font-semibold">
                                {String(v)}
                              </li>
                            ),
                          )}
                        </ul>
                        <p className="text-[8px] text-rose-400/70 italic mt-1">
                          A user disliked or blocked this match. Publishing is disabled.
                        </p>
                      </div>
                    )}

                    {/* Waivable issues — admin may override WITH a recorded reason */}
                    {Object.keys(publishViolations.overridable || {}).length > 0 && (
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3">
                        <p className="text-[9px] font-black uppercase tracking-[0.2em] text-amber-400 mb-1">
                          ⚠️ Issues found — you can override (admin responsibility)
                        </p>
                        <ul className="list-disc list-inside space-y-0.5">
                          {Object.entries(publishViolations.overridable).map(
                            ([k, v]: any) => (
                              <li key={k} className="text-[10px] text-amber-200 font-semibold">
                                {String(v)}
                              </li>
                            ),
                          )}
                        </ul>
                        {publishViolations.can_override !== false && (
                          <input
                            type="text"
                            value={publishOverrideReason}
                            onChange={(e) => setPublishOverrideReason(e.target.value)}
                            placeholder="Reason for overriding (required)…"
                            className="mt-2 w-full bg-slate-950/60 border border-amber-500/30 rounded-lg px-3 py-2 text-[11px] text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-400"
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}

              {publishInstructionLoading ? (
                <div className="flex items-center justify-center py-6">
                  <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  <span className="ml-3 text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                    Loading data...
                  </span>
                </div>
              ) : publishInstructionData ? (
                <div className="grid grid-cols-2 gap-3">
                  {/* ===== USER COLUMN ===== */}
                  <div className="space-y-2 bg-slate-950/30 p-3 rounded-xl border border-white/5">
                    <p className="text-[9px] font-black uppercase tracking-[0.3em] text-cyan-400 border-b border-cyan-500/20 pb-1">
                      ðŸ‘¤ Main User
                    </p>

                    {userProfileData.sammelanRegistrations &&
                      userProfileData.sammelanRegistrations.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1 mb-2">
                          {userProfileData.sammelanRegistrations.map(
                            (reg: any, i: number) => (
                              <div
                                key={i}
                                className={`px-2 py-0.5 rounded text-[7px] font-black uppercase tracking-tighter border ${reg.is_registered ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20 shadow-[0_0_5px_rgba(16,185,129,0.1)]" : "bg-rose-500/10 text-rose-500 border-rose-500/20 opacity-40"}`}
                              >
                                {reg.event_name}
                              </div>
                            ),
                          )}
                        </div>
                      )}

                    {(publishInstructionData.user?.instructions?.length || 0) >
                      0 && (
                        <div>
                          <p className="text-[9px] font-black uppercase tracking-[0.15em] text-blue-400 mb-1 flex items-center gap-1">
                            <FileText size={10} /> Instructions
                          </p>
                          <div className="space-y-1 max-h-[100px] overflow-y-auto custom-scrollbar pr-1">
                            {publishInstructionData.user.instructions.map(
                              (ins: any, i: number) => (
                                <div key={i} className="flex gap-1.5">
                                  <div className="w-1 h-1 rounded-full bg-slate-600 mt-1.5 shrink-0" />
                                  <p className="text-[10px] text-slate-300 leading-snug font-medium">
                                    {typeof ins === "string"
                                      ? ins
                                      : ins.instruction ||
                                      ins.text ||
                                      JSON.stringify(ins)}
                                  </p>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}

                    {(publishInstructionData.user?.dislike_reasons?.length ||
                      0) > 0 && (
                        <div>
                          <p className="text-[9px] font-black uppercase tracking-[0.15em] text-rose-400 mb-1 flex items-center gap-1">
                            <X size={10} /> Dislikes
                          </p>
                          <div className="space-y-1 max-h-[100px] overflow-y-auto custom-scrollbar pr-1">
                            {publishInstructionData.user.dislike_reasons.map(
                              (dis: any, i: number) => (
                                <div
                                  key={i}
                                  className="bg-rose-500/5 p-1.5 rounded-lg border border-rose-500/10"
                                >
                                  <div className="flex justify-between items-center">
                                    <span className="text-[8px] text-rose-400 font-black uppercase">
                                      {dis.name || "Unknown"}
                                    </span>
                                    <span className="text-[7px] text-slate-600 font-mono">
                                      #{dis.matrimonial_id || "N/A"}
                                    </span>
                                  </div>
                                  <p className="text-[10px] text-slate-300 leading-snug font-medium italic">
                                    "{dis.reason}"
                                  </p>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}

                    {!publishInstructionData.user?.instructions?.length &&
                      !publishInstructionData.user?.dislike_reasons?.length && (
                        <p className="text-slate-600 italic text-[9px]">
                          No instructions or dislikes.
                        </p>
                      )}
                  </div>

                  {/* ===== CANDIDATE COLUMN ===== */}
                  <div className="space-y-2 bg-slate-950/30 p-3 rounded-xl border border-white/5">
                    <p className="text-[9px] font-black uppercase tracking-[0.3em] text-amber-400 border-b border-amber-500/20 pb-1">
                      ðŸ¤ Candidate
                    </p>

                    {publishInstructionData.candidate?.sammelan_registrations &&
                      publishInstructionData.candidate.sammelan_registrations
                        .length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1 mb-2">
                          {publishInstructionData.candidate.sammelan_registrations.map(
                            (reg: any, i: number) => (
                              <div
                                key={i}
                                className={`px-2 py-0.5 rounded text-[7px] font-black uppercase tracking-tighter border ${reg.is_registered ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20 shadow-[0_0_5px_rgba(16,185,129,0.1)]" : "bg-rose-500/10 text-rose-500 border-rose-500/20 opacity-40"}`}
                              >
                                {reg.event_name}
                              </div>
                            ),
                          )}
                        </div>
                      )}

                    {(publishInstructionData.candidate?.instructions?.length ||
                      0) > 0 && (
                        <div>
                          <p className="text-[9px] font-black uppercase tracking-[0.15em] text-blue-400 mb-1 flex items-center gap-1">
                            <FileText size={10} /> Instructions
                          </p>
                          <div className="space-y-1 max-h-[100px] overflow-y-auto custom-scrollbar pr-1">
                            {publishInstructionData.candidate.instructions.map(
                              (ins: any, i: number) => (
                                <div key={i} className="flex gap-1.5">
                                  <div className="w-1 h-1 rounded-full bg-slate-600 mt-1.5 shrink-0" />
                                  <p className="text-[10px] text-slate-300 leading-snug font-medium">
                                    {typeof ins === "string"
                                      ? ins
                                      : ins.instruction ||
                                      ins.text ||
                                      JSON.stringify(ins)}
                                  </p>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}

                    {(publishInstructionData.candidate?.dislike_reasons
                      ?.length || 0) > 0 && (
                        <div>
                          <p className="text-[9px] font-black uppercase tracking-[0.15em] text-rose-400 mb-1 flex items-center gap-1">
                            <X size={10} /> Dislikes
                          </p>
                          <div className="space-y-1 max-h-[100px] overflow-y-auto custom-scrollbar pr-1">
                            {publishInstructionData.candidate.dislike_reasons.map(
                              (dis: any, i: number) => (
                                <div
                                  key={i}
                                  className="bg-rose-500/5 p-1.5 rounded-lg border border-rose-500/10"
                                >
                                  <div className="flex justify-between items-center">
                                    <span className="text-[8px] text-rose-400 font-black uppercase">
                                      {dis.name || "Unknown"}
                                    </span>
                                    <span className="text-[7px] text-slate-600 font-mono">
                                      #{dis.matrimonial_id || "N/A"}
                                    </span>
                                  </div>
                                  <p className="text-[10px] text-slate-300 leading-snug font-medium italic">
                                    "{dis.reason}"
                                  </p>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}

                    {!publishInstructionData.candidate?.instructions?.length &&
                      !publishInstructionData.candidate?.dislike_reasons
                        ?.length && (
                        <p className="text-slate-600 italic text-[9px]">
                          No instructions or dislikes.
                        </p>
                      )}
                  </div>
                </div>
              ) : (
                <p className="text-slate-500 italic text-[10px] text-center py-4 flex flex-col items-center gap-1">
                  <ShieldCheck size={16} className="text-slate-700" />
                  No specific flags found.
                </p>
              )}
            </div>
          }
          isOpen={confirmPublishModal}
          onClose={() => setConfirmPublishModal(false)}
          onConfirm={async () => {
            const v = publishViolations;
            // Hard floor: a user's own dislike or a block/report can never be overridden.
            if (v && v.can_override === false) {
              toast.error(
                "Cannot publish — a user has disliked or blocked this match. This can never be overridden.",
              );
              return; // keep modal open
            }
            // Waivable violations (manglik / caste / marital / income / age / prefs) require a reason.
            if (v && v.needs_override) {
              if (publishOverrideReason.trim().length < 3) {
                toast.error("Enter a reason to override the flagged issues before publishing.");
                return; // keep modal open
              }
              setConfirmPublishModal(false);
              if (publishPending?.type === "match_row") {
                await actualHandlePublishMatchOverride(
                  publishPending.row,
                  publishOverrideReason.trim(),
                );
              }
              setPublishPending(null);
              return;
            }
            // No violations -> normal publish path.
            setConfirmPublishModal(false);
            if (publishPending?.type === "individual") {
              await actualHandlePublish(publishPending.candidateId);
            } else if (publishPending?.type === "match_row") {
              await actualHandlePublishMatch(publishPending.row);
            }
            setPublishPending(null);
          }}
        />

        <ConfirmationModal
          title={
            `Delete - ${userDetails.name} UserId: ` +
            (userIdParams || userDetails.userId)
          }
          description={`Are you sure you want to permanently delete user ${userIdParams || userDetails.userId}? This operation is irreversible.`}
          variant="danger"
          isOpen={confirmDeleteModal}
          onClose={() => {
            setConfirmDeleteModal(false);
          }}
          onConfirm={() => {
            deleteCurrentUser();
          }}
        />

        <ConfirmationModal
          title={
            removeSammelanTarget
              ? `Remove from Sammelan: ${removeSammelanTarget.eventName}`
              : "Remove from Sammelan"
          }
          description={
            removeSammelanTarget
              ? `Are you sure you want to remove this user from ${removeSammelanTarget.eventName}?`
              : "Are you sure you want to remove this user from the selected Sammelan event?"
          }
          variant="danger"
          isOpen={showRemoveSammelanModal}
          onClose={() => setShowRemoveSammelanModal(false)}
          onConfirm={confirmRemoveSammelan}
        />

        <ConfirmationModal
          title={
            `Enable - ${userDetails.name} UserId: ` +
            (userIdParams || userDetails.userId)
          }
          description={`Are you sure you want to Enable (reactivate) user ${userIdParams || userDetails.userId}?`}
          variant="danger"
          isOpen={confirmEnableModal}
          onClose={() => {
            setConfirmEnableModal(false);
          }}
          onConfirm={() => {
            enableCurrentUser();
          }}
        />

        <ConfirmationModal
          title={
            `Disable - ${userDetails.name} UserId: ` +
            (userIdParams || userDetails.userId)
          }
          description={`Are you sure you want to Disable (suspend) user ${userIdParams || userDetails.userId}?`}
          variant="green"
          isOpen={confirmDisableModal}
          onClose={() => {
            setConfirmDisableModal(false);
          }}
          onConfirm={() => {
            disableCurrentUser();
          }}
        />

        {holdUserModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm">
            <div className="w-full max-w-md rounded-2xl border border-white/10 bg-slate-950 p-6 text-white shadow-2xl">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.3em] text-blue-400">
                    Hold User
                  </p>
                  <h2 className="mt-2 text-xl font-bold">
                    {userDetails.name || "Selected profile"}
                  </h2>
                  <p className="mt-1 text-xs text-slate-500">
                    ID: {userIdParams || userDetails.userId}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setHoldUserModal(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-slate-400 transition hover:bg-white/10 hover:text-white"
                  aria-label="Close hold user modal"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="mt-5 space-y-4">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
                    Hold Start Date
                  </label>
                  <input
                    type="text"
                    value={displayHoldDate}
                    readOnly
                    className="mt-2 w-full rounded-xl border border-slate-800 bg-slate-900/70 px-3 py-3 text-sm font-bold text-slate-200 outline-none"
                  />
                </div>

                <div>
                  <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
                    Hold Duration
                  </label>
                  <div className="mt-2 flex overflow-hidden rounded-xl border border-slate-800 bg-slate-900/70">
                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={holdDurationDays}
                      onChange={(e) => setHoldDurationDays(e.target.value)}
                      className="min-w-0 flex-1 bg-transparent px-3 py-3 text-sm font-bold text-white outline-none"
                    />
                    <span className="flex items-center border-l border-slate-800 px-4 text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
                      Days
                    </span>
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
                    Hold Reason (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Going on vacation"
                    value={holdReason}
                    onChange={(e) => setHoldReason(e.target.value)}
                    className="mt-2 w-full rounded-xl border border-slate-800 bg-slate-900/70 px-3 py-3 text-sm font-bold text-white placeholder-slate-600 outline-none focus:border-blue-500/50 transition-colors"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setHoldUserModal(false)}
                  className="rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-bold text-slate-200 transition hover:bg-white/10"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={holdCurrentUser}
                  className="rounded-xl bg-blue-600 px-4 py-2 text-xs font-bold text-white shadow-lg shadow-blue-600/20 transition hover:bg-blue-500"
                >
                  Hold User
                </button>
              </div>
            </div>
          </div>
        )}

        <ConfirmationModal
          title={`Add payment for - ${userDetails.name} (ID: ${userIdParams || userDetails.userId})`}
          description={`Are you sure you want to proceed with adding a payment for ${userDetails.name}?`}
          variant="success"
          isOpen={confirmAddPaymentModal}
          onClose={() => {
            setConfirmAddPaymentModal(false);
          }}
          onConfirm={() => {
            setConfirmAddPaymentModal(false);
            setShowPaymentModal(true);
          }}
        />

        <ConfirmationModal
          title={`Send all matches to ${userDetails.name} (ID: ${userIdParams || userDetails.userId})?`}
          description={`This will send all matched profiles to ${userDetails.name} via WhatsApp. Do you want to continue?`}
          variant="success"
          isOpen={confirmAddWhatsappPdfModal}
          onClose={() => setConfirmAddWhatsappPdfModal(false)}
          onConfirm={async () => {
            setConfirmAddWhatsappPdfModal(false);
            await PostWhatsappPdfMatches({
              uid: userIdParams || userDetails.userId,
              service: "whatsapp_pdf_template",
            });
          }}
        />

        <ConfirmationModal
          title={`Verify Caste - ${userDetails.name} (ID: ${userIdParams || userDetails.userId})`}
          description={`Are you sure you want to verify and approve the caste for ${userDetails.name}? This will mark the user as verified.`}
          variant="success"
          isOpen={confirmVerifyModal}
          onClose={() => setConfirmVerifyModal(false)}
          onConfirm={handleVerifyUser}
        />

        <ConfirmationModal
          title="Deactivate Pair"
          description={`Are you sure you want to deactivate the pair for candidate ${pendingDeactivateCandidateId}? This will set is_active=false across matched, queued and skipped tables.`}
          variant="danger"
          isOpen={confirmDeactivatePairModal}
          onClose={() => {
            setConfirmDeactivatePairModal(false);
            setPendingDeactivateCandidateId("");
          }}
          onConfirm={async () => {
            const candidateId = pendingDeactivateCandidateId;
            setConfirmDeactivatePairModal(false);
            setPendingDeactivateCandidateId("");
            if (!candidateId) return;
            await handleDeactivatePair(candidateId);
          }}
        />

        <SimpleMessageModal
          show={errorModal.show}
          onClose={() => setErrorModal((prev) => ({ ...prev, show: false }))}
          message={errorModal.message}
          variant={errorModal.variant}
        />

        {/* Reset Password Modal */}
        <ConfirmationModal
          title={`Reset Password - ${userDetails.name}`}
          description={
            <div className="space-y-4">
              <p className="text-slate-300">
                Enter a new password for {userDetails.name} (ID:{" "}
                {userIdParams || userDetails.userId})
              </p>
              <input
                type="text"
                placeholder="Enter new password (min 6 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                minLength={6}
              />
              {newPassword && newPassword.length < 6 && (
                <p className="text-rose-400 text-xs">
                  Password must be at least 6 characters long
                </p>
              )}
            </div>
          }
          variant="warning"
          isOpen={resetPasswordModal}
          onClose={() => {
            setResetPasswordModal(false);
            setNewPassword("");
          }}
          onConfirm={async () => {
            if (newPassword.length < 6) {
              toast.error("Password must be at least 6 characters long");
              return;
            }
            try {
              LoaderService.show();
              const response = await ApiRequest(
                "POST",
                "admin/v2/reset-user-password",
                {
                  user_id: userIdParams || userDetails.userId,
                  new_password: newPassword,
                } as any,
              );
              if (response.status === true) {
                toast.success(
                  response.message || "Password reset successfully",
                );
                setResetPasswordModal(false);
                setNewPassword("");
              } else {
                toast.error(response.message || "Failed to reset password");
              }
            } catch (error: any) {
              toast.error("Error resetting password");
            } finally {
              LoaderService.hide();
            }
          }}
        />

        <ConfirmationModal
          title={`Reset Matchmaking Data - ${userDetails.name}`}
          description={`Are you sure you want to RE-CALCULATE and RESET all matchmaking results for ${userDetails.name} (ID: ${userIdParams || userDetails.userId})? This will start fresh calculations.`}
          variant="danger"
          isOpen={confirmResetMatchingModal}
          onClose={() => setConfirmResetMatchingModal(false)}
          onConfirm={handleResetMatchingData}
        />

        {/* Reason Modal */}
        <EditableModal
          isOpen={models.reasonAndGapModal}
          onCancel={() => {
            setModels((prev) => ({ ...prev, reasonAndGapModal: false }));
          }}
          heading="Reason"
          inputHeading1="Reason"
          inputHeading2="Gap"
          value={{
            title:
              models?.row?.reason?.reason ||
              models?.row?.reason?.overview ||
              (typeof models?.row?.reason === "string"
                ? models?.row?.reason
                : ""),
            description: models?.row?.reason?.gap || "",
          }}

        // onSave={() => { }}
        />

        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          onConfirm={async (packageId: number) => {
            try {
              LoaderService.show();
              const response = await PostAddCreditsToUser({
                user_id: userIdParams || userDetails.userId,
                package_id: packageId,
                admin_note: "Manual Admin Credit Grant",
              });
              LoaderService.hide();
              if (response?.status === "success" || response?.status === true) {
                toast.success("Credits added successfully");
                refreshData(); // Refresh to show new credits
              } else {
                toast.error(response?.message || "Failed to add credits");
              }
            } catch (error) {
              LoaderService.hide();
              console.error("AddCredits error:", error);
              toast.error("Error adding credits");
            }
            setShowPaymentModal(false);
          }}
          title={`Add Credit Package - ${userDetails.name}`}
        // userPlatform={userDetails.platform}
        />

        {/* Photo Comparison Modal */}
        {showPhotoComparison && (
          <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/90 backdrop-blur-xl animate-fade-in">
            <div className="bg-slate-900 border border-slate-700/50 w-full max-w-6xl max-h-[90vh] rounded-[2rem] shadow-2xl overflow-hidden flex flex-col scale-in text-slate-200">
              {/* Header */}
              <div className="flex-shrink-0 p-8 border-b border-slate-800 bg-slate-900/50 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-indigo-500/20 text-indigo-400 rounded-2xl border border-indigo-500/20 shadow-inner">
                    <Camera size={28} strokeWidth={2.5} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-black text-white tracking-tight uppercase">
                      High-Definition Photo Comparison
                    </h3>
                    <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.3em] mt-1">
                      Biometric & Personality Projection Analysis
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPhotoComparison(false)}
                  className="p-3 hover:bg-slate-800 rounded-2xl text-slate-500 hover:text-rose-500 transition-all active:scale-90"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-12 custom-scrollbar">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 relative">
                  {/* VS Indicator */}
                  <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-slate-950 border-2 border-slate-800 items-center justify-center z-10 shadow-[0_0_30px_rgba(0,0,0,0.8)]">
                    <span className="text-indigo-400 text-xs font-black tracking-widest">
                      VS
                    </span>
                  </div>

                  {/* User Photo Node */}
                  <div className="space-y-6">
                    <div className="flex items-center justify-between px-2">
                      <div>
                        <h4 className="text-sm font-black text-white uppercase tracking-wider">
                          {userDetails.name}
                        </h4>
                        <p className="text-[9px] text-indigo-400 font-bold uppercase tracking-[0.2em] mt-0.5">
                          Primary Principal
                        </p>
                      </div>
                      <div className="px-3 py-1 bg-slate-800/50 rounded-lg text-[9px] font-black text-slate-500 uppercase tracking-widest border border-slate-700/50">
                        Zoom: {(userPhotoZoom * 100).toFixed(0)}%
                      </div>
                    </div>

                    <div className="relative group/photo">
                      <div className="bg-slate-950 border border-slate-800 rounded-[2rem] overflow-hidden shadow-2xl relative h-[500px]">
                        <div
                          className="w-full h-full flex items-center justify-center cursor-grab active:cursor-grabbing"
                          onWheel={(e) => handlePhotoWheel(e, true)}
                          onMouseDown={(e) => handleMouseDown(e, true)}
                          onMouseMove={(e) => handleMouseMove(e, true)}
                          onMouseUp={handleMouseUp}
                          onMouseLeave={handleMouseUp}
                        >
                          {userDetails.photo ? (
                            <img
                              src={userDetails.photo}
                              alt={userDetails.name}
                              className="max-w-none transition-transform duration-75 select-none"
                              style={{
                                transform: `translate(${userPhotoPos.x}px, ${userPhotoPos.y}px) scale(${userPhotoZoom})`,
                                transformOrigin: "center",
                              }}
                            />
                          ) : (
                            <div className="flex flex-col items-center gap-4 opacity-20">
                              <X size={64} className="text-slate-500" />
                              <span className="text-[10px] font-black uppercase tracking-widest">
                                Spectral Data Missing
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Overlays */}
                        <div className="absolute top-4 left-4 p-3 bg-black/40 backdrop-blur-md rounded-xl border border-white/5 pointer-events-none">
                          <div className="flex gap-1.5">
                            {[1, 2, 3].map((i) => (
                              <div
                                key={i}
                                className="w-1 h-1 rounded-full bg-indigo-500/50"
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                      <p className="text-center text-[9px] text-slate-600 font-medium mt-4 italic uppercase tracking-widest">
                        Scroll to Zoom â€¢ Drag to Inspect Detail
                      </p>
                    </div>
                  </div>

                  {/* Candidate Photo Node */}
                  <div className="space-y-6">
                    <div className="flex items-center justify-between px-2">
                      <div>
                        <h4 className="text-sm font-black text-white uppercase tracking-wider">
                          {astroModalData?.profile?.name || "Candidate Node"}
                        </h4>
                        <p className="text-[9px] text-amber-400 font-bold uppercase tracking-[0.2em] mt-0.5">
                          Match Candidate
                        </p>
                      </div>
                      <div className="px-3 py-1 bg-slate-800/50 rounded-lg text-[9px] font-black text-slate-500 uppercase tracking-widest border border-slate-700/50">
                        Zoom: {(matchedPhotoZoom * 100).toFixed(0)}%
                      </div>
                    </div>

                    <div className="relative group/photo">
                      <div className="bg-slate-950 border border-slate-800 rounded-[2rem] overflow-hidden shadow-2xl relative h-[500px]">
                        <div
                          className="w-full h-full flex items-center justify-center cursor-grab active:cursor-grabbing"
                          onWheel={(e) => handlePhotoWheel(e, false)}
                          onMouseDown={(e) => handleMouseDown(e, false)}
                          onMouseMove={(e) => handleMouseMove(e, false)}
                          onMouseUp={handleMouseUp}
                          onMouseLeave={handleMouseUp}
                        >
                          {astroModalData?.profile?.photo ? (
                            <img
                              src={astroModalData.profile.photo}
                              alt="Candidate"
                              className="max-w-none transition-transform duration-75 select-none"
                              style={{
                                transform: `translate(${matchedPhotoPos.x}px, ${matchedPhotoPos.y}px) scale(${matchedPhotoZoom})`,
                                transformOrigin: "center",
                              }}
                            />
                          ) : (
                            <div className="flex flex-col items-center gap-4 opacity-20">
                              <X size={64} className="text-slate-500" />
                              <span className="text-[10px] font-black uppercase tracking-widest">
                                Spectral Data Missing
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Overlays */}
                        <div className="absolute top-4 right-4 p-3 bg-black/40 backdrop-blur-md rounded-xl border border-white/5 pointer-events-none text-right">
                          <div className="flex gap-1.5 justify-end">
                            {[1, 2, 3].map((i) => (
                              <div
                                key={i}
                                className="w-1 h-1 rounded-full bg-amber-500/50"
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                      <p className="text-center text-[9px] text-slate-600 font-medium mt-4 italic uppercase tracking-widest">
                        Synchronized View Interaction Enabled
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-8 py-6 bg-slate-950 border-t border-slate-800 flex justify-between items-center">
                <div className="flex items-center gap-6 text-[9px] font-black text-slate-600 uppercase tracking-[0.2em]">
                  <span className="flex items-center gap-2 font-mono">
                    <RefreshCw size={12} /> Real-time Sync
                  </span>
                  <span className="flex items-center gap-2 font-mono">
                    <ShieldCheck size={12} className="text-indigo-500" /> 1:1
                    Comparative Map
                  </span>
                </div>
                <button
                  onClick={() => setShowPhotoComparison(false)}
                  className="px-12 py-3 bg-indigo-500 hover:bg-indigo-400 text-white text-[10px] font-black uppercase tracking-[0.3em] rounded-2xl shadow-xl shadow-indigo-500/20 transition-all active:scale-95"
                >
                  Terminate Comparison
                </button>
              </div>
            </div>
          </div>
        )}

        {astrologyDetailsModal.open && (
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100000] flex items-center justify-center p-4"
            onClick={() =>
              setAstrologyDetailsModal((prev) => ({ ...prev, open: false }))
            }
          >
            <div
              className="bg-slate-900 border border-fuchsia-500/30 rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-black text-white">
                    Astrology Details
                  </h3>
                  <p className="text-xs text-slate-400">
                    Candidate: {astrologyDetailsModal.candidateName || "N/A"}
                  </p>
                </div>
                <button
                  onClick={() =>
                    setAstrologyDetailsModal((prev) => ({
                      ...prev,
                      open: false,
                    }))
                  }
                  className="p-2 rounded-lg hover:bg-slate-800 text-slate-300"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="p-6 overflow-auto max-h-[calc(85vh-80px)]">
                {astrologyDetailsModal.loading && (
                  <p className="text-slate-300 text-sm">
                    Loading astrology details...
                  </p>
                )}

                {!astrologyDetailsModal.loading &&
                  astrologyDetailsModal.error && (
                    <p className="text-rose-400 text-sm">
                      {astrologyDetailsModal.error}
                    </p>
                  )}

                {!astrologyDetailsModal.loading &&
                  !astrologyDetailsModal.error &&
                  astrologyDetailsModal.data && (
                    <AstrologyDetailsView data={astrologyDetailsModal.data} />
                  )}
              </div>
            </div>
          </div>
        )}

        {/* Pagination */}
        <div className="flex justify-center gap-2 mt-6">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              onClick={() => setPage(i + 1)}
              className={`px-3 py-1 rounded ${page === i + 1
                ? "bg-[#ffcc00] text-black"
                : "bg-white text-black"
                }`}
            >
              {i + 1}
            </button>
          ))}
        </div>
        {/* Send PDF Modal */}
        {sendPdfModal.isOpen && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100000] flex items-center justify-center p-4"
            onClick={() =>
              setSendPdfModal({
                isOpen: false,
                candidateId: "",
                candidateName: "",
                isUserRequested: false,
              })
            }
          >
            <div
              className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-md w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <FileText size={20} className="text-indigo-500" />
                <h3 className="text-lg font-black text-white uppercase tracking-tight">
                  Send Match PDF
                </h3>
              </div>
              <p className="text-slate-400 text-sm mb-6">
                Send match PDF for{" "}
                <span className="text-white font-bold">
                  {sendPdfModal.candidateName}
                </span>
                . Choose who should receive the notification:
              </p>
              <div className="space-y-3">
                <button
                  onClick={() =>
                    handleSendMatchPdf(sendPdfModal.candidateId, "user")
                  }
                  className="w-full px-4 py-3 bg-blue-600/10 hover:bg-blue-600/20 text-blue-400 border border-blue-500/30 rounded-lg text-sm font-bold transition-all text-left"
                >
                  Send to {userDetails.name} (Current User)
                </button>
                <button
                  onClick={() =>
                    handleSendMatchPdf(sendPdfModal.candidateId, "candidate")
                  }
                  className="w-full px-4 py-3 bg-emerald-600/10 hover:bg-emerald-600/20 text-emerald-400 border border-emerald-500/30 rounded-lg text-sm font-bold transition-all text-left"
                >
                  Send to {sendPdfModal.candidateName} (Candidate)
                </button>
                <button
                  onClick={() =>
                    handleSendMatchPdf(sendPdfModal.candidateId, "both")
                  }
                  className="w-full px-4 py-3 bg-purple-600/10 hover:bg-purple-600/20 text-purple-400 border border-purple-500/30 rounded-lg text-sm font-bold transition-all text-left"
                >
                  Send to Both Users
                </button>
                {sendPdfModal.isUserRequested && (
                  <button
                    disabled={Boolean(sendingRequestedTemplate[sendPdfModal.candidateId])}
                    onClick={() =>
                      handleSendRequestedMatchTemplate(sendPdfModal.candidateId)
                    }
                    className="w-full px-4 py-3 bg-cyan-600/10 hover:bg-cyan-600/20 text-cyan-400 border border-cyan-500/30 rounded-lg text-sm font-bold transition-all text-left disabled:cursor-not-allowed disabled:opacity-50 flex items-center gap-2"
                  >
                    <MessageSquare size={15} />
                    {sendingRequestedTemplate[sendPdfModal.candidateId]
                      ? "Sending requested match..."
                      : `Send Requested Match to ${sendPdfModal.candidateName}`}
                  </button>
                )}
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() =>
                    setSendPdfModal({
                      isOpen: false,
                      candidateId: "",
                      candidateName: "",
                      isUserRequested: false,
                    })
                  }
                  className="flex-1 px-4 py-2.5 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 transition-all font-bold text-sm uppercase tracking-wider"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Phone Call Modal */}
        {phoneCallModal.isOpen && (
          <div className="fixed inset-0 z-[100001] flex items-center justify-center p-4 pointer-events-none">
            <div
              className="bg-slate-900 border border-slate-700/50 rounded-3xl p-8 max-w-lg w-full shadow-[0_30px_100px_rgba(0,0,0,0.8)] animate-in zoom-in-95 duration-200 pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 flex items-center justify-center border border-indigo-500/20">
                    <PhoneCall size={24} className="text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-white uppercase tracking-tight">
                      Call Event Details
                    </h3>
                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1">
                      Review call history & add feedback
                    </p>
                  </div>
                </div>
                <button
                  onClick={() =>
                    setPhoneCallModal((prev) => ({ ...prev, isOpen: false }))
                  }
                  className="p-2 rounded-full hover:bg-slate-800 text-slate-500 hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {phoneCallModal.loading ? (
                <div className="flex flex-col items-center justify-center py-12 gap-4">
                  <div className="w-10 h-10 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
                  <span className="text-xs font-black text-slate-500 uppercase tracking-widest">
                    Retrieving Call Data...
                  </span>
                </div>
              ) : phoneCallModal.data ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-slate-950/50 p-4 rounded-2xl border border-white/5">
                      <span className="text-[9px] font-black text-slate-600 uppercase tracking-widest block mb-1">
                        Duration
                      </span>
                      <span className="text-base font-mono font-black text-white tracking-tighter">
                        {phoneCallModal.data.call_duration || 0}s
                      </span>
                    </div>
                    <div className="bg-slate-950/50 p-4 rounded-2xl border border-white/5">
                      <span className="text-[9px] font-black text-slate-600 uppercase tracking-widest block mb-1">
                        Status
                      </span>
                      <span
                        className={`text-[10px] font-black uppercase tracking-widest ${phoneCallModal.data.call_status === "ANSWERED" ? "text-emerald-400" : "text-amber-400"}`}
                      >
                        {phoneCallModal.data.call_status || "N/A"}
                      </span>
                    </div>
                    <div className="bg-slate-950/50 p-4 rounded-2xl border border-white/5">
                      <span className="text-[9px] font-black text-slate-600 uppercase tracking-widest block mb-1">
                        Hit Count
                      </span>
                      <span className="text-base font-mono font-black text-indigo-400 tracking-tighter">
                        {phoneCallModal.data.hit_count || 0}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">
                        Admin Name
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <UserCheck
                            size={14}
                            className="text-slate-600 group-focus-within:text-indigo-400 transition-colors"
                          />
                        </div>
                        <select
                          value={phoneCallModal.adminName}
                          onChange={(e) =>
                            setPhoneCallModal((prev) => ({
                              ...prev,
                              adminName: e.target.value,
                            }))
                          }
                          className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-11 pr-4 py-3 text-white text-xs font-bold focus:outline-none focus:border-indigo-500 transition-colors appearance-none cursor-pointer"
                        >
                          <option value="">Select Admin</option>
                          {supportAdmins && supportAdmins.length > 0 ? (
                            supportAdmins.map((admin, idx) => (
                              <option
                                key={admin.id || admin.username || idx}
                                value={admin.username || admin.Username}
                              >
                                {admin.username || admin.Username}
                              </option>
                            ))
                          ) : (
                            <option disabled>No Admins Available</option>
                          )}
                        </select>
                        <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                          <ChevronDown size={14} className="text-slate-600" />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">
                        Feedback
                      </label>
                      <textarea
                        placeholder="Enter call feedback here..."
                        value={phoneCallModal.feedback}
                        onChange={(e) =>
                          setPhoneCallModal((prev) => ({
                            ...prev,
                            feedback: e.target.value,
                          }))
                        }
                        className="w-full h-24 bg-slate-950 border border-slate-800 rounded-2xl p-4 text-white text-xs font-medium focus:outline-none focus:border-indigo-500 transition-colors resize-none placeholder:text-slate-700"
                      />
                    </div>

                    <button
                      onClick={handleAddFeedback}
                      className="w-full py-4 bg-indigo-500 hover:bg-indigo-400 text-white text-xs font-black uppercase tracking-[0.2em] rounded-2xl shadow-xl shadow-indigo-500/20 transition-all active:scale-[0.98] mt-4 flex items-center justify-center gap-2"
                    >
                      <Check size={16} strokeWidth={3} />{" "}
                      {phoneCallModal.data.feedback
                        ? "Update Feedback"
                        : "Submit Feedback"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-4 border border-slate-700">
                    <PhoneOff size={24} className="text-slate-500" />
                  </div>
                  <h4 className="text-sm font-black text-white uppercase tracking-tight">
                    No Call Data Available
                  </h4>
                  <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1">
                    This user has not been contacted yet
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Dislike Reason Modal */}
        <DislikeReasonModal
          isOpen={dislikeModal.isOpen}
          onClose={() =>
            setDislikeModal((prev) => ({ ...prev, isOpen: false }))
          }
          userName={dislikeModal.candidateName}
          onSubmit={(reasons) => {
            handleLikeDislikeAction(
              dislikeModal.candidateId,
              false,
              true,
              reasons,
            );
          }}
        />
      </div>
      {/* History Modal */}
      {historyModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-slate-900 border border-slate-800 w-full max-w-4xl max-h-[90vh] rounded-[2rem] overflow-hidden shadow-2xl flex flex-col animate-in zoom-in-95 duration-300">
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-800 flex flex-col gap-6 bg-slate-950/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
                    <History className="text-indigo-400" size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-black text-white uppercase tracking-tight italic">
                      User History
                    </h3>
                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">
                      Chronological activity and profile updates
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setHistoryModalOpen(false)}
                  className="p-2 hover:bg-slate-800 rounded-xl transition-colors text-slate-400"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Filters Row */}
              <div className="flex items-center justify-between gap-4">
                {/* Tab Toggle */}
                <div className="inline-flex p-1 bg-slate-950/80 rounded-xl border border-slate-800 shadow-inner">
                  <button
                    onClick={() => setActiveHistoryTab("Profile")}
                    className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all duration-300 ${activeHistoryTab === "Profile"
                      ? "bg-slate-800 text-white shadow-lg ring-1 ring-white/10"
                      : "text-slate-500 hover:text-slate-300"
                      }`}
                  >
                    <UserCheck size={12} />
                    Profile
                    <span className="ml-1 px-1 py-0.5 rounded-md bg-slate-900 text-slate-400 text-[8px] font-mono">
                      {userHistory.filter(h => h.type === "Profile Update").length}
                    </span>
                  </button>
                  <button
                    onClick={() => setActiveHistoryTab("Preference")}
                    className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all duration-300 ${activeHistoryTab === "Preference"
                      ? "bg-slate-800 text-white shadow-lg ring-1 ring-white/10"
                      : "text-slate-500 hover:text-slate-300"
                      }`}
                  >
                    <TrendingUp size={12} />
                    Preference
                    <span className="ml-1 px-1 py-0.5 rounded-md bg-slate-900 text-slate-400 text-[8px] font-mono">
                      {userHistory.filter(h => h.type === "Preference Update").length}
                    </span>
                  </button>
                </div>

                {/* Search Input */}
                <div className="flex-1 max-w-xs relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none">
                    <Search size={14} />
                  </div>
                  <input
                    type="text"
                    placeholder="Search fields..."
                    value={historySearchQuery}
                    onChange={(e) => setHistorySearchQuery(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 text-slate-300 text-[10px] font-black uppercase tracking-widest rounded-xl pl-9 pr-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500/50 transition-all placeholder:text-slate-700"
                  />
                  {historySearchQuery && (
                    <button
                      onClick={() => setHistorySearchQuery("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 p-1"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>

                {/* Date Dropdown Filter */}
                <div className="flex items-center gap-2">
                  <Calendar size={14} className="text-slate-500" />
                  <select
                    value={historyDateFilter}
                    onChange={(e) => setHistoryDateFilter(e.target.value)}
                    className="bg-slate-950 border border-slate-800 text-slate-300 text-[10px] font-black uppercase tracking-widest rounded-xl px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500/50 transition-all cursor-pointer hover:bg-slate-900"
                  >
                    <option value="all">All Dates</option>
                    {Array.from(new Set(userHistory
                      .filter(h => h.type === (activeHistoryTab === "Profile" ? "Profile Update" : "Preference Update"))
                      .map(h => h.timestamp ? new Date(h.timestamp).toLocaleDateString() : "")
                      .filter(d => d !== "")
                    ))
                      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
                      .map(date => (
                        <option key={date as string} value={date as string}>{date as string}</option>
                      ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-8 custom-scrollbar">
              {historyLoading ? (
                <div className="flex flex-col items-center justify-center py-20 space-y-4">
                  <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
                  <p className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] animate-pulse">
                    Aggregating Audit Logs...
                  </p>
                </div>
              ) : (
                (() => {
                  const filteredHistory = userHistory.filter(item => {
                    const typeMatch = item.type === (activeHistoryTab === "Profile" ? "Profile Update" : "Preference Update");
                    const dateMatch = historyDateFilter === "all" || (item.timestamp && new Date(item.timestamp).toLocaleDateString() === historyDateFilter);
                    const searchMatch = !historySearchQuery || JSON.stringify(item.details || {}).toLowerCase().includes(historySearchQuery.toLowerCase()) ||
                      item.label.toLowerCase().includes(historySearchQuery.toLowerCase());
                    return typeMatch && dateMatch && searchMatch;
                  });

                  if (filteredHistory.length === 0) {
                    return (
                      <div className="text-center py-20">
                        <div className="w-16 h-16 rounded-full bg-slate-800/50 flex items-center justify-center mx-auto mb-4">
                          <History size={24} className="text-slate-600" />
                        </div>
                        <p className="text-slate-400 font-bold">No records found for the selected filters.</p>
                        {(historySearchQuery || historyDateFilter !== "all") && (
                          <button
                            onClick={() => { setHistorySearchQuery(""); setHistoryDateFilter("all"); }}
                            className="mt-4 text-[10px] font-black text-indigo-400 uppercase tracking-widest hover:text-indigo-300 transition-colors"
                          >
                            Clear all filters
                          </button>
                        )}
                      </div>
                    );
                  }

                  // Group by Date
                  const groups = filteredHistory.reduce((acc: any, item) => {
                    const date = item.timestamp ? new Date(item.timestamp).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Unknown Date';
                    if (!acc[date]) acc[date] = [];
                    acc[date].push(item);
                    return acc;
                  }, {});

                  return Object.entries(groups).map(([date, items]: [string, any], groupIdx) => (
                    <div key={date} className="space-y-6">
                      <div className="sticky top-0 z-20 bg-slate-900/90 backdrop-blur-sm py-2">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 border border-slate-700 shadow-lg">
                          <Calendar size={12} className="text-indigo-400" />
                          <span className="text-[10px] font-black text-white uppercase tracking-[0.2em] italic">
                            {date}
                          </span>
                        </div>
                      </div>

                      <div className="relative pl-6 space-y-8">
                        {/* Timeline Line */}
                        <div className="absolute left-[7px] top-4 bottom-4 w-px bg-gradient-to-b from-indigo-500/50 via-slate-800 to-indigo-500/50"></div>

                        {items.map((item: any, idx: number) => {
                          const isCreate = item.label.toLowerCase().includes('created') || item.label.toLowerCase().includes('initial');
                          const isUpdate = !isCreate && (item.label.toLowerCase().includes('updated') || item.label.toLowerCase().includes('changed'));

                          const themeColor = isCreate ? "emerald" : isUpdate ? "sky" : "purple";

                          return (
                            <div key={idx} className="relative pl-8 group">
                              {/* Dot */}
                              <div
                                className={`absolute left-0 top-1.5 w-[16px] h-[16px] rounded-full border-2 bg-slate-900 z-10 shadow-lg transition-transform group-hover:scale-125 ${themeColor === "emerald" ? "border-emerald-500" :
                                  themeColor === "sky" ? "border-sky-500" : "border-purple-500"
                                  }`}
                              ></div>

                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md ${themeColor === "emerald" ? "bg-emerald-500/10 text-emerald-400" :
                                      themeColor === "sky" ? "bg-sky-500/10 text-sky-400" : "bg-purple-500/10 text-purple-400"
                                      }`}>
                                      {item.label}
                                    </span>
                                    {item.timestamp && (
                                      <span className="text-[9px] font-mono text-slate-500">
                                        {new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                      </span>
                                    )}
                                  </div>
                                </div>

                                <div className={`bg-slate-950/40 border border-white/[0.04] rounded-2xl p-4 transition-all group-hover:bg-slate-950/60 group-hover:border-${themeColor}-500/20 group-hover:shadow-xl group-hover:shadow-${themeColor}-500/5`}>
                                  {item.details ? (
                                    <div className="space-y-4">
                                      {/* Primary Changes Grid */}
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {Object.entries(item.details)
                                          .filter(([k]) => k !== 'context')
                                          .map(([k, v], i) => {
                                            const isObject = typeof v === 'object' && v !== null;
                                            const label = k.replace(/_/g, ' ').toUpperCase();

                                            return (
                                              <div key={i} className="space-y-1 bg-slate-900/30 p-3 rounded-xl border border-white/[0.02]">
                                                <span className="text-slate-500 text-[8px] font-black uppercase tracking-widest block mb-1">{label}</span>
                                                <div className="pl-2 border-l border-indigo-500/20">
                                                  {isObject ? (
                                                    <pre className="text-slate-300 text-[9px] whitespace-pre-wrap break-words">
                                                      {JSON.stringify(v, null, 2)}
                                                    </pre>
                                                  ) : (
                                                    <span className="text-slate-200 text-[10px] font-medium">
                                                      {historySearchQuery ? (
                                                        String(v).split(new RegExp(`(${historySearchQuery})`, 'gi')).map((part, pi) =>
                                                          part.toLowerCase() === historySearchQuery.toLowerCase()
                                                            ? <mark key={pi} className="bg-indigo-500/30 text-white rounded px-0.5">{part}</mark>
                                                            : part
                                                        )
                                                      ) : String(v)}
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                            );
                                          })
                                        }
                                      </div>

                                      {/* Updated By Section */}
                                      <div className="pt-3 border-t border-white/[0.03]">
                                        <p className="text-xs text-gray-400">
                                          Updated By: {item.updated_by_name || item.updated_by_id || "System"}
                                        </p>
                                      </div>

                                      {/* Context Section */}
                                      {item.details.context && (
                                        <div className="pt-3 border-t border-white/[0.03]">
                                          <div className="flex items-center gap-2 mb-2">
                                            <div className="w-4 h-4 rounded bg-slate-800 flex items-center justify-center">
                                              <Activity size={10} className="text-slate-500" />
                                            </div>
                                            <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest">Source Context</span>
                                          </div>
                                          <div className="bg-indigo-500/5 rounded-lg p-2 border border-indigo-500/10">
                                            <pre className="text-[8px] font-mono text-indigo-300/70 whitespace-pre-wrap break-words">
                                              {typeof item.details.context === 'object'
                                                ? JSON.stringify(item.details.context, null, 2)
                                                : String(item.details.context)}
                                            </pre>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <p className="text-[10px] text-slate-600 italic text-center py-2">No additional metadata available</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ));
                })()
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-800 bg-slate-950/30 flex justify-end">
              <button
                onClick={() => setHistoryModalOpen(false)}
                className="px-6 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all border border-slate-700"
              >
                Close History Log
              </button>
            </div>
          </div>
        </div>
      )}

      {deactivatedMatchesModalOpen && (
        <DeactivatedMatchesModal
          userId={userDetails.userId}
          onClose={() => setDeactivatedMatchesModalOpen(false)}
        />
      )}
    </div>
  );
};

export default UserDetails;
